/*
  Bolty -- resident shop-assistant mascot (draft theme only). Chrome
  countersunk bolt with a Towelie-type face, standing in front of the Eva
  Hardware nuts-and-bolts matchbox. Rendered by snippets/bolty.liquid as a
  direct child of <body>, outside #MainContent -- survives PJAX navigation
  the same way assets/eva-radio.js's widget does.

  CORRECTION PASS (this build): character geometry rebuilt from the exact
  locked-calibration formulas (previous build's eyes/threads/box were a
  "best-effort geometric read," self-flagged as unverified -- this pass
  fixes both). Architecture also changed: the box backdrop is no longer a
  separately-positioned <img> -- box + cast shadow + character are all
  drawn into ONE raw (undithered) stage canvas every frame variant, and
  THAT combined canvas is what goes through the dither pass, per spec
  ("whole composite runs through the dither pipeline together"). Ordered
  (Bayer 4x4) dithering has no inter-pixel dependency, so this is not just
  a style match -- it's required for the cast shadow's 15%-alpha overlap
  onto the box to quantize correctly (dithering box and shadow separately
  then compositing after would double-quantize the overlap and not match
  what a single pass produces).

  FEATURES PASS (this build): draggable (character+box+shadow move as one
  unit, since there's only one canvas), an indefinite show/hide toggle in
  the header icon row (replaces the polish pass's 30-day dismiss), and a
  real emotion state machine driving lids/pupils/brows/mouth -- rest
  (default) / talk (balloon open) / bored (40s idle) / sleep (90s idle,
  the old "rest" raster state renamed to avoid colliding with the new
  default) / vibe (radio playing) / stoked (add-to-cart). Priority: talk >
  stoked > vibe > bored/sleep > rest. Blink is orthogonal to all of this
  (a brief lids-closed override on top of whichever emotion is active),
  not a competing state, matching the priority list the spec itself gives
  (which never mentions blink).

  This file owns: the renderer, the dither pass, the animation loop, the
  balloon, and the full state machine (hidden -> entering ->
  resident(rest|talk|bored|sleep|vibe|stoked) -> hidden).
*/
(function () {
  // Kill switch -- same pattern as eva-pjax.js's EVA_PJAX_ENABLED.
  window.EVA_BOLTY_ENABLED = (window.EVA_BOLTY_ENABLED !== false);
  if (!window.EVA_BOLTY_ENABLED) { return; }

  // --- Ask Bolty config (real Q&A in the balloon, draft theme only) -------
  // Paste the real Cloudflare Worker (or equivalent) endpoint here to turn
  // the feature on, e.g. "https://bolty-ask.your-worker.workers.dev". Left
  // empty on purpose -- shipping a non-empty placeholder string here would
  // make askBoltyEnabled() below read as "configured" and start firing
  // real POSTs at a URL that doesn't exist, which is a worse failure mode
  // than the option simply not rendering (its own stated spec). The
  // worker is expected to accept POST {question, products, collections,
  // page:{title}} and respond 200 with JSON {answer, link} where link is
  // null or {url, label} -- anything else (non-200, timeout, empty/
  // missing answer) is treated as a failure and shows the in-character
  // fallback line instead.
  var BOLTY_ASK_URL = "https://bolty.per-578.workers.dev";
  // Kill switch -- same window-flag pattern as EVA_BOLTY_ENABLED above.
  window.EVA_ASK_BOLTY_ENABLED = (window.EVA_ASK_BOLTY_ENABLED !== false);

  // Real collection handles, pulled live from the store (Shopify Admin
  // API search_collections, 2026-08-02) rather than guessed -- sent to
  // the worker as light-touch routing context so it can point a "where
  // do I find X" answer at a real page instead of hallucinating a URL.
  // Only the shop's actual main-category collections, not every one-off
  // brand/drop collection in the catalog (over 50 of those exist).
  var BOLTY_COLLECTIONS = [
    { title: 'SKATEBOARDS', url: '/collections/skateboards' },
    { title: 'HARDWARE', url: '/collections/hardware' },
    { title: 'TRUCKS', url: '/collections/trucks' },
    { title: 'WHEELS', url: '/collections/wheels' },
    { title: 'T-SHIRTS', url: '/collections/t-shirts' },
    { title: 'ACCESSORIES', url: '/collections/accessories' },
    { title: 'MOB GRIPTAPE', url: '/collections/mob-griptape' }
  ];

  // --- Ask-Bolty product routing: category/brand -> real collections ------
  // (2026-08-24) Grounding used to run every question through
  // /search/suggest.json, which AND-matches query tokens as PREFIXES across
  // title/tag/vendor/type and crosses categories without hesitation: asked
  // about bearings it served the Bones Brigade Video Tunes LP -- a record --
  // because the title matched on "Bones". Same root cause as the July
  // griptape-cleaner bug.
  //
  // Filtering the search results isn't possible on this catalog: product_type
  // is empty on essentially everything (2 of ~250 have one, and they say
  // "Thing" and "Shoes"), tags cover under a fifth of the catalog
  // inconsistently, and `vendor` is the literal string "Eva Hardware" on
  // every single product -- confirmed live against products.json this pass,
  // not assumed. COLLECTIONS are the only complete category data this store
  // has, so the fix is to ROUTE to a collection instead of searching text.
  //
  // Every handle below was read off the live store (Admin API, 2026-08-24),
  // never guessed from a display name, because several have drifted:
  // INDEPENDENT lives at "indendent" (sic), "killer-pads-copy-1" is titled
  // Bullet, and "bullet-copy" is titled Birdhouse.

  // Ordered MOST specific -> least; first match wins. The generic deck words
  // sit deliberately at the bottom so "skateboard bearings" resolves to
  // bearings rather than skateboards.
  var BOLTY_CATEGORY_COLLECTIONS = [
    { phrases: ['bearing', 'bearings'], handles: ['bearings'] },
    { phrases: ['griptape', 'griptapes', 'grip tape', 'grip'], handles: ['griptape'] },
    { phrases: ['truck', 'trucks'], handles: ['trucks'] },
    { phrases: ['wheel', 'wheels'], handles: ['wheels'] },
    { phrases: ['hardware', 'bolt', 'bolts', 'nut', 'nuts'], handles: ['hardware'] },
    // Two collections, both real protection SKUs: "killer-pads-copy" is
    // Killer Pads, "killer-pads-copy-1" is Bullet (title/handle drift again).
    { phrases: ['pad', 'pads', 'protection'], handles: ['killer-pads-copy', 'killer-pads-copy-1'] },
    { phrases: ['shoe', 'shoes'], handles: ['shoes'] },
    // normalizeAskText turns "-" into a space, so "t-shirt" arrives as "t shirt".
    { phrases: ['t shirt', 't shirts', 'tshirt', 'tshirts', 'tee', 'tees'], handles: ['t-shirts'] },
    { phrases: ['hoodie', 'hoodies', 'sweat', 'sweats', 'sweatshirt', 'sweatshirts', 'crewneck', 'crewnecks'], handles: ['tops'] },
    { phrases: ['beanie', 'beanies', 'cap', 'caps', 'hat', 'hats'], handles: ['headwear'] },
    { phrases: ['sock', 'socks', 'accessory', 'accessories'], handles: ['accessories'] },
    { phrases: ['deck', 'decks', 'board', 'boards', 'skateboard', 'skateboards'], handles: ['skateboards'] }
  ];

  // Brand collections. Multi-word names are listed in their normalized
  // (space-separated) form; possessives too, since "shorty's" normalizes to
  // "shorty s". Deliberately NOT included: the live "real" collection (the
  // word is far too common in ordinary questions to use as a brand trigger),
  // and the shop's own house collections (eva, pagan, latest, ...), which are
  // not brands a visitor asks for by name.
  var BOLTY_BRAND_COLLECTIONS = [
    { phrases: ['spitfire'], handle: 'spitfire' },
    { phrases: ['baker'], handle: 'baker' },
    { phrases: ['bones'], handle: 'bones' },
    { phrases: ['skf'], handle: 'skf' },
    { phrases: ['independent', 'indy'], handle: 'indendent' },
    { phrases: ['thunder'], handle: 'thunder' },
    { phrases: ['lurpiv'], handle: 'lurpiv' },
    { phrases: ['sour'], handle: 'sour' },
    { phrases: ['antihero', 'anti hero'], handle: 'antihero' },
    { phrases: ['hardbody'], handle: 'hardbody' },
    { phrases: ['sci fi fantasy', 'scifi fantasy', 'sci fi'], handle: 'sci-fi-fantasy' },
    { phrases: ['quartersnacks'], handle: 'quartersnacks' },
    { phrases: ['hockey'], handle: 'hockey' },
    { phrases: ['fucking awesome'], handle: 'fucking-awesome' },
    { phrases: ['palace'], handle: 'palace' },
    { phrases: ['ripcare'], handle: 'ripcare' },
    { phrases: ['bronson', 'bronson speed co'], handle: 'bronson-speed-co' },
    { phrases: ['village pm', 'village'], handle: 'village-pm' },
    { phrases: ['shortys', 'shorty s', 'shorty'], handle: 'shortys' },
    { phrases: ['gx1000'], handle: 'gx1000' },
    { phrases: ['shake junt'], handle: 'shake-junt' },
    // Birdhouse's collection lives at the handle "bullet-copy" -- another
    // drift, same family as indendent/killer-pads-copy-1. Verified live.
    { phrases: ['birdhouse'], handle: 'bullet-copy' }
  ];

  function askBoltyEnabled() {
    return !!(window.EVA_ASK_BOLTY_ENABLED && BOLTY_ASK_URL);
  }

  function ready(fn) {
    if (document.readyState !== 'loading') { fn(); }
    else { document.addEventListener('DOMContentLoaded', fn); }
  }

  var reduceMotion = false;
  try { reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) {}

  // Randomized delay before an UNPROMPTED line (entrance greeting, or the
  // periodic spontaneous context/world/philosophy/generic ladder) appears
  // -- Per's own words: "he speaks almost directly when loading a new
  // page... I want it to feel like he's watching what you're doing."
  // Named/top-of-file per spec so these are easy to retune later without
  // hunting through the file. Named constants only -- actual wiring
  // (speakAfterDelay) lives down in the voice-lines section, next to the
  // functions it wraps.
  var BOLTY_SPEAK_DELAY_MIN_MS = 1500;
  var BOLTY_SPEAK_DELAY_MAX_MS = 3500;

  // Quiet period after ANY balloon interaction, before he's allowed to open
  // a balloon on his own again (2026-08-21, Per's number). This is a
  // decaying suppression, NOT a permanent latch: the clock restarts on every
  // interaction, so it measures silence since the LAST thing the visitor
  // did, never a countdown from first contact. Wiring lives with
  // markBoltyEngaged/isBoltyEngaged down in the engaged-flag section --
  // named here per this block's own top-of-file convention.
  var ENGAGED_QUIET_MS = 5 * 60 * 1000;

  // --- Animation tuning (five one-shot animations: cough, smoke, ------
  // nodding off, yawn, spying) -------------------------------------------
  // All are discrete stepped frames at the existing TICK_MS cadence
  // (defined below, 110ms/~9fps -- squarely in the "6-10fps hand-
  // animation" range the spec asks for), never eased/interpolated in
  // real time -- matches this file's whole animation philosophy already
  // established for IDLE_BOB/VIBE_BOB/STOKED_BOB etc. Frame counts,
  // hold lengths, and trigger rates all named and grouped here, at the
  // top of the file, so Per can retune timing without hunting through
  // the per-animation builders further down (assets/bolty.js's own
  // "Animations" section) or the trigger functions (its "Animation
  // triggers" section). "_EVERY_MS" values are in-expectation periods,
  // same convention BLINK_EVERY_MS already uses below (a per-tick roll
  // of TICK_MS/EVERY_MS).
  var COUGH_FRAME_COUNT = 6;                 // ~660ms -- "short and sharp, under a second"; the JOLT itself, not the trailing smoke-burst decay tail (see COUGH_SMOKE_TOTAL_FRAMES)
  var COUGH_RANDOM_REST_EVERY_MS = 45000;    // rare random trigger while idle in rest

  // Ambient smoke (the lazy one-exhale idle/vibe puff) -- every value
  // named so Per can dial it without a rebuild, same pattern as the
  // reaction-pack-2 constants further down. drawSmokePuffs (see the
  // "Scene stage" section) is the single shared puff renderer; this is
  // its "ambient" style config.
  //
  // HEAD_WIDTH anchors puff size to Bolty's own proportions instead of
  // an arbitrary pixel value, so it stays correctly proportioned at
  // every --eva-bolty-scale (the whole character, smoke included,
  // already scales together via the CSS transform on .bolty-scene --
  // this is about the SIZE RELATIONSHIP being intentional/traceable,
  // not about scale-responsiveness, which was never broken). Defined
  // here, ahead of the "Character calibration" section further down,
  // purely so this file's top-to-bottom var-assignment order can use it
  // -- value (91) is NOT independent, it's copied from buildHead's own
  // outer cap ellipse (rx=45.5, i.e. 45.5*2), the same "hw" the
  // headphones-overlay/zZ-text math already keys off (see that
  // section's own comment). If buildHead's head geometry ever changes,
  // update this to match -- there is no live reference between them.
  var HEAD_WIDTH = 91;                       // character-local units, pre-CHAR_SCALE -- Bolty's own head width ("hw")
  var SMOKE_PUFF_COUNT = 4;                  // puffs per exhale
  var SMOKE_PUFF_STAGGER_TICKS = 4;          // ticks between each puff's own release
  var SMOKE_FRAME_COUNT = 44;                // 4.84s total puff drift/grow/fade at TICK_MS 110 -- last puff (releases at STAGGER*(COUNT-1)=12) still gets 32 frames of life. Per's rig value 2026-08-24, was 30
  var SMOKE_IDLE_EVERY_MS = 9000;            // occasional trigger while idle (rest/bored) -- more often per Per's ask
  var SMOKE_VIBE_EVERY_MS = 3000;            // more often while vibing
  // 2026-08-10 amendment: Per's own words, "almost as big as his head" --
  // the prior 26/42 pass (2026-08-09) still read as "hard to see" against
  // that target. Sized off HEAD_WIDTH so the relationship is explicit
  // rather than another guessed pixel value: peak puff DIAMETER lands at
  // SMOKE_PUFF_MAX_DIAM_RATIO of HEAD_WIDTH (target range 70-90%; 0.8
  // chosen as the midpoint). maxRadius/maxDist/startRadius/startDist are
  // all scaled up together by the same factor (new maxRadius / old 26 =
  // 1.4x) rather than just growing the radius alone -- keeping the old
  // curve's shape (how far a puff has drifted by the time it's fully
  // grown) means a bigger cloud still clears the mouth/face by the same
  // proportion the old, smaller cloud did, instead of drifting less
  // relative to its own now-larger size and reading as stuck to his face.
  var SMOKE_PUFF_MAX_DIAM_RATIO = 1.05;      // fraction of HEAD_WIDTH the peak puff DIAMETER reaches -- now slightly WIDER than his head. Per's rig value 2026-08-24, was 0.8
  var SMOKE_PUFF_MAX_RADIUS = HEAD_WIDTH * SMOKE_PUFF_MAX_DIAM_RATIO / 2; // = 47.775 canvas-local units (rig reads 47.77), pre-CHAR_SCALE, puff radius at t=1 -- still DERIVED from the ratio, never hardcoded
  // The three values below were previously derived by multiplying a base
  // number by SMOKE_SIZE_SCALE_FACTOR (= MAX_RADIUS/26), so they grew
  // automatically with the max radius. They are now literals chosen by Per
  // directly on the calibration rig against the real dither pass, so that
  // factor no longer describes them and has been removed rather than left
  // as dead code -- raising the ratio must NOT silently drag these along
  // any more. Rig values 2026-08-24.
  var SMOKE_PUFF_START_RADIUS = 5.6;         // canvas-local units, pre-CHAR_SCALE, puff radius at birth (t=0) -- unchanged in value, previously 4 * 1.4
  var SMOKE_PUFF_START_DIST = 0;             // canvas-local units, pre-CHAR_SCALE, distance from mouth at birth -- puffs now start AT the mouth, was 4.2
  var SMOKE_PUFF_MAX_DIST = 58.8;            // canvas-local units, pre-CHAR_SCALE, distance from mouth at t=1 -- unchanged in value, previously 42 * 1.4
  var SMOKE_ANGLE_BASE_DEG = -100;           // degrees, canvas convention (0=right, 90=down) -- "up and toward the puckered mouth side"
  var SMOKE_ANGLE_STEP_DEG = 29;             // degrees added per puff index (times SMOKE_MOUTH_SIDE) -- a much wider fan than the old tight 10. Per's rig value 2026-08-24
  var SMOKE_PEAK_ALPHA = 0.85;               // held for most of the puff's life -- clears ditherCanvas's binary ALPHA_THRESHOLD=128 per-block cutoff, see drawSmokePuffs' 2026-08-08 fix note
  var SMOKE_FADE_START_T = 0.6;              // puff-life fraction where alpha/color start ramping toward gone
  var SMOKE_COLOR = '#cfd6da';               // same cool grey the sleep-state zZ text already uses
  var SMOKE_FADE_COLOR = '#f4f6f7';          // near-background grey the puff lerps toward while fading -- reads as "softening" through the dither's few discrete levels, not just fading straight to transparent

  // Cough smoke -- short sharp bursts timed to the convulsions, reusing
  // drawSmokePuffs with a different style config: faster release,
  // wider scattered angles, smaller/quicker-decaying puffs than the
  // lazy ambient exhale above. Never a second puff implementation.
  // 2026-08-10 amendment: Per's "much bigger" ask was specifically about
  // the ambient exhale ("almost as big as his head" -- see
  // SMOKE_PUFF_MAX_DIAM_RATIO above); the twice-repeated cough ask was
  // about confirming it emits smoke AT ALL (it does, via COUGH_PLAY_FRAMES
  // below), not about resizing it. Left un-scaled here on purpose --
  // still deliberately smaller/punchier than ambient, per the original
  // design intent right above.
  var COUGH_SMOKE_PUFF_COUNT = 6;                // Per's rig value 2026-08-24, was 3 (one per jolt beat) -- now two per beat, a denser burst
  var COUGH_SMOKE_BURST_STAGGER_TICKS = 1;       // released on consecutive ticks, timed to each jolt -- far faster than ambient's 4-tick stagger
  var COUGH_SMOKE_TOTAL_FRAMES = 11;             // burst decay window, ~1.21s at TICK_MS 110 -- sharper still, and far shorter than ambient's 44-frame drift. Per's rig value 2026-08-24, was 14
  var COUGH_SMOKE_START_RADIUS = 3;              // canvas-local units, pre-CHAR_SCALE
  var COUGH_SMOKE_MAX_RADIUS = 22;               // canvas-local units, pre-CHAR_SCALE -- still well under ambient's 47.775, punchy bursts rather than one big cloud. Per's rig value 2026-08-24, was 16
  var COUGH_SMOKE_START_DIST = 2;                // canvas-local units, pre-CHAR_SCALE
  var COUGH_SMOKE_MAX_DIST = 30;                 // canvas-local units, pre-CHAR_SCALE
  var COUGH_SMOKE_ANGLE_BASE_DEG = SMOKE_ANGLE_BASE_DEG;
  var COUGH_SMOKE_ANGLE_STEP_DEG = 20;           // Per's rig value 2026-08-24, was 22 -- now slightly TIGHTER than ambient's 29
  var COUGH_SMOKE_PEAK_ALPHA = SMOKE_PEAK_ALPHA; // same threshold-clearing value -- must not regress the Aug-8 fix
  var COUGH_SMOKE_FADE_START_T = 0.4;            // fades sooner than ambient -- a sharp pop-off, not a lazy dissolve

  var NOD_OFF_CYCLES = 3;                    // dip/hold/jerk-up repeats before sleep takes over
  var NOD_OFF_BASE_HOLD_TICKS = 1;           // first (lightest) cycle's hold length
  var NOD_OFF_HOLD_GROWTH_TICKS = 1;         // each later cycle holds this many ticks longer -- "heavier"

  var YAWN_FRAME_COUNT = 14;                 // ~1.5s, longer than the cough
  var YAWN_ON_BORED_CHANCE = 0.5;            // chance of actually yawning right as bored starts
  var YAWN_ON_BORED_LATE_NIGHT_CHANCE = 0.9; // much likelier when world.late_night is true

  var SPYING_HOLD_TICKS = 4;                 // ticks spent on each look position (duration = this * SPYING_LOOK_POSITIONS.length, see that array further down)
  var SPYING_LONG_SESSION_EVERY_MS = 25000;  // occasional trigger during a long session
  var SPYING_ON_TAB_RETURN_CHANCE = 0.5;     // chance of spying when the tab regains visibility (not guaranteed every time, would get repetitive for anyone alt-tabbing often)

  // --- Animation tuning (reaction pack 2: dollar eyes, surprised, lick, --
  // plus smoke/yawn refinements) ------------------------------------------
  // Same conventions as the block above: named constants only, grouped
  // here rather than scattered near their builders.

  // Dollar eyes: an orthogonal PUPIL overlay on top of the add-to-cart
  // reaction, not a full activeAnim (stoked already drives its own
  // bob/mouth outside the activeAnim system -- see triggerStoked/
  // currentBob -- so this can't consume the single activeAnim slot the
  // way cough/smoke/yawn/spying/surprised do). Time-window based
  // (dollarEyesUntil, a wall-clock deadline) exactly like stokedUntil
  // already is, rather than a tick-indexed frame array.
  var BOLTY_DOLLAR_EYES_CHANCE = 0.3;        // odds it happens at all on a given add-to-cart
  var BOLTY_DOLLAR_EYES_DURATION_MS = 1000;  // "a short beat (~1s)"

  // Surprised: a real activeAnim (brows/lids/mouth), same family as
  // cough/yawn. SURPRISED_FRAME_COUNT ~= 1s at the file's TICK_MS.
  var SURPRISED_FRAME_COUNT = 9;
  var SURPRISED_HOLD_TICKS = 4;              // "hold a beat" at the peak, after the 2-3 frame jump-in
  var SURPRISED_BROW_OFFSET = -8;            // "a step or two" -- bigger jump than BROW_OFFSET_STOKED (-5)
  var SURPRISED_LID_DROP = 5;                // "lids fully up" -- well under base LID_DROP (16.3), most of the eye exposed
  var BOLTY_SURPRISED_CHANCE = 0.25;         // add-to-cart's second roll, only reached when the dollar-eyes roll above fails

  // Lick: a real activeAnim, same family as spying (a short list of
  // discrete positions, each held a few ticks) -- here the positions are
  // tongue placements around the mouth outline rather than pupil drifts.
  var LICK_HOLD_TICKS = 2;                       // ticks per position -- 3 positions * 2 = 6 ticks, ~660ms, "under a second"
  var LICK_RANDOM_REST_EVERY_MS = COUGH_RANDOM_REST_EVERY_MS; // "same rarity class as the random cough" -- literally the same value, own name so Per can retune it independently later

  // Smoke refinement: puffs now release from a mouth CORNER, not
  // mouth-center -- the mouth itself shifts+puckers to that same side.
  var SMOKE_MOUTH_SIDE = -1;          // -1 = left, +1 = right -- "pick one, keep it consistent"; left chosen, arbitrary
  var SMOKE_MOUTH_OFFSET_X = 9;       // character-local units, how far the puckered mouth shifts off CX
  var SMOKE_PUCKER_FRAME_COUNT = 14;  // covers the whole puff-release window (last release at SMOKE_PUFF_STAGGER_TICKS*(SMOKE_PUFF_COUNT-1)=12) plus a couple frames' buffer, then the mouth relaxes back for the remaining drift/fade frames

  // Yawn refinement: a stretch lead-in (body up, head tilts back) before
  // the existing open/peak/close mouth sequence, which is otherwise
  // unchanged.
  var YAWN_STRETCH_FRAME_COUNT = 4;  // frames spent stretching up before the mouth starts to open
  var YAWN_STRETCH_BOB = -2;         // "1-2 px units at character scale" -- same bob unit space as IDLE_BOB/STOKED_BOB etc.
  var YAWN_STRETCH_ROTATE = -5;      // "head tilts back slightly" -- opposite sign from cough/nod's forward-jolt rotate

  // --- Never-render guards --------------------------------------------

  // /cart used to be denied alongside /checkout -- removed (this pass):
  // /cart has its own context.cart voice-line bucket and Per wants him
  // present there. Checkout stays denied -- theme code doesn't run on
  // Shopify's actual checkout pages at all, so denying it here is just
  // documentation of that fact, not something enforcing it.
  function isDeniedPath() {
    var p = window.location.pathname;
    return p.indexOf('/checkout') === 0;
  }

  // isHidden() (the features pass's indefinite show/hide flag) lives in
  // the state machine section below, alongside setHidden/toggleBoltyVisibility.
  //
  // No desktop-width guard here any more (30 Jul mobile pass) -- he used
  // to be hard-denied under 768px in three places (this function, plus a
  // matching CSS !important rule and a matching check in the sync
  // placement script in snippets/bolty.liquid) as a deliberate design
  // choice at the time. ~80% of this store's traffic is phones, so that
  // made him invisible to most visitors; all three guards are gone now.
  // Mobile gets a smaller --eva-bolty-scale and a taller default bottom
  // offset instead (assets/bolty.css), plus the touch handling below.

  // --- Character calibration (canonical -- do not eyeball) ---------------
  // Every number below is copied verbatim from the correction-pass spec.
  // CX/HEAD_CY (80/34) and VB_W/VB_H (160/240) are carried over from the
  // prior build's canvas -- the new spec's own "21" head-drop and the
  // shared sw/bh values match that canvas's old hh=21/sw=46/bh=190
  // exactly, so reusing it (rather than picking a new size) keeps the
  // existing headphones-overlay/zZ-text math (both keyed off HEAD_CY/hw)
  // valid unchanged, and gives correct headroom for both.

  var CX = 80;
  var HEAD_CY = 34;
  var VB_W = 160;
  var VB_H = 240;

  var EYE_COLOR = '#ffbaa8';
  var LID_COLOR = '#787878';
  var PUPIL_COLOR = '#0D1013';
  var VEIN_COLOR = '#B0483E';

  var SW = 46;   // shank width
  var BH = 190;  // shank (body) length, shankTop -> bottom

  var TOP_Y = HEAD_CY - 10.5;         // head cone's own top edge (hh/2 = 21/2)
  var SHANK_TOP = TOP_Y + 21;         // head cone bottom == shank top
  var THREAD_START = SHANK_TOP + 87.4; // 46% down the shank
  var BOTTOM_Y = SHANK_TOP + BH;       // shank tip
  var EYE_Y = SHANK_TOP + 22;

  var EYE_R = 18.5;
  var EYE_GAP_HALF = 19.5; // exL = CX-19.5, exR = CX+19.5
  var PUPIL_R = 7;
  var PUPIL_OFFSET = 1.85; // toward center
  var PUPIL_Y_OFFSET = 1.1;
  var PUPIL_LOOK_UP_OFFSET = -3; // "pupils up" for the ask-Bolty thinking state -- same magnitude as the eye-tracking grid's own max step (EYE_TRACK_MAX)
  var LID_DROP = 16.3;     // open-eye lid coverage
  var LID_OUTER_LIFT = 3;  // outer end sits this much higher than inner end
  var LID_BOW = 1;         // edge-line bows up 1px at center (bowControl sign bugfix, see its own comment)

  var MOUTH_Y = EYE_Y + 24.5;
  var MOUTH_W = 28;

  // --- Floating hands (reaction pack 3, 2026-08-08) -----------------------
  // AMENDS the "he has no arms" note further down (see the animations
  // section comment) -- he still has no arms or wrists connecting
  // anything to the body. These are genuinely detached floating gloves
  // (Rayman reference: hands with nothing joining them to the shoulders),
  // same category as the smoke puffs above -- drawn straight onto the raw
  // stage in buildRawStage/drawHands, never baked into the character's
  // own SVG raster -- rather than a body-attached limb. They exist ONLY
  // while an animation that uses them (juggle/scratch/nosePick) is
  // playing; every existing emotion/animation is completely unchanged,
  // and default (at-rest) Bolty never shows hands at all.
  var HAND_REST_Y = SHANK_TOP + 95;  // character-local -- "roughly hand height" beside the shank (close to where THREAD_START sits, i.e. natural hands-at-the-side level)
  var HAND_REST_X_OFFSET = 34;       // character-local, either side of CX -- clears the shank's own silhouette (half-width 23) with a real gap rather than overlapping it
  var HAND_PALM_RX = 12, HAND_PALM_RY = 14;   // character-local units -- cartoon-mitten proportions, slightly taller than wide
  var HAND_FINGER_LEN = 9, HAND_FINGER_R = 3.6;
  var HAND_THUMB_LEN = 8.5, HAND_THUMB_R = 4;
  var HAND_STROKE = '#0D1013';       // same ink already used for pupils/mouth-fill/brows -- one black across the whole character, not a new tone
  var HAND_STROKE_W = 2.2;
  var HAND_FILL = '#ffffff';         // flat white cartoon glove, deliberately not a gradient -- see drawGlove's own comment for why flat fill is what survives the shared dither pass cleanest
  var HAND_MATERIALIZE_CELL = 5;     // raw-stage px per scatter cell during dither-in/out (see drawGlove) -- coarse enough to read as chunky retro static, not a modern fade
  var HAND_ENTRANCE_MATERIALIZE = 0.4; // materialize value for every gesture's opening/closing frame -- "quick dither-in/out, not a slide or fade" is the default; set to 1 for a hard pop instead (materialize=1 is already "fully solid" everywhere else this value is used, so this one constant is the whole toggle)

  // --- Emotions (features pass) -------------------------------------------
  var EMOTION_REST = 'rest';     // default
  var EMOTION_TALK = 'talk';     // balloon visible (+ grace after it closes)
  var EMOTION_BORED = 'bored';   // 40s idle
  var EMOTION_SLEEP = 'sleep';   // 90s idle -- the old raster "rest" state, renamed
  var EMOTION_VIBE = 'vibe';     // radio playing
  var EMOTION_STOKED = 'stoked'; // add-to-cart
  var EMOTION_THINKING = 'thinking'; // ask-Bolty answer fetch in flight (2026-08-01) -- pupils up, closed-mouth smile; ranks above talk since the balloon is open the whole time this is active

  var BORED_DROOP_FRAC = 0.60;   // vs base 44% (LID_DROP)
  var BORED_PUPIL_DRIFT = 3;     // px, horizontal
  var BROW_OFFSET_VIBE = -3;     // up 3px
  var BROW_OFFSET_STOKED = -5;   // up 5px
  var BROW_OFFSET_BORED = 2;     // down 2px

  function lerp(a, b, t) { return a + (b - a) * t; }

  function multiplyHex(hex, factor) {
    var n = parseInt(hex.replace('#', ''), 16);
    var r = Math.round(((n >> 16) & 255) * factor);
    var g = Math.round(((n >> 8) & 255) * factor);
    var b = Math.round((n & 255) * factor);
    return 'rgb(' + r + ',' + g + ',' + b + ')';
  }

  // Quadratic-bezier control point that makes the curve's own t=0.5 point
  // sit `lift` px above (positive) or below (negative) the straight chord
  // midpoint -- point(0.5) = mid + 0.5*(control-mid), so control must be
  // offset from mid by 2x the desired lift, in the same direction (up on
  // screen = smaller y = this formula's positive `lift` maps to a
  // NEGATIVE y offset).
  //
  // Polish-pass bugfix: this doc comment previously had the above
  // backwards (claimed negative lift == above), while the actual
  // formula always implemented positive-lift-is-up. buildBrow's caller
  // trusted the wrong doc comment and passed a negative lift intending
  // "bow up," which this formula read as "bow down" -- pulling the
  // brow's control point toward the eye instead of away from it, which
  // is what actually produced the reported "thick straight slash" look
  // (short chord + thick 4px stroke + a control point sagging the wrong
  // way reads as a near-straight diagonal, not the flagged geometry
  // being merely under-curved). Fixed by dropping bowControl from
  // buildBrow entirely in favor of literal coordinates (see below), and
  // by flipping LID_BOW's sign so its own "bows up 1px" call is now
  // actually correct too -- same latent bug, much smaller visual effect
  // there (1px on a heavily-dithered edge), not separately reported.
  function bowControl(ax, ay, bx, by, lift) {
    return { x: (ax + bx) / 2, y: (ay + by) / 2 - lift * 2 };
  }

  // --- SVG builders ----------------------------------------------------

  function buildDefs() {
    return (
      '<defs>' +
        '<linearGradient id="boltyChromeHead" x1="0" y1="0" x2="1" y2="0">' +
          '<stop offset="0%" stop-color="#6b6f73"/>' +
          '<stop offset="30%" stop-color="#e8ecef"/>' +
          '<stop offset="45%" stop-color="#ffffff"/>' +
          '<stop offset="60%" stop-color="#aeb4b8"/>' +
          '<stop offset="80%" stop-color="#7d8286"/>' +
          '<stop offset="100%" stop-color="#dfe3e6"/>' +
        '</linearGradient>' +
        '<linearGradient id="boltyChromeShank" x1="0" y1="0" x2="1" y2="0">' +
          '<stop offset="0%" stop-color="#5f6367"/>' +
          '<stop offset="28%" stop-color="#d9dde0"/>' +
          '<stop offset="48%" stop-color="#ffffff"/>' +
          '<stop offset="65%" stop-color="#9ba0a4"/>' +
          '<stop offset="100%" stop-color="#71767a"/>' +
        '</linearGradient>' +
      '</defs>'
    );
  }

  // Flat cone (trapezoid) + top cap ellipse + inner recess ellipse. Cone
  // bottom (23 each side of CX = 46 wide) matches the shank width exactly
  // so the two meet with no seam.
  function buildHead() {
    var xOuterL = CX - 45.5, xInnerL = CX - 23, xInnerR = CX + 23, xOuterR = CX + 45.5;
    var coneBottomY = TOP_Y + 21;
    var cone =
      '<path d="M' + xOuterL + ',' + TOP_Y + ' L' + xInnerL + ',' + coneBottomY +
        ' L' + xInnerR + ',' + coneBottomY + ' L' + xOuterR + ',' + TOP_Y + ' Z" ' +
        'fill="url(#boltyChromeHead)" stroke="#4a4d50" stroke-width="1"/>';
    var outerEllipse =
      '<ellipse cx="' + CX + '" cy="' + TOP_Y + '" rx="45.5" ry="8.2" ' +
        'fill="url(#boltyChromeHead)" stroke="#4a4d50" stroke-width="1"/>';
    // Inner ellipse: recessed drive detail on the head's flat top face,
    // same ry-to-rx squash ratio as the outer cap (both flat, same
    // viewing angle) -- ry = 8.2 * (29/45.5). No explicit ry given in
    // spec (only rx=29), this is the reading that keeps both ellipses
    // optically consistent.
    var innerRy = (8.2 * 29 / 45.5).toFixed(2);
    var innerEllipse =
      '<ellipse cx="' + CX + '" cy="' + TOP_Y + '" rx="29" ry="' + innerRy + '" ' +
        'fill="#7d8286" stroke="#5a5d60" stroke-width="0.6"/>';
    return cone + outerEllipse + innerEllipse;
  }

  function buildShank() {
    var x0 = CX - SW / 2, w = SW, tipR = 9;
    var body =
      '<path d="M' + x0 + ',' + SHANK_TOP +
        ' L' + x0 + ',' + (BOTTOM_Y - tipR) +
        ' Q' + x0 + ',' + BOTTOM_Y + ' ' + (x0 + tipR) + ',' + BOTTOM_Y +
        ' L' + (x0 + w - tipR) + ',' + BOTTOM_Y +
        ' Q' + (x0 + w) + ',' + BOTTOM_Y + ' ' + (x0 + w) + ',' + (BOTTOM_Y - tipR) +
        ' L' + (x0 + w) + ',' + SHANK_TOP + ' Z" ' +
        'fill="url(#boltyChromeShank)" stroke="#4a4d50" stroke-width="1"/>';
    // Smooth region (shankTop -> threadStart): one vertical specular
    // strip, zero thread lines -- the face lives here.
    var specular =
      '<rect x="' + (CX - 10) + '" y="' + SHANK_TOP + '" width="5.5" ' +
        'height="' + (THREAD_START - SHANK_TOP) + '" fill="#ffffff" opacity="0.5"/>';
    // Threads (threadStart -> bottom), every 9px: dark ridge + light
    // highlight 4px below it, both bowing down ~4px at center.
    var threads = '';
    var y = THREAD_START;
    while (y < BOTTOM_Y) {
      threads +=
        '<path d="M' + x0 + ',' + y + ' Q' + CX + ',' + (y + 4) + ' ' + (x0 + w) + ',' + y + '" ' +
          'stroke="#5D6B75" stroke-width="3.6" fill="none" opacity="0.55"/>' +
        '<path d="M' + x0 + ',' + (y + 4) + ' Q' + CX + ',' + (y + 8) + ' ' + (x0 + w) + ',' + (y + 4) + '" ' +
          'stroke="#F3F7F9" stroke-width="2" fill="none" opacity="0.65"/>';
      y += 9;
    }
    return body + specular + threads;
  }

  // One eye (mirrored via `side`: -1 left, +1 right). Pupil drawn full
  // size then clipped by the eye circle AND covered by the lid on top --
  // the layering alone produces the half-moon (lid bottom edge sits
  // BELOW the pupil's own top edge given these numbers).
  function buildEye(side, opts) {
    opts = opts || {};
    var ex = CX + side * EYE_GAP_HALF;
    var r = EYE_R;
    var lidsClosed = opts.lidsClosed;
    var clipId = 'boltyEyeClip' + (side < 0 ? 'L' : 'R');

    // pupilDriftX/pupilDriftY/lidDropOverride: bored-emotion and eye-
    // tracking overrides (pupilDriftY added for eye tracking, this
    // pass -- bored only ever used X) -- absent for every other emotion,
    // base calibration applies.
    var pupilX = ex - side * PUPIL_OFFSET + (opts.pupilDriftX || 0); // toward center
    var pupilY = EYE_Y + PUPIL_Y_OFFSET + (opts.pupilDriftY || 0);

    var lidTopY = EYE_Y - r;
    var baseLidDrop = opts.lidDropOverride != null ? opts.lidDropOverride : LID_DROP;
    var lidDrop = lidsClosed ? r * 2 : baseLidDrop;
    var innerY = lidTopY + lidDrop;                                   // toward center, no lift
    var outerY = lidsClosed ? innerY : innerY - LID_OUTER_LIFT;        // toward temple, 3px higher

    // innerX (toward center) = ex - side*r; outerX (toward temple) = ex + side*r.
    // So the point at x=(ex+r) is innerY for the left eye, outerY for the right.
    var yAtPlusR = (side < 0) ? innerY : outerY;
    var yAtMinusR = (side < 0) ? outerY : innerY;
    var bow = bowControl(ex - r, yAtMinusR, ex + r, yAtPlusR, lidsClosed ? 0 : LID_BOW);

    var edgeColor = multiplyHex(LID_COLOR, 0.82); // #626262

    var veins = '';
    for (var i = 0; i < 2; i++) {
      var vx = ex + (i - 0.5) * r * 0.5 * side;
      var vy = EYE_Y + (i % 2 === 0 ? -r * 0.3 : r * 0.2);
      veins +=
        '<path d="M' + (vx - 4) + ',' + vy + ' q4,-3 8,0" stroke="' + VEIN_COLOR + '" stroke-width="0.6" fill="none" opacity="0.55"/>';
    }

    // Dollar eyes (reaction pack 2): the pupil circle is swapped for the
    // $ glyph, same clip/lid layering as a normal pupil -- still clipped
    // to the eye circle, still covered by the lid on top, so it reads
    // exactly like a pupil replacement rather than a pasted-on overlay.
    var pupilOrDollar = opts.dollarEyes ?
      buildDollarGlyph(pupilX, pupilY) :
      '<circle cx="' + pupilX + '" cy="' + pupilY + '" r="' + PUPIL_R + '" fill="' + PUPIL_COLOR + '"/>';

    return (
      '<g>' +
        '<clipPath id="' + clipId + '"><circle cx="' + ex + '" cy="' + EYE_Y + '" r="' + r + '"/></clipPath>' +
        '<circle cx="' + ex + '" cy="' + EYE_Y + '" r="' + r + '" fill="' + EYE_COLOR + '"/>' +
        veins +
        '<g clip-path="url(#' + clipId + ')">' +
          pupilOrDollar +
          '<path d="M' + (ex - r) + ',' + lidTopY + ' L' + (ex + r) + ',' + lidTopY +
            ' L' + (ex + r) + ',' + yAtPlusR + ' Q' + bow.x + ',' + bow.y + ' ' + (ex - r) + ',' + yAtMinusR + ' Z" ' +
            'fill="' + LID_COLOR + '"/>' +
          '<path d="M' + (ex - r) + ',' + yAtMinusR + ' Q' + bow.x + ',' + bow.y + ' ' + (ex + r) + ',' + yAtPlusR + '" ' +
            'stroke="' + edgeColor + '" stroke-width="1.4" fill="none"/>' +
        '</g>' +
      '</g>'
    );
  }

  // Small arc above the eyes, per the polish-pass's exact literal paths
  // (control point given directly, not derived -- see bowControl's own
  // comment for why the previous derived version rendered as a thick
  // straight slash instead of a visible curve).
  // LEFT:  M (exL-15.6) (eyeY-23.1) Q (exL-2.6) (eyeY-34.6) (exL+10.4) (eyeY-30.1)
  // RIGHT: M (exR+15.6) (eyeY-23.1) Q (exR+2.6) (eyeY-34.6) (exR-10.4) (eyeY-30.1)
  // yOffset (features pass): shifts the whole arc up (negative) or down
  // (positive) as a unit for vibe/stoked/bored's own brow positions.
  function buildBrow(side, yOffset) {
    yOffset = yOffset || 0;
    var ex = CX + side * EYE_GAP_HALF;
    var ax = ex + side * 15.6, ay = EYE_Y - 23.1 + yOffset;
    var cx2 = ex + side * 2.6, cy2 = EYE_Y - 34.6 + yOffset;
    var bx = ex - side * 10.4, by = EYE_Y - 30.1 + yOffset;
    return (
      '<path d="M' + ax + ',' + ay + ' Q' + cx2 + ',' + cy2 + ' ' + bx + ',' + by + '" ' +
        'stroke="#0D1013" stroke-width="4" fill="none" stroke-linecap="round"/>'
    );
  }

  // Mouth is emotion-driven (features pass) -- SMILE (closed, default)
  // for rest/sleep, GRIN (open, the correction-pass mouth) for
  // talk/vibe/stoked, FLAT (straight line) for bored.
  var MOUTH_SMILE = 'smile', MOUTH_GRIN = 'grin', MOUTH_FLAT = 'flat';
  // Reaction pack 2: OOOH (surprised -- a compact dark oval, clearly
  // smaller/rounder than the grin's wide tooth band) and PUCKER (smoke --
  // a small puckered circle, smaller still, meant to sit off-center via
  // buildCharacterSVG's mouthOffsetX rather than centered like every
  // other variant here).
  var MOUTH_OOOH = 'oooh', MOUTH_PUCKER = 'pucker';

  function buildMouth(variant) {
    if (variant === MOUTH_GRIN) { return buildMouthGrin(); }
    if (variant === MOUTH_FLAT) { return buildMouthFlat(); }
    if (variant === MOUTH_OOOH) { return buildMouthOooh(); }
    if (variant === MOUTH_PUCKER) { return buildMouthPucker(); }
    return buildMouthSmile();
  }

  // <path d="M (cx-14) (mY) Q (cx) (mY+8.4) (cx+14) (mY-3)" .../> -- exact
  // literal formula, deliberately asymmetric (left corner at baseline,
  // right corner raised 3px) rather than a mirrored smile.
  function buildMouthSmile() {
    return (
      '<path d="M' + (CX - 14) + ',' + MOUTH_Y + ' Q' + CX + ',' + (MOUTH_Y + 8.4) + ' ' + (CX + 14) + ',' + (MOUTH_Y - 3) + '" ' +
        'stroke="#0D1013" stroke-width="2.6" fill="none" stroke-linecap="round"/>'
    );
  }

  function buildMouthFlat() {
    var w = 22, x0 = CX - w / 2, x1 = CX + w / 2;
    return (
      '<line x1="' + x0 + '" y1="' + MOUTH_Y + '" x2="' + x1 + '" y2="' + MOUTH_Y + '" ' +
        'stroke="#0D1013" stroke-width="2.6" stroke-linecap="round"/>'
    );
  }

  // Open grin: white tooth band (top bows up 4px), 3 dividers, black gap
  // below (~11px deep, deeper on the left than the right).
  function buildMouthGrin() {
    var x0 = CX - MOUTH_W / 2, x1 = CX + MOUTH_W / 2;
    var toothH = 7;
    var toothBottomY = MOUTH_Y + toothH;
    var topBow = bowControl(x0, MOUTH_Y, x1, MOUTH_Y, 4); // bows up 4px at center
    var gapLeftDepth = 11, gapRightDepth = 5;

    var dividers = '';
    for (var i = 1; i <= 3; i++) {
      var dx = x0 + (MOUTH_W / 4) * i;
      dividers += '<line x1="' + dx + '" y1="' + MOUTH_Y + '" x2="' + dx + '" y2="' + toothBottomY + '" stroke="#999999" stroke-width="0.8"/>';
    }

    var toothBand =
      '<path d="M' + x0 + ',' + MOUTH_Y + ' Q' + topBow.x + ',' + topBow.y + ' ' + x1 + ',' + MOUTH_Y +
        ' L' + x1 + ',' + toothBottomY + ' L' + x0 + ',' + toothBottomY + ' Z" fill="#f5f2ea"/>';
    var gap =
      '<path d="M' + x0 + ',' + toothBottomY + ' L' + x1 + ',' + toothBottomY +
        ' L' + x1 + ',' + (toothBottomY + gapRightDepth) + ' L' + x0 + ',' + (toothBottomY + gapLeftDepth) + ' Z" fill="#0a0a0a"/>';
    var outline =
      '<path d="M' + x0 + ',' + MOUTH_Y + ' Q' + topBow.x + ',' + topBow.y + ' ' + x1 + ',' + MOUTH_Y +
        ' L' + x1 + ',' + (toothBottomY + gapRightDepth) + ' L' + x0 + ',' + (toothBottomY + gapLeftDepth) + ' Z" ' +
        'fill="none" stroke="#000000" stroke-width="1"/>';

    return toothBand + gap + dividers + outline;
  }

  // Surprised's "oooh" -- a small dark oval, taller than wide, distinct
  // from the grin's 28-wide tooth band at a glance. Centered on CX;
  // surprised never shifts it (only smoke's pucker uses mouthOffsetX).
  function buildMouthOooh() {
    var rx = 6, ry = 8;
    return (
      '<ellipse cx="' + CX + '" cy="' + (MOUTH_Y + 1) + '" rx="' + rx + '" ry="' + ry + '" ' +
        'fill="#0a0a0a" stroke="#000000" stroke-width="1"/>'
    );
  }

  // Smoke's small puckered mouth -- smaller and rounder than oooh, drawn
  // centered here; buildCharacterSVG's mouthOffsetX wraps this in a
  // translate to actually shift it to one side.
  function buildMouthPucker() {
    var r = 4;
    return (
      '<circle cx="' + CX + '" cy="' + (MOUTH_Y + 1) + '" r="' + r + '" ' +
        'fill="#0a0a0a" stroke="#000000" stroke-width="1"/>'
    );
  }

  // Lick's tongue -- a small ellipse in VEIN_COLOR, the only warm/organic
  // tone this file's palette already has (same reuse-an-existing-color
  // convention as drawSmoke's puffs borrowing the sleep zZ's grey, see
  // that function's own comment) -- "tongue color from the existing
  // mouth-interior palette," not a new hex. pos is one of LICK_POSITIONS.
  function buildTongue(pos) {
    return '<ellipse cx="' + pos.x + '" cy="' + pos.y + '" rx="3" ry="2.2" fill="' + VEIN_COLOR + '"/>';
  }

  // Dollar eyes' "$" -- a 3-wide x 5-tall grid of square blocks (roughly
  // the "3x5 px units" spec), drawn the same way the rest of this file
  // draws chunky pixel-art detail: flat-filled SVG rects at a size big
  // enough to survive the dither pass as a coherent shape rather than a
  // sub-block smudge. Three full-width bars connected by single-column
  // zigzag links, same reduced form tiny bitmap fonts use for S/$ at
  // this resolution -- the clearest of several patterns tried (see
  // report to Per: at 3 cols wide there isn't room for a fully separate
  // vertical stroke through the S without the two merging into an
  // unreadable blob; this reads as a deliberate zigzag mark, not a
  // font-accurate dollar sign, and legibility at the LIVE default
  // --eva-bolty-scale is marginal at best -- flagged explicitly, not
  // shipped as a silent assumption).
  var DOLLAR_GLYPH_ROWS = ['XXX', 'X..', 'XXX', '..X', 'XXX'];
  var DOLLAR_GLYPH_COLS = 3;
  var DOLLAR_GLYPH_CELL = 2.4; // character-local (VB_W/VB_H) units per block

  function buildDollarGlyph(cx, cy) {
    var w = DOLLAR_GLYPH_COLS * DOLLAR_GLYPH_CELL;
    var h = DOLLAR_GLYPH_ROWS.length * DOLLAR_GLYPH_CELL;
    var x0 = cx - w / 2, y0 = cy - h / 2;
    var rects = '';
    for (var r = 0; r < DOLLAR_GLYPH_ROWS.length; r++) {
      for (var c = 0; c < DOLLAR_GLYPH_COLS; c++) {
        if (DOLLAR_GLYPH_ROWS[r].charAt(c) === 'X') {
          rects += '<rect x="' + (x0 + c * DOLLAR_GLYPH_CELL) + '" y="' + (y0 + r * DOLLAR_GLYPH_CELL) +
            '" width="' + DOLLAR_GLYPH_CELL + '" height="' + DOLLAR_GLYPH_CELL + '" fill="' + PUPIL_COLOR + '"/>';
        }
      }
    }
    return rects;
  }

  // headphones overlay for the vibe state -- unchanged from the prior
  // build, still valid: keyed off HEAD_CY/hw (91), both untouched by
  // this correction pass.
  function buildHeadphones() {
    var r = 45.5;
    return (
      '<g fill="none" stroke="#111111" stroke-width="4" stroke-linecap="round">' +
        '<path d="M' + (CX - r * 0.8) + ',' + (HEAD_CY - 2) + ' Q' + CX + ',' + (HEAD_CY - r * 0.9) + ' ' + (CX + r * 0.8) + ',' + (HEAD_CY - 2) + '"/>' +
      '</g>' +
      '<circle cx="' + (CX - r * 0.85) + '" cy="' + HEAD_CY + '" r="6" fill="#111111"/>' +
      '<circle cx="' + (CX + r * 0.85) + '" cy="' + HEAD_CY + '" r="6" fill="#111111"/>'
    );
  }

  function buildZZ(tick) {
    var drift = (tick % 8) * 2;
    return (
      '<g font-family="monospace" font-weight="bold" fill="#cfd6da" opacity="0.85">' +
        '<text x="' + (CX + 45.5) + '" y="' + (HEAD_CY - 10 - drift) + '" font-size="' + (10 + drift * 0.3) + '">z</text>' +
        '<text x="' + (CX + 45.5 + 8) + '" y="' + (HEAD_CY - 20 - drift) + '" font-size="' + (7 + drift * 0.2) + '">z</text>' +
      '</g>'
    );
  }

  // Emotion -> mouth variant / brow offset (features pass). rest/sleep
  // both read as SMILE -- sleep isn't listed as GRIN in the spec, and a
  // closed calm mouth reads correctly for a sleeping character anyway.
  function emotionMouth(emotion) {
    if (emotion === EMOTION_TALK || emotion === EMOTION_VIBE || emotion === EMOTION_STOKED) { return MOUTH_GRIN; }
    if (emotion === EMOTION_BORED) { return MOUTH_FLAT; }
    // EMOTION_THINKING also lands here (closed-mouth smile, ask-Bolty
    // spec) -- already the fallback, called out explicitly so it's not
    // mistaken for an oversight.
    return MOUTH_SMILE;
  }

  function emotionBrowOffset(emotion) {
    if (emotion === EMOTION_VIBE) { return BROW_OFFSET_VIBE; }
    if (emotion === EMOTION_STOKED) { return BROW_OFFSET_STOKED; }
    if (emotion === EMOTION_BORED) { return BROW_OFFSET_BORED; }
    return 0;
  }

  // --- Animations (five one-shot, discrete-frame overlays) ----------------
  // Each is a fixed array of frame-override objects, one played per
  // animation tick (activeAnim/getActiveAnimFrame, in the animation-loop
  // section further down, walks these exactly like IDLE_BOB/VIBE_BOB
  // already get walked by tickCount -- same never-eased philosophy).
  // A frame's own fields (rotate/bob/mouthVariant/lidDropOverride/
  // browOffset/pupilDriftX/pupilDriftY/lidsClosed) win over whatever the
  // character's current base emotion would otherwise use for that field
  // -- see buildCharacterSVG's own animOverride handling below. He has
  // no arms, so every one of these expresses entirely through head/eyes/
  // lids/brows/mouth/body lean-bounce (rotate + bob), same vocabulary
  // the emotion system already uses.

  // 1. COUGH -- body jolts forward and settles, mouth opens on the jolt,
  // lids narrow. Sharp and short (COUGH_FRAME_COUNT ticks, ~660ms). Also
  // now emits smoke on each jolt beat (see COUGH_PLAY_FRAMES below) --
  // the jolt itself stays exactly COUGH_FRAME_COUNT frames; the trailing
  // neutral frames exist purely to give the cough-triggered smoke burst
  // room to decay after the jolt's own animation is long over.
  var COUGH_ANIM_FRAMES = [
    { rotate: 6, bob: 3, mouthVariant: MOUTH_GRIN, lidDropOverride: EYE_R * 2 * 0.7 },
    { rotate: 9, bob: 5, mouthVariant: MOUTH_GRIN, lidDropOverride: EYE_R * 2 * 0.85 },
    { rotate: 5, bob: 2, mouthVariant: MOUTH_GRIN, lidDropOverride: EYE_R * 2 * 0.6 },
    { rotate: 2, bob: 0, mouthVariant: MOUTH_FLAT },
    { rotate: 0, bob: 0 },
    { rotate: 0, bob: 0 }
  ];

  // The jolt is only COUGH_FRAME_COUNT (6) ticks, but a cough-triggered
  // smoke burst needs longer than that to finish decaying
  // (COUGH_SMOKE_TOTAL_FRAMES) -- same pattern buildSmokeFrames uses for
  // the ambient exhale below, where the mouth relaxes back well before
  // the puffs themselves finish fading, because the puff visuals are
  // driven by the frame INDEX alone (activeAnim.smokeStyle), never by
  // anything in a frame's own override. Padded with neutral (no
  // override) frames rather than a second frames array or a second
  // activeAnim slot, so playCough always plays through this one array.
  var COUGH_PLAY_FRAMES = COUGH_ANIM_FRAMES.slice();
  while (COUGH_PLAY_FRAMES.length < COUGH_SMOKE_TOTAL_FRAMES) { COUGH_PLAY_FRAMES.push({}); }

  // 2. BLOWING SMOKE -- no smoke asset exists, so the puffs are drawn
  // procedurally straight onto the raw stage canvas (drawSmokePuffs, in
  // the "Scene stage" section further down, where CHAR_TX/CHAR_SCALE/etc.
  // live) so they pass through the SAME dither pass as the character
  // instead of looking pasted on. Refinement pass: the character-level
  // override now shifts the mouth to one side and puckers it small
  // (MOUTH_PUCKER + mouthOffsetX, SMOKE_MOUTH_SIDE/SMOKE_MOUTH_OFFSET_X)
  // for the whole puff-release window, settling back to the base emotion
  // mouth once puffing is done -- drawSmokePuffs' own emission point
  // moves to match (see that function), so the puffs visibly leave from
  // the puckered corner rather than mouth-center. Puff visuals themselves
  // are still driven by the frame INDEX alone (activeAnim.smokeStyle,
  // 'ambient' here), not by anything in this array -- unchanged puff
  // system, only the mouth/emission-point moved.
  function buildSmokeFrames() {
    var frames = [];
    var puckerOffsetX = SMOKE_MOUTH_SIDE * SMOKE_MOUTH_OFFSET_X;
    for (var i = 0; i < SMOKE_FRAME_COUNT; i++) {
      frames.push(i < SMOKE_PUCKER_FRAME_COUNT ?
        { mouthVariant: MOUTH_PUCKER, mouthOffsetX: puckerOffsetX } :
        {});
    }
    return frames;
  }
  var SMOKE_ANIM_FRAMES = buildSmokeFrames();

  // 3. NODDING OFF -- the missing bridge between bored and sleep. Dips
  // (rotate+bob forward, lids dropping), holds, jerks back up (a beat of
  // wider-than-normal eyes), repeats NOD_OFF_CYCLES times with each cycle
  // dipping further and holding longer ("heavier") before finally
  // handing off to sleep (see startNoddingOff's onComplete, further
  // down) -- replaces the old instant bored->sleep switch entirely.
  function buildNoddingOffFrames() {
    var frames = [];
    for (var cycle = 0; cycle < NOD_OFF_CYCLES; cycle++) {
      var heaviness = (cycle + 1) / NOD_OFF_CYCLES;
      var dipRotate = 8 + heaviness * 10;
      var dipBob = 4 + heaviness * 6;
      var dipLidDrop = EYE_R * 2 * (0.6 + heaviness * 0.35);
      var holdTicks = NOD_OFF_BASE_HOLD_TICKS + cycle * NOD_OFF_HOLD_GROWTH_TICKS;

      frames.push({ rotate: dipRotate * 0.5, bob: Math.round(dipBob * 0.5), lidDropOverride: dipLidDrop * 0.6 });
      frames.push({ rotate: dipRotate, bob: Math.round(dipBob), lidDropOverride: dipLidDrop });
      for (var h = 0; h < holdTicks; h++) {
        frames.push({ rotate: dipRotate, bob: Math.round(dipBob), lidDropOverride: dipLidDrop });
      }
      frames.push({ rotate: -3, bob: -2, lidDropOverride: LID_DROP * 0.5 }); // startled-wide jerk back up
      frames.push({ rotate: 0, bob: 0 }); // settles before the next, heavier cycle
    }
    return frames;
  }
  var NODDING_OFF_FRAMES = buildNoddingOffFrames();

  // 4. YAWN -- refinement pass: a proper body stretch now leads in
  // (body elongates upward, head tilts back -- YAWN_STRETCH_*), THEN the
  // mouth opens wide and slow exactly as originally built, lids close,
  // then settles back down through the same frames as before. The open
  // ramp's own bob/rotate now continue from the stretch's end values
  // (YAWN_STRETCH_BOB/YAWN_STRETCH_ROTATE) down to the peak's -3
  // bob/0 rotate, rather than starting flat at 0 -- one continuous lead-
  // in, not two disconnected segments. Peak/close are untouched from the
  // original build. Also now doubles as the wake-from-sleep transition
  // (see noteActivity) -- same frames either way, just a different
  // trigger.
  function buildYawnFrames() {
    var frames = [];
    var i, t;
    for (i = 0; i < YAWN_STRETCH_FRAME_COUNT; i++) {
      t = (i + 1) / YAWN_STRETCH_FRAME_COUNT;
      frames.push({
        bob: Math.round(lerp(0, YAWN_STRETCH_BOB, t)),
        rotate: lerp(0, YAWN_STRETCH_ROTATE, t)
      });
    }
    var openFrames = 5, peakFrames = 4;
    var closeFrames = YAWN_FRAME_COUNT - openFrames - peakFrames;
    for (i = 0; i < openFrames; i++) {
      t = (i + 1) / openFrames;
      frames.push({
        mouthVariant: MOUTH_GRIN,
        lidDropOverride: lerp(LID_DROP, EYE_R * 2, t),
        bob: Math.round(lerp(YAWN_STRETCH_BOB, -3, t)),
        rotate: lerp(YAWN_STRETCH_ROTATE, 0, t)
      });
    }
    for (i = 0; i < peakFrames; i++) {
      frames.push({ mouthVariant: MOUTH_GRIN, lidDropOverride: EYE_R * 2, lidsClosed: true, bob: -3 });
    }
    for (i = 0; i < closeFrames; i++) {
      t = (i + 1) / closeFrames;
      frames.push({
        mouthVariant: MOUTH_GRIN,
        lidDropOverride: lerp(EYE_R * 2, LID_DROP, t),
        bob: Math.round(lerp(-3, 0, t))
      });
    }
    return frames;
  }
  var YAWN_ANIM_FRAMES = buildYawnFrames();

  // 5. SPYING -- eyes track deliberately around the frame as if watching
  // something off to the side, lids narrowed, head and body still
  // (rotate/bob both 0 throughout -- everything here is in the pupils).
  // Built on the exact same pupilDriftX/pupilDriftY fields the cursor-
  // tracking mechanism (computeEyeTrackOffset, further down) feeds into
  // buildEye -- same subsystem per spec, just a scripted sequence of
  // look positions instead of a live cursor position. Duration =
  // SPYING_HOLD_TICKS * SPYING_LOOK_POSITIONS.length -- add/remove
  // entries here to change where he looks, or the hold constant to
  // change how long each glance lasts.
  var SPYING_LOOK_POSITIONS = [
    { x: -3, y: 0.5 },  // glance off to one side
    { x: 3, y: -0.5 },  // then the other, slightly up
    { x: 0, y: 0 }       // back to center before handing back to normal rendering
  ];

  function buildSpyingFrames() {
    var frames = [];
    var lidDrop = EYE_R * 2 * 0.55; // narrowed, not closed
    SPYING_LOOK_POSITIONS.forEach(function (pos) {
      for (var i = 0; i < SPYING_HOLD_TICKS; i++) {
        frames.push({ pupilDriftX: pos.x, pupilDriftY: pos.y, lidDropOverride: lidDrop, rotate: 0, bob: 0 });
      }
    });
    return frames;
  }
  var SPYING_ANIM_FRAMES = buildSpyingFrames();

  // 6. SURPRISED (reaction pack 2) -- brows jump up, lids pull fully up
  // (wider than normal, not the usual base LID_DROP), mouth snaps to a
  // small dark "oooh" distinct from the grin's wide tooth band. Two
  // ramp-in frames reach the peak, SURPRISED_HOLD_TICKS holds it, then a
  // short settle back to neutral -- ~1s total (SURPRISED_FRAME_COUNT).
  // No body movement (rotate/bob untouched, default 0) -- purely a
  // facial reaction, per spec's own description of it.
  function buildSurprisedFrames() {
    var frames = [];
    frames.push({ browOffset: SURPRISED_BROW_OFFSET * 0.6, lidDropOverride: lerp(LID_DROP, SURPRISED_LID_DROP, 0.6), mouthVariant: MOUTH_OOOH });
    frames.push({ browOffset: SURPRISED_BROW_OFFSET, lidDropOverride: SURPRISED_LID_DROP, mouthVariant: MOUTH_OOOH });
    var i;
    for (i = 0; i < SURPRISED_HOLD_TICKS; i++) {
      frames.push({ browOffset: SURPRISED_BROW_OFFSET, lidDropOverride: SURPRISED_LID_DROP, mouthVariant: MOUTH_OOOH });
    }
    var settleFrames = SURPRISED_FRAME_COUNT - frames.length;
    for (i = 0; i < settleFrames; i++) {
      var t = (i + 1) / settleFrames;
      frames.push({
        browOffset: lerp(SURPRISED_BROW_OFFSET, 0, t),
        lidDropOverride: lerp(SURPRISED_LID_DROP, LID_DROP, t),
        mouthVariant: MOUTH_OOOH
      });
    }
    return frames;
  }
  var SURPRISED_ANIM_FRAMES = buildSurprisedFrames();

  // 7. LICK (reaction pack 2) -- a small tongue (buildTongue, drawn in
  // VEIN_COLOR) visits a few discrete positions around the mouth
  // outline: one corner, the top, the other corner -- then it's gone.
  // Same scripted-positions technique SPYING_LOOK_POSITIONS already
  // uses, just aimed at the mouth instead of the pupils. Head/body/
  // mouth-variant are untouched throughout -- only the tongue overlay
  // (animOverride.tonguePos, drawn by buildCharacterSVG) appears and
  // disappears.
  var LICK_POSITIONS = [
    { x: CX - 11, y: MOUTH_Y + 2 },  // corner
    { x: CX, y: MOUTH_Y - 5 },       // top
    { x: CX + 11, y: MOUTH_Y + 2 }   // other corner
  ];

  function buildLickFrames() {
    var frames = [];
    LICK_POSITIONS.forEach(function (pos) {
      for (var i = 0; i < LICK_HOLD_TICKS; i++) {
        frames.push({ tonguePos: pos });
      }
    });
    return frames;
  }
  var LICK_ANIM_FRAMES = buildLickFrames();

  // 8. JUGGLE (reaction pack 3) -- both hands, three hex nuts (this
  // shop's own hardware, not generic balls) cascading in an arc above
  // him. Every hand-bearing anim opens/closes with exactly one
  // materialize<1 frame ("quick dither-in/out, not a slide or fade") and
  // is otherwise fully solid -- established once here, reused as-is by
  // SCRATCH/NOSE_PICK below.
  //
  // Each nut bounces left-hand -> right-hand -> left-hand on its own
  // JUGGLE_CYCLE_TICKS-long loop, arcing up via a single sin() hump per
  // leg (peaks mid-leg, lands near hand height at each end) -- not real
  // juggling physics, just a readable "arcing between two points above
  // him" silhouette at this file's own 9fps/stepped-never-eased frame
  // rate. The three nuts are staggered a third of a cycle apart
  // (JUGGLE_PHASE_OFFSETS) so they overlap in a cascade rhythm rather
  // than moving in lockstep. Hands themselves stay put at HAND_REST --
  // the nuts crossing between them is what reads as "juggling," a second
  // hand-bob layered on top would be motion for its own sake.
  var JUGGLE_CYCLE_TICKS = 8;   // one full left-right-left bounce per nut
  var JUGGLE_CYCLES = 3;        // "loops a few cycles then stops"
  var JUGGLE_ARC_HEIGHT = 30;   // character-local units above hand height each throw peaks at
  var JUGGLE_LEFT_X = CX - HAND_REST_X_OFFSET, JUGGLE_RIGHT_X = CX + HAND_REST_X_OFFSET;
  var JUGGLE_PEAK_Y = HAND_REST_Y - 20 - JUGGLE_ARC_HEIGHT;
  var JUGGLE_PHASE_OFFSETS = [0, JUGGLE_CYCLE_TICKS / 3, JUGGLE_CYCLE_TICKS * 2 / 3];

  function juggleObjectPos(tickInCycle) {
    var t = tickInCycle / JUGGLE_CYCLE_TICKS;
    t = t - Math.floor(t); // wrap into [0,1)
    var leftToRight = t < 0.5;
    var legT = leftToRight ? (t / 0.5) : ((t - 0.5) / 0.5);
    var startX = leftToRight ? JUGGLE_LEFT_X : JUGGLE_RIGHT_X;
    var endX = leftToRight ? JUGGLE_RIGHT_X : JUGGLE_LEFT_X;
    return {
      x: lerp(startX, endX, legT),
      y: lerp(HAND_REST_Y - 8, JUGGLE_PEAK_Y, Math.sin(legT * Math.PI))
    };
  }

  function juggleHandsAt(materialize, objects, seed) {
    return {
      left: { x: JUGGLE_LEFT_X, y: HAND_REST_Y, rot: 0, materialize: materialize },
      right: { x: JUGGLE_RIGHT_X, y: HAND_REST_Y, rot: 0, materialize: materialize },
      objects: objects || [],
      seed: seed
    };
  }

  function buildJuggleFrames() {
    var frames = [];
    frames.push({ hands: juggleHandsAt(HAND_ENTRANCE_MATERIALIZE, [], 0) });

    var totalSteadyTicks = JUGGLE_CYCLE_TICKS * JUGGLE_CYCLES;
    for (var i = 0; i < totalSteadyTicks; i++) {
      var objs = JUGGLE_PHASE_OFFSETS.map(function (offset) {
        var pos = juggleObjectPos(i + offset);
        return { x: pos.x, y: pos.y, rot: (i * 25) % 360, materialize: 1 };
      });
      // Eyes track the cascade's own center of mass, not any one nut --
      // smoother than snapping between three independent targets, still
      // clearly "watching the juggling," clamped to the same small
      // magnitude SPYING already established for scripted pupil drift.
      var avgX = (objs[0].x + objs[1].x + objs[2].x) / 3;
      var driftX = Math.max(-3, Math.min(3, ((avgX - CX) / HAND_REST_X_OFFSET) * 3));
      frames.push({ pupilDriftX: driftX, hands: juggleHandsAt(1, objs, i + 1) });
    }

    // Nuts vanish along with the hands on the way out -- he "catches"
    // them off-screen rather than needing a separate settle animation.
    frames.push({ hands: juggleHandsAt(HAND_ENTRANCE_MATERIALIZE, [], totalSteadyTicks + 1) });
    return frames;
  }
  var JUGGLE_ANIM_FRAMES = buildJuggleFrames();

  // 9. SCRATCH (reaction pack 3) -- one hand up at the side of the head
  // plate, small circular scratching motion, brows shifting slightly in
  // sync. The other hand never appears at all (spec, not an oversight).
  var SCRATCH_SIDE = 1; // right hand -- arbitrary, "pick one and keep it consistent" (same convention as SMOKE_MOUTH_SIDE)
  var SCRATCH_ANCHOR_X = CX + SCRATCH_SIDE * 50, SCRATCH_ANCHOR_Y = HEAD_CY - 10;
  var SCRATCH_R = 5;
  var SCRATCH_TICKS_PER_CIRCLE = 6;
  var SCRATCH_CIRCLES = 2; // "few cycles"

  function scratchHandsAt(angle, materialize, seed) {
    var key = SCRATCH_SIDE < 0 ? 'left' : 'right';
    var hands = { seed: seed };
    hands[key] = {
      x: SCRATCH_ANCHOR_X + Math.cos(angle) * SCRATCH_R,
      y: SCRATCH_ANCHOR_Y + Math.sin(angle) * SCRATCH_R,
      rot: Math.sin(angle) * 12, // small wrist wobble, not a full spin
      materialize: materialize
    };
    return hands;
  }

  function buildScratchFrames() {
    var frames = [];
    frames.push({ hands: scratchHandsAt(0, HAND_ENTRANCE_MATERIALIZE, 0) });

    var totalTicks = SCRATCH_TICKS_PER_CIRCLE * SCRATCH_CIRCLES;
    for (var i = 0; i < totalTicks; i++) {
      var angle = (i / SCRATCH_TICKS_PER_CIRCLE) * Math.PI * 2;
      frames.push({
        browOffset: Math.sin(angle) * 2, // "brows can shift with it" -- small, in step with the circular motion
        hands: scratchHandsAt(angle, 1, i + 1)
      });
    }

    frames.push({ hands: scratchHandsAt(0, HAND_ENTRANCE_MATERIALIZE, totalTicks + 1) });
    return frames;
  }
  var SCRATCH_ANIM_FRAMES = buildScratchFrames();

  // 10. NOSE_PICK (reaction pack 3) -- the gag is the absence: one hand
  // travels up to where a nose would sit on the bolt head, finds
  // nothing, pokes around a couple of small offsets, gives up, retreats.
  // Deadpan per spec -- the only facial beat is the mouth going flat for
  // the poke frames, nothing bigger (no surprise brows, no held take).
  var NOSEPICK_SIDE = 1; // consistent with SCRATCH_SIDE, arbitrary
  var NOSEPICK_X = CX + NOSEPICK_SIDE * 6; // just off-center -- approaches from its own side, doesn't cross the midline
  var NOSEPICK_NOSE_Y = EYE_Y + EYE_R + 9; // just below the eyes' own bottom edge, clear of them -- "where a nose would be" without the glove obscuring the eyes themselves (EYE_R=18.5 means EYE_Y+12 -- the original value -- still sat inside the eye; +6 still grazed the lower lid; confirmed live 2026-08-10). MOUTH_Y is EYE_Y+24.5, so this still clears the mouth by real margin.
  var NOSEPICK_TRAVEL_STEPS = 3;
  var NOSEPICK_POKE_OFFSETS = [{ x: 0, y: 0 }, { x: -2, y: 1 }, { x: 2, y: -1 }]; // small, brief -- searching, not flailing

  function nosePickHandsAt(x, y, materialize, seed) {
    var key = NOSEPICK_SIDE < 0 ? 'left' : 'right';
    var hands = { seed: seed };
    hands[key] = { x: x, y: y, rot: 0, materialize: materialize };
    return hands;
  }

  function buildNosePickFrames() {
    var frames = [];
    var restX = NOSEPICK_SIDE < 0 ? (CX - HAND_REST_X_OFFSET) : (CX + HAND_REST_X_OFFSET);
    var seed = 0;

    frames.push({ hands: nosePickHandsAt(restX, HAND_REST_Y, HAND_ENTRANCE_MATERIALIZE, seed++) });

    var i, t;
    for (i = 1; i <= NOSEPICK_TRAVEL_STEPS; i++) {
      t = i / NOSEPICK_TRAVEL_STEPS;
      frames.push({ hands: nosePickHandsAt(lerp(restX, NOSEPICK_X, t), lerp(HAND_REST_Y, NOSEPICK_NOSE_Y, t), 1, seed++) });
    }

    NOSEPICK_POKE_OFFSETS.forEach(function (off) {
      frames.push({
        mouthVariant: MOUTH_FLAT,
        hands: nosePickHandsAt(NOSEPICK_X + off.x, NOSEPICK_NOSE_Y + off.y, 1, seed++)
      });
    });

    for (i = 1; i <= NOSEPICK_TRAVEL_STEPS; i++) {
      t = i / NOSEPICK_TRAVEL_STEPS;
      frames.push({ hands: nosePickHandsAt(lerp(NOSEPICK_X, restX, t), lerp(NOSEPICK_NOSE_Y, HAND_REST_Y, t), 1, seed++) });
    }

    frames.push({ hands: nosePickHandsAt(restX, HAND_REST_Y, HAND_ENTRANCE_MATERIALIZE, seed++) });
    return frames;
  }
  var NOSEPICK_ANIM_FRAMES = buildNosePickFrames();

  // Bored's pupil drift: stepped (never eased, matches the rest of this
  // file's animation philosophy), BORED_DRIFT_STEPS positions over an
  // ~11s cycle, oscillating via one sine sample per step -- not a
  // continuous per-tick recompute, so it stays cheap to cache (one raster
  // per step, not one per tick).
  var BORED_DRIFT_STEPS = 10;
  // 110 here must match TICK_MS below -- can't reference it directly,
  // this runs at module-init time, before TICK_MS's own assignment.
  var BORED_DRIFT_CYCLE_TICKS = Math.round(11000 / 110);

  function boredDriftPhase(tick) {
    return Math.floor((tick % BORED_DRIFT_CYCLE_TICKS) / (BORED_DRIFT_CYCLE_TICKS / BORED_DRIFT_STEPS));
  }

  function boredDriftX(tick) {
    var phase = boredDriftPhase(tick);
    return Math.round(Math.sin((phase / BORED_DRIFT_STEPS) * Math.PI * 2) * BORED_PUPIL_DRIFT);
  }

  // Full character SVG string. emotion: 'rest' | 'talk' | 'bored' |
  // 'sleep' | 'vibe' | 'stoked'. blinking is orthogonal (see top-of-file
  // comment) -- a brief lids-closed override on top of any emotion.
  // trackOffset ({x,y} or null/undefined): cursor eye-tracking nudge, see
  // the "Eye tracking" section below -- only ever non-null when emotion
  // is REST (the eye-tracking gate itself enforces that), so it can never
  // fight bored's own pupilDriftX.
  // animOverride (one of the animations' own current frame, or
  // null/undefined): wins over EVERYTHING above field-by-field when
  // present -- a cough playing during REST still shows REST's own base
  // values for any field the current frame doesn't itself set (e.g.
  // frame 3 of COUGH_ANIM_FRAMES has no lidDropOverride, so that tick's
  // lids read whatever the base emotion would already show). Reaction
  // pack 2 adds two more animOverride fields on top of the original set
  // (mouthOffsetX, tonguePos) plus a wholly separate trailing param,
  // dollarEyes -- NOT part of animOverride, since dollar eyes overlay on
  // top of the add-to-cart reaction without ever consuming the single
  // activeAnim slot (see BOLTY_DOLLAR_EYES_CHANCE's own comment).
  function buildCharacterSVG(emotion, tick, blinking, trackOffset, animOverride, dollarEyes) {
    var lidsClosed = blinking || emotion === EMOTION_SLEEP;
    if (animOverride && animOverride.lidsClosed != null) { lidsClosed = animOverride.lidsClosed; }
    var rotate = (animOverride && animOverride.rotate != null) ? animOverride.rotate : (emotion === EMOTION_SLEEP ? -7.5 : 0);
    // Ask-Bolty type-out reveal (isAskAnswering, module state) overrides
    // the mouth every OTHER tick regardless of emotion -- "mouth
    // alternates grin frames" while the answer text is streaming in,
    // synced for free off the same tick the reveal itself advances on
    // (see drawFrame). askMouthOverride is the one-shot settle after the
    // reveal finishes ("settles to smile at the end"), cleared the next
    // time a balloon/menu/ask flow opens fresh.
    var mouthVariant = (animOverride && animOverride.mouthVariant) ? animOverride.mouthVariant :
      isAskAnswering ? (tick % 2 === 0 ? MOUTH_GRIN : MOUTH_SMILE) :
      (askMouthOverride || emotionMouth(emotion));
    var browOffset = (animOverride && animOverride.browOffset != null) ? animOverride.browOffset : emotionBrowOffset(emotion);
    var mouthOffsetX = (animOverride && animOverride.mouthOffsetX) ? animOverride.mouthOffsetX : 0;
    var tonguePos = animOverride && animOverride.tonguePos;

    var eyeOpts = { lidsClosed: lidsClosed, dollarEyes: !!dollarEyes };
    if (emotion === EMOTION_BORED) {
      eyeOpts.lidDropOverride = EYE_R * 2 * BORED_DROOP_FRAC;
      eyeOpts.pupilDriftX = boredDriftX(tick);
    } else if (emotion === EMOTION_REST && trackOffset) {
      eyeOpts.pupilDriftX = trackOffset.x;
      eyeOpts.pupilDriftY = trackOffset.y;
    } else if (emotion === EMOTION_THINKING) {
      eyeOpts.pupilDriftY = PUPIL_LOOK_UP_OFFSET;
    }
    if (animOverride) {
      if (animOverride.lidDropOverride != null) { eyeOpts.lidDropOverride = animOverride.lidDropOverride; }
      if (animOverride.pupilDriftX != null) { eyeOpts.pupilDriftX = animOverride.pupilDriftX; }
      if (animOverride.pupilDriftY != null) { eyeOpts.pupilDriftY = animOverride.pupilDriftY; }
    }

    var parts = [];
    parts.push('<svg xmlns="http://www.w3.org/2000/svg" width="' + VB_W + '" height="' + VB_H + '" viewBox="0 0 ' + VB_W + ' ' + VB_H + '">');
    parts.push(buildDefs());
    parts.push('<g transform="rotate(' + rotate + ' ' + CX + ' ' + HEAD_CY + ')">');
    parts.push(buildShank());
    parts.push(buildHead());
    parts.push(buildEye(-1, eyeOpts));
    parts.push(buildEye(1, eyeOpts));
    if (mouthOffsetX) {
      parts.push('<g transform="translate(' + mouthOffsetX + ',0)">' + buildMouth(mouthVariant) + '</g>');
    } else {
      parts.push(buildMouth(mouthVariant));
    }
    if (tonguePos) { parts.push(buildTongue(tonguePos)); }
    parts.push(buildBrow(-1, browOffset));
    parts.push(buildBrow(1, browOffset));
    if (emotion === EMOTION_VIBE) { parts.push(buildHeadphones()); }
    parts.push('</g>');
    if (emotion === EMOTION_SLEEP) { parts.push(buildZZ(tick)); }
    parts.push('</svg>');
    return parts.join('');
  }

  // --- Rasterize SVG -> offscreen canvas (raw, undithered) --------------

  var charRasterCache = {}; // keyed by charVariantKey()

  function rasterizeSVG(svgString, cb) {
    var img = new Image();
    var blob;
    try {
      blob = new Blob([svgString], { type: 'image/svg+xml' });
    } catch (e) {
      cb(null);
      return;
    }
    var url = URL.createObjectURL(blob);
    img.onload = function () {
      var canvas = document.createElement('canvas');
      canvas.width = VB_W;
      canvas.height = VB_H;
      var ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(img, 0, 0, VB_W, VB_H);
      URL.revokeObjectURL(url);
      cb(canvas);
    };
    img.onerror = function () {
      URL.revokeObjectURL(url);
      cb(null);
    };
    img.src = url;
  }

  // sleep varies by tick%8 (zZ drift), bored by its own stepped drift
  // phase, rest by its own (also stepped/discrete, never continuous --
  // see the eye-tracking section) trackOffset grid position, blink is an
  // independent suffix on top of any of those -- all bounded (8, 10, a
  // small fixed grid, and 2 respectively), not one raster per tick.
  function charVariantKey(emotion, tick, blinking, trackOffset, animOverride, dollarEyes) {
    var key = emotion;
    if (emotion === EMOTION_SLEEP) { key += ':' + (tick % 8); }
    if (emotion === EMOTION_BORED) { key += ':' + boredDriftPhase(tick); }
    if (emotion === EMOTION_REST && trackOffset) { key += ':eye' + trackOffset.x + ',' + trackOffset.y; }
    if (blinking) { key += ':blink'; }
    // Ask-Bolty type-out reveal alternates the mouth by tick%2 independent
    // of emotion/animOverride (see buildCharacterSVG) -- without this, two
    // frames a tick apart during the reveal would collide on the same
    // cache key (TALK's own key doesn't otherwise vary by tick at all)
    // and the second would silently render the first's stale mouth.
    // askMouthOverride (the post-reveal settle) is a plain string/null,
    // safe to key on directly.
    if (isAskAnswering) { key += ':ask' + (tick % 2); }
    if (askMouthOverride) { key += ':askset' + askMouthOverride; }
    // animOverride objects are always one of this file's own small,
    // precomputed frame descriptors (bounded set -- at most a few dozen
    // distinct combinations across all animations combined), never
    // arbitrary data, so a plain values-join is enough here -- no
    // JSON.stringify key-order risk since the property list is fixed.
    if (animOverride) {
      key += ':a' + [
        animOverride.rotate, animOverride.mouthVariant, animOverride.lidDropOverride,
        animOverride.browOffset, animOverride.pupilDriftX, animOverride.pupilDriftY,
        animOverride.lidsClosed, animOverride.mouthOffsetX,
        animOverride.tonguePos ? (animOverride.tonguePos.x + ',' + animOverride.tonguePos.y) : ''
      ].join(',');
    }
    if (dollarEyes) { key += ':dollar'; }
    return key;
  }

  function getCharRaster(emotion, tick, blinking, trackOffset, animOverride, dollarEyes, cb) {
    var key = charVariantKey(emotion, tick, blinking, trackOffset, animOverride, dollarEyes);
    if (charRasterCache[key]) { cb(charRasterCache[key]); return; }
    rasterizeSVG(buildCharacterSVG(emotion, tick, blinking, trackOffset, animOverride, dollarEyes), function (canvas) {
      if (canvas) { charRasterCache[key] = canvas; }
      cb(canvas);
    });
  }

  // --- Scene stage: box (backdrop) + cast shadow + character ------------
  // 340x330 stage. Z-order: box -> shadow -> Bolty. Box is Per's own
  // cutout PNG (assets/bolty-box-cutout.png), used as-is per spec -- no
  // procedural box, no resizing/recompositing of the source file itself.

  var STAGE_W = 340, STAGE_H = 330;
  // translate/scale read literally (x,y,w,h passed straight to
  // drawImage) put the box's rendered footprint mostly off the bottom
  // of the stage -- checked live via Chrome against the actual draft
  // theme render, not just computed. Retuned by eye from that live
  // look: box's own alpha bbox fills ~93%/91% of its source canvas (no
  // real padding to lean on), so BOX_BASE_W now stands in for "how wide
  // the box reads on stage" directly.
  //
  // BOX_SCALE halved (0.86 -> 0.43, "box tweak" pass) -- BOX_TX left
  // untouched and only BOX_TY moved, so the box's own near/bottom-left
  // corner (where it reads as "under Bolty's feet") stays anchored at
  // the same point while the box shrinks up-and-in from there, rather
  // than shrinking from an unrelated top-left origin and losing the
  // overlap. BOX_TY = old bottom edge (85+240=325) minus the new
  // height (300*0.43*(2196/2361)=120) = 205.
  var BOX_TX = 40, BOX_TY = 205, BOX_SCALE = 0.43;
  var BOX_BASE_W = 300;
  var BOX_NATURAL_W = 2361, BOX_NATURAL_H = 2196; // assets/bolty-box-cutout.png

  var CHAR_TX = 6, CHAR_TY = 72, CHAR_SCALE = 0.9;

  var boxImg = null;
  var boxReady = false;

  function initBoxImage() {
    var srcEl = document.querySelector('[data-bolty-box-source]');
    if (!srcEl || !srcEl.getAttribute('src')) { return; }
    boxImg = new Image();
    boxImg.onload = function () { boxReady = true; };
    boxImg.src = srcEl.getAttribute('src');
  }

  // Cast shadow: from Bolty's feet toward upper-right, 74 deg off
  // vertical (mostly rightward, slight rise -- the only angle reading
  // that lands the shadow's own tip on the box rather than sailing over
  // the character's own head), width tapering 34->28, ending in a
  // rotated ellipse (rx26 ry12) aligned to the travel direction.
  // Length shortened 130 -> 70 (box tweak pass, same 74deg angle) to
  // match the box's own halved footprint -- at 130 the tip would land
  // well past the now-smaller box's own edge.
  function drawShadow(ctx) {
    var feetX = CHAR_TX + CHAR_SCALE * CX;
    var feetY = CHAR_TY + CHAR_SCALE * BOTTOM_Y;
    var angle = 74 * Math.PI / 180;
    var dirX = Math.sin(angle), dirY = -Math.cos(angle);
    var perpX = Math.cos(angle), perpY = Math.sin(angle);
    var length = 70, baseW = 34, tipW = 28, rx = 26, ry = 12;

    var tipX = feetX + dirX * length, tipY = feetY + dirY * length;
    var b0x = feetX + perpX * (baseW / 2), b0y = feetY + perpY * (baseW / 2);
    var b1x = feetX - perpX * (baseW / 2), b1y = feetY - perpY * (baseW / 2);
    var t0x = tipX + perpX * (tipW / 2), t0y = tipY + perpY * (tipW / 2);
    var t1x = tipX - perpX * (tipW / 2), t1y = tipY - perpY * (tipW / 2);

    ctx.save();
    ctx.fillStyle = '#22303C';
    ctx.globalAlpha = 0.15;
    ctx.beginPath();
    ctx.moveTo(b0x, b0y);
    ctx.lineTo(t0x, t0y);
    ctx.lineTo(t1x, t1y);
    ctx.lineTo(b1x, b1y);
    ctx.closePath();
    ctx.fill();

    ctx.translate(tipX, tipY);
    ctx.rotate(Math.atan2(dirY, dirX));
    ctx.beginPath();
    ctx.ellipse(0, 0, rx, ry, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  // Lerps between two '#rrggbb' hex colors. Used only for the smoke
  // puffs' own fade-to-background softening (see SMOKE_FADE_COLOR) --
  // small and local rather than a general color-utils addition, since
  // nothing else on this canvas needs a color lerp.
  function lerpHexColor(hexA, hexB, t) {
    var a = parseInt(hexA.slice(1), 16), b = parseInt(hexB.slice(1), 16);
    var ar = (a >> 16) & 255, ag = (a >> 8) & 255, ab = a & 255;
    var br = (b >> 16) & 255, bg = (b >> 8) & 255, bb = b & 255;
    var r = Math.round(lerp(ar, br, t)), g = Math.round(lerp(ag, bg, t)), bl = Math.round(lerp(ab, bb, t));
    return 'rgb(' + r + ',' + g + ',' + bl + ')';
  }

  // Style configs for drawSmokePuffs -- ambient (lazy one-exhale idle/
  // vibe puff) vs cough (short sharp bursts on each convulsion beat).
  // ONE renderer, two data-driven configs -- see drawSmokePuffs' own
  // comment for why this must stay a single implementation.
  var SMOKE_STYLE_AMBIENT = {
    puffCount: SMOKE_PUFF_COUNT,
    staggerTicks: SMOKE_PUFF_STAGGER_TICKS,
    totalFrames: SMOKE_FRAME_COUNT,
    angleBaseDeg: SMOKE_ANGLE_BASE_DEG,
    angleStepDeg: SMOKE_ANGLE_STEP_DEG,
    startDist: SMOKE_PUFF_START_DIST,
    maxDist: SMOKE_PUFF_MAX_DIST,
    startRadius: SMOKE_PUFF_START_RADIUS,
    maxRadius: SMOKE_PUFF_MAX_RADIUS,
    peakAlpha: SMOKE_PEAK_ALPHA,
    fadeStartT: SMOKE_FADE_START_T,
    color: SMOKE_COLOR,
    fadeColor: SMOKE_FADE_COLOR
  };
  var SMOKE_STYLE_COUGH = {
    puffCount: COUGH_SMOKE_PUFF_COUNT,
    staggerTicks: COUGH_SMOKE_BURST_STAGGER_TICKS,
    totalFrames: COUGH_SMOKE_TOTAL_FRAMES,
    angleBaseDeg: COUGH_SMOKE_ANGLE_BASE_DEG,
    angleStepDeg: COUGH_SMOKE_ANGLE_STEP_DEG,
    startDist: COUGH_SMOKE_START_DIST,
    maxDist: COUGH_SMOKE_MAX_DIST,
    startRadius: COUGH_SMOKE_START_RADIUS,
    maxRadius: COUGH_SMOKE_MAX_RADIUS,
    peakAlpha: COUGH_SMOKE_PEAK_ALPHA,
    fadeStartT: COUGH_SMOKE_FADE_START_T,
    color: SMOKE_COLOR,
    fadeColor: SMOKE_FADE_COLOR
  };
  var SMOKE_STYLES = { ambient: SMOKE_STYLE_AMBIENT, cough: SMOKE_STYLE_COUGH };

  // Smoke puffs (animation 2, BLOWING SMOKE, and the cough-burst reuse
  // of it) -- drawn straight onto the raw stage canvas, same as
  // drawShadow above, so they run through the SAME dither pass as the
  // character instead of being composited after and looking pasted on.
  // Mouth position derived via the same CHAR_TX/CHAR_SCALE transform
  // drawShadow already uses to find Bolty's feet, applied to MOUTH_Y/CX
  // instead. The emission point sits at the SAME mouth corner the
  // puckered mouth override sits at (SMOKE_MOUTH_SIDE*SMOKE_MOUTH_OFFSET_X
  // off CX) for both styles, and puffs fan out from styleCfg.angleBaseDeg
  // biased toward that same side. Puff angle/releaseAt are a pure
  // function of (styleName, puff index) -- never randomized -- so the
  // composite cache (keyed by style+frameIndex, see getComposite) always
  // produces the identical raster for a given key; real per-frame
  // randomness would both look jittery at this frame rate and break the
  // cache's "same key -> same pixels" premise.
  //
  // DITHER DISCIPLINE: this runs through the exact same ditherCanvas
  // pass (same PIXEL_SIZE=2 block averaging, same ALPHA_THRESHOLD=128
  // binary cutoff, same DITHER_LEVELS=7 quantization) as the rest of the
  // character -- there is no separate/softer render path for smoke, by
  // construction (see buildRawStage, which draws this before the single
  // shared ditherCanvas call). A too-small puff radius produces almost
  // no surviving 2x2 blocks after that binary cutoff -- confirmed by an
  // isolated replica of ditherCanvas swept across radii (2026-08-09):
  // ~4 opaque blocks survive at r=2.7 (the OLD puff's birth radius),
  // ~44 at r=8.61 (the OLD puff's own max) -- both too sparse to read as
  // a cloud, just scattered dots the same light-grey family as Bolty's
  // own body. ~406 blocks survive at r=25, a legibly solid shape. The
  // new SMOKE_PUFF_MAX_RADIUS/COUGH_SMOKE_MAX_RADIUS constants above
  // were chosen against that same curve, not guessed.
  function drawSmokePuffs(ctx, frameIndex, styleName) {
    var style = SMOKE_STYLES[styleName] || SMOKE_STYLE_AMBIENT;
    var mouthX = CHAR_TX + CHAR_SCALE * (CX + SMOKE_MOUTH_SIDE * SMOKE_MOUTH_OFFSET_X);
    var mouthY = CHAR_TY + CHAR_SCALE * MOUTH_Y;

    ctx.save();
    for (var i = 0; i < style.puffCount; i++) {
      var releaseAt = style.staggerTicks * i;
      var age = frameIndex - releaseAt;
      var maxAge = style.totalFrames - releaseAt;
      if (age < 0 || maxAge <= 0) { continue; }
      var t = Math.min(1, age / maxAge);
      var angleDeg = style.angleBaseDeg + SMOKE_MOUTH_SIDE * style.angleStepDeg * (i + 1);
      var rad = angleDeg * Math.PI / 180;
      var dist = lerp(style.startDist, style.maxDist, t) * CHAR_SCALE;
      var px = mouthX + Math.cos(rad) * dist;
      var py = mouthY + Math.sin(rad) * dist;
      var r = lerp(style.startRadius, style.maxRadius, t) * CHAR_SCALE;
      // Peak alpha held well clear of ditherCanvas's binary
      // ALPHA_THRESHOLD=128 per-block cutoff for most of the puff's
      // life, then ramps down only in the final (1-fadeStartT) fraction
      // -- a smooth alpha fade genuinely isn't possible under a binary
      // per-block cutoff, so the "fade" reads as a quick pop-off near
      // the end instead, same as it would for anything else drawn on
      // this pipeline. Root-caused live 2026-08-08 (see git history) --
      // an earlier peak of 0.5 sat AT the threshold and was being
      // discarded to fully transparent almost every tick.
      ctx.globalAlpha = t < style.fadeStartT ? style.peakAlpha : lerp(style.peakAlpha, 0, (t - style.fadeStartT) / (1 - style.fadeStartT));
      // Color lerps toward SMOKE_FADE_COLOR over the same fade window --
      // "soften as it fades" under a hard-binary-alpha dither means
      // stepping through the palette's few discrete quantized levels
      // toward background, not a soft edge (which this pipeline cannot
      // produce at all).
      ctx.fillStyle = t < style.fadeStartT ? style.color : lerpHexColor(style.color, style.fadeColor, (t - style.fadeStartT) / (1 - style.fadeStartT));
      ctx.beginPath();
      ctx.ellipse(px, py, r, r * 0.85, 0, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  function drawCharacter(ctx, raster, extraRise, rotateDeg) {
    var cx = CHAR_TX + CHAR_SCALE * VB_W / 2;
    var cy = CHAR_TY + CHAR_SCALE * VB_H / 2 + (extraRise || 0);
    ctx.save();
    ctx.translate(cx, cy);
    if (rotateDeg) { ctx.rotate(rotateDeg * Math.PI / 180); }
    ctx.scale(CHAR_SCALE, CHAR_SCALE);
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(raster, -VB_W / 2, -VB_H / 2);
    ctx.restore();
  }

  // --- Floating hands: drawing (reaction pack 3) --------------------------
  // Deterministic hash, NOT Math.random() -- drawHands must be a pure
  // function of its inputs (same requirement drawSmoke's own comment
  // states for frameIndex) so the composite cache's "same key -> same
  // pixels" premise holds for hand frames too.
  function pseudoRandom(n) {
    var x = Math.sin(n * 12.9898) * 43758.5453;
    return x - Math.floor(x);
  }

  // One rounded end-cap "sausage" (finger/thumb) from (x1,y1) to (x2,y2),
  // filled+stroked as a single continuous path -- standard two-arc/two-
  // line stadium shape, not two circles plus a rect (which would leave
  // visible seams once stroked).
  function drawCapsule(ctx, x1, y1, x2, y2, r) {
    var angle = Math.atan2(y2 - y1, x2 - x1);
    var perp = angle + Math.PI / 2;
    var ox = Math.cos(perp) * r, oy = Math.sin(perp) * r;
    ctx.beginPath();
    ctx.moveTo(x1 + ox, y1 + oy);
    ctx.lineTo(x2 + ox, y2 + oy);
    ctx.arc(x2, y2, r, perp, perp - Math.PI, true);
    ctx.lineTo(x1 - ox, y1 - oy);
    ctx.arc(x1, y1, r, perp - Math.PI, perp, true);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  }

  // Cartoon mitten: a rounded palm, three fingers along its top edge
  // (middle longest), one thumb on the INNER side (toward CX, so a pair
  // of hands reads as "facing" each other, like a relaxed at-the-sides
  // pose) -- side is -1 (left) / +1 (right), same convention buildEye
  // already uses. Drawn in CHARACTER-LOCAL units centered on its own
  // local (0,0); the caller applies CHAR_SCALE + position + rotation via
  // ctx transform, same division of responsibility drawCharacter uses
  // for the SVG raster. Flat white fill + solid black stroke, no
  // gradient -- deliberately: a gradient needs to survive the shared
  // dither pass's palette quantization to still read as smooth, but flat
  // color has nothing to quantize away, so it comes out exactly as
  // clean/chunky as the character's own flat-color parts (pupils, mouth
  // fill) rather than risking looking "pasted on" next to the dithered
  // chrome (the spec's own stated main risk for this feature).
  function drawGloveShape(ctx, side) {
    ctx.fillStyle = HAND_FILL;
    ctx.strokeStyle = HAND_STROKE;
    ctx.lineWidth = HAND_STROKE_W;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';

    ctx.beginPath();
    ctx.ellipse(0, 0, HAND_PALM_RX, HAND_PALM_RY, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    var fingerXs = [-HAND_PALM_RX * 0.55, 0, HAND_PALM_RX * 0.55];
    var fingerLens = [HAND_FINGER_LEN * 0.85, HAND_FINGER_LEN, HAND_FINGER_LEN * 0.85];
    for (var i = 0; i < 3; i++) {
      var fx = fingerXs[i], fy0 = -HAND_PALM_RY * 0.5;
      drawCapsule(ctx, fx, fy0, fx, fy0 - fingerLens[i], HAND_FINGER_R);
    }

    var innerDir = -side; // thumb points toward CX, not away from it
    var thumbBaseX = innerDir * HAND_PALM_RX * 0.7, thumbBaseY = HAND_PALM_RY * 0.15;
    var thumbTipX = innerDir * (HAND_PALM_RX * 0.7 + HAND_THUMB_LEN * 0.7);
    var thumbTipY = thumbBaseY - HAND_THUMB_LEN * 0.65;
    drawCapsule(ctx, thumbBaseX, thumbBaseY, thumbTipX, thumbTipY, HAND_THUMB_R);
  }

  // Materialize (dither-in/out): an alpha fade can't read smoothly under
  // ditherCanvas's own binary per-block alpha cutoff (see drawSmoke's
  // fix comment above for the full reasoning), and a slide/scale tween
  // would read as a modern UI transition, not "90s." Instead, a
  // materializing hand is drawn with a random subset of a coarse cell
  // grid over its own bounding box punched out -- reads as scattering
  // in/out of static, the same visual language this whole file's CRT/
  // dither aesthetic already speaks, and it's quick (materialize jumps
  // from ~0.4 to 1 across exactly one tick in the frame builders below,
  // not a multi-frame tween).
  var HAND_BOX = 72; // raw-stage px, offscreen mask size -- generous headroom for the glove's full span at any rotation (max reach from origin is ~17.3 local units * CHAR_SCALE =~ 15.6px)

  // Shared by both the glove mask and the juggled-nut mask below --
  // draws drawFn into a size x size offscreen canvas, then (materialize
  // < 1 only) punches out a deterministic random subset of a coarse cell
  // grid over it. Generic so JUGGLE's props reuse the exact same
  // dither-in/out language the hands themselves use, not a second
  // effect invented for objects.
  function buildScatterMask(size, materialize, seed, drawFn) {
    var off = document.createElement('canvas');
    off.width = size;
    off.height = size;
    var octx = off.getContext('2d');
    octx.imageSmoothingEnabled = false;
    drawFn(octx);

    var mat = materialize == null ? 1 : materialize;
    if (mat < 1) {
      var cells = Math.ceil(size / HAND_MATERIALIZE_CELL);
      for (var cy = 0; cy < cells; cy++) {
        for (var cx = 0; cx < cells; cx++) {
          var idx = cy * cells + cx;
          if (pseudoRandom(seed * 97.13 + idx * 13.7) >= mat) {
            octx.clearRect(cx * HAND_MATERIALIZE_CELL, cy * HAND_MATERIALIZE_CELL, HAND_MATERIALIZE_CELL, HAND_MATERIALIZE_CELL);
          }
        }
      }
    }
    return off;
  }

  function buildGloveMask(side, rotateDeg, materialize, seed) {
    return buildScatterMask(HAND_BOX, materialize, seed + (side < 0 ? 0 : 0.5), function (octx) {
      octx.save();
      octx.translate(HAND_BOX / 2, HAND_BOX / 2);
      if (rotateDeg) { octx.rotate(rotateDeg * Math.PI / 180); }
      octx.scale(CHAR_SCALE, CHAR_SCALE);
      drawGloveShape(octx, side);
      octx.restore();
    });
  }

  // JUGGLE's three props: hex nuts, matching this shop's own hardware --
  // fill color (#9ba0a4) is pulled directly from buildDefs' own chrome-
  // shank gradient stop, not a new palette entry, so a juggled nut reads
  // as made of the same metal as the rest of him.
  var NUT_BOX = 30, NUT_R = 6.5;
  var NUT_FILL = '#9ba0a4', NUT_HOLE = '#4a4d50';

  function drawNutShape(ctx) {
    ctx.fillStyle = NUT_FILL;
    ctx.strokeStyle = HAND_STROKE;
    ctx.lineWidth = 1.4;
    ctx.lineJoin = 'round';
    ctx.beginPath();
    for (var i = 0; i < 6; i++) {
      var a = i * Math.PI / 3;
      var px = Math.cos(a) * NUT_R, py = Math.sin(a) * NUT_R;
      if (i === 0) { ctx.moveTo(px, py); } else { ctx.lineTo(px, py); }
    }
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(0, 0, NUT_R * 0.42, 0, Math.PI * 2);
    ctx.fillStyle = NUT_HOLE;
    ctx.fill();
  }

  function buildNutMask(rotateDeg, materialize, seed) {
    return buildScatterMask(NUT_BOX, materialize, seed, function (octx) {
      octx.save();
      octx.translate(NUT_BOX / 2, NUT_BOX / 2);
      if (rotateDeg) { octx.rotate(rotateDeg * Math.PI / 180); }
      octx.scale(CHAR_SCALE, CHAR_SCALE);
      drawNutShape(octx);
      octx.restore();
    });
  }

  // Drawn straight onto the raw stage, same technique as drawShadow/
  // drawSmoke above -- runs through the SAME ditherCanvas pass as
  // everything else on the stage, so it's never a separately-composited
  // overlay that could look pasted on. handFrame shape:
  // { left: {x,y,rot,materialize} | null, right: {...} | null,
  //   objects: [{x,y,rot,materialize}, ...] | undefined, seed }
  // -- x/y are CHARACTER-LOCAL units (same space as CX/MOUTH_Y/
  // HAND_REST_Y), converted to stage px here the same way drawShadow/
  // drawSmoke convert their own local coordinates. rot in degrees.
  // materialize 0..1, 1 = fully solid (omit the field, or pass null, for
  // fully solid). Either hand may be absent -- SCRATCH/NOSE_PICK only
  // ever show one. objects is JUGGLE-only (the three nuts); absent for
  // every other hand-bearing anim. seed is the animation's own frame
  // index, threaded through so buildScatterMask's scatter pattern is
  // deterministic per frame (see its own comment) rather than re-
  // randomized every repaint of a cached composite.
  function drawHands(ctx, handFrame) {
    if (!handFrame) { return; }
    var seed = handFrame.seed || 0;
    ['left', 'right'].forEach(function (key) {
      var h = handFrame[key];
      if (!h) { return; }
      var side = key === 'left' ? -1 : 1;
      var stageX = CHAR_TX + CHAR_SCALE * h.x;
      var stageY = CHAR_TY + CHAR_SCALE * h.y;
      var mask = buildGloveMask(side, h.rot || 0, h.materialize, seed + (side < 0 ? 0 : 0.5));
      ctx.drawImage(mask, stageX - HAND_BOX / 2, stageY - HAND_BOX / 2);
    });
    if (handFrame.objects) {
      handFrame.objects.forEach(function (o, i) {
        var stageX = CHAR_TX + CHAR_SCALE * o.x;
        var stageY = CHAR_TY + CHAR_SCALE * o.y;
        var mask = buildNutMask(o.rot || 0, o.materialize, seed + 1 + i * 0.1);
        ctx.drawImage(mask, stageX - NUT_BOX / 2, stageY - NUT_BOX / 2);
      });
    }
  }

  // Raw (undithered) 340x330 stage: box, then shadow (steady-state only
  // -- doesn't make sense mid-flight during entrance), then character at
  // its normal position plus whatever bob/entrance offset applies.
  function buildRawStage(raster, opts) {
    opts = opts || {};
    var stage = document.createElement('canvas');
    stage.width = STAGE_W;
    stage.height = STAGE_H;
    var ctx = stage.getContext('2d');
    ctx.imageSmoothingEnabled = false;

    if (boxReady) {
      var ratio = BOX_NATURAL_H / BOX_NATURAL_W;
      var w = BOX_BASE_W * BOX_SCALE;
      var h = BOX_BASE_W * ratio * BOX_SCALE;
      ctx.drawImage(boxImg, BOX_TX, BOX_TY, w, h);
    }

    if (opts.showShadow !== false) { drawShadow(ctx); }

    if (raster) {
      var bobPx = (opts.bob || 0) * CHAR_SCALE;
      drawCharacter(ctx, raster, bobPx + (opts.riseOffset || 0), opts.rotateDeg || 0);
    }

    // Hands drawn above the character (z-order: box -> shadow ->
    // character -> hands -> smoke), so they can pass in front of the
    // body per spec. Mutually exclusive with smokeFrame in practice
    // (only one activeAnim slot exists), but the order here is still the
    // documented one, not accidental.
    if (opts.handFrame) { drawHands(ctx, opts.handFrame); }

    if (opts.smokeFrame != null) { drawSmokePuffs(ctx, opts.smokeFrame, opts.smokeStyle); }

    return stage;
  }

  // --- Dither: Bayer 4x4 ordered dither, block-quantized -----------------
  // "pixel size 2" == each 2x2 block of source pixels is averaged and
  // written back as one flat block (a real resolution-halving mosaic
  // step, not just a per-pixel threshold), using the BLOCK's own (bx,by)
  // for the Bayer lookup so the dither pattern is coarse/chunky to match.
  // "palette levels 7" replaces the prior build's 4.

  var BAYER4 = [
    [0, 8, 2, 10],
    [12, 4, 14, 6],
    [3, 11, 1, 9],
    [15, 7, 13, 5]
  ];
  var DITHER_LEVELS = 7;
  var PIXEL_SIZE = 2;
  var DITHER_STRENGTH = 0.9;
  var ALPHA_THRESHOLD = 128;

  function quantizeChannel(avg, step, threshold) {
    var v = avg / 255 * (DITHER_LEVELS - 1);
    var adjusted = v + threshold * DITHER_STRENGTH;
    var q = Math.max(0, Math.min(DITHER_LEVELS - 1, Math.round(adjusted)));
    return Math.round(q * step);
  }

  function ditherCanvas(srcCanvas) {
    var w = srcCanvas.width, h = srcCanvas.height;
    var out = document.createElement('canvas');
    out.width = w;
    out.height = h;
    var srcCtx = srcCanvas.getContext('2d');
    var outCtx = out.getContext('2d');
    outCtx.imageSmoothingEnabled = false;

    var imgData;
    try {
      imgData = srcCtx.getImageData(0, 0, w, h);
    } catch (e) {
      // Cross-origin canvas tainting or similar -- fall back to the
      // un-dithered raster rather than break rendering entirely.
      outCtx.drawImage(srcCanvas, 0, 0);
      return out;
    }
    var data = imgData.data;
    var step = 255 / (DITHER_LEVELS - 1);
    var blocksX = Math.ceil(w / PIXEL_SIZE);
    var blocksY = Math.ceil(h / PIXEL_SIZE);

    for (var by = 0; by < blocksY; by++) {
      for (var bx = 0; bx < blocksX; bx++) {
        var x0 = bx * PIXEL_SIZE, y0 = by * PIXEL_SIZE;
        var x1 = Math.min(x0 + PIXEL_SIZE, w), y1 = Math.min(y0 + PIXEL_SIZE, h);
        var rSum = 0, gSum = 0, bSum = 0, aSum = 0, count = 0;
        for (var yy = y0; yy < y1; yy++) {
          for (var xx = x0; xx < x1; xx++) {
            var idx = (yy * w + xx) * 4;
            rSum += data[idx]; gSum += data[idx + 1]; bSum += data[idx + 2]; aSum += data[idx + 3];
            count++;
          }
        }
        var aAvg = aSum / count;
        var outA = aAvg < ALPHA_THRESHOLD ? 0 : 255;
        var outR = 0, outG = 0, outB = 0;
        if (outA) {
          var threshold = (BAYER4[by % 4][bx % 4] / 16) - 0.5;
          outR = quantizeChannel(rSum / count, step, threshold);
          outG = quantizeChannel(gSum / count, step, threshold);
          outB = quantizeChannel(bSum / count, step, threshold);
        }
        for (var yy2 = y0; yy2 < y1; yy2++) {
          for (var xx2 = x0; xx2 < x1; xx2++) {
            var idx2 = (yy2 * w + xx2) * 4;
            data[idx2] = outR; data[idx2 + 1] = outG; data[idx2 + 2] = outB; data[idx2 + 3] = outA;
          }
        }
      }
    }

    outCtx.putImageData(imgData, 0, 0);
    return out;
  }

  // --- Minimize button face (balloon-anchoring pass) -----------------------
  // A small Win95-style bevel button (raised face, light top/left edge,
  // dark bottom/right edge, black frame, a minimize dash near the
  // bottom) run through the SAME ditherCanvas pass the character itself
  // goes through -- "drawn in the same 90s pixel language as the
  // character... dithered consistently," not a modern flat circle. Built
  // once (cached as a data URL, not a live canvas -- this button never
  // animates) and set as a CSS background-image on .bolty-minimize.
  var minimizeBtnDataUrl = null;

  function buildMinimizeButtonDataUrl() {
    if (minimizeBtnDataUrl) { return minimizeBtnDataUrl; }
    var size = 24;
    var raw = document.createElement('canvas');
    raw.width = size;
    raw.height = size;
    var ctx = raw.getContext('2d');

    ctx.fillStyle = '#c0c0c0';
    ctx.fillRect(0, 0, size, size);
    ctx.fillStyle = '#ffffff'; // raised bevel -- light top/left
    ctx.fillRect(0, 0, size, 2);
    ctx.fillRect(0, 0, 2, size);
    ctx.fillStyle = '#404040'; // raised bevel -- dark bottom/right
    ctx.fillRect(0, size - 2, size, 2);
    ctx.fillRect(size - 2, 0, 2, size);
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 1;
    ctx.strokeRect(0.5, 0.5, size - 1, size - 1);
    ctx.fillStyle = '#000000'; // minimize glyph: a short bar, lower third (classic Win95 position)
    var barW = size * 0.5;
    ctx.fillRect((size - barW) / 2, size * 0.68, barW, 3);

    var dithered = ditherCanvas(raw);
    minimizeBtnDataUrl = dithered.toDataURL();
    return minimizeBtnDataUrl;
  }

  // Final dithered composite, cached per (charRasterKey, bob) so steady
  // -state animation doesn't re-dither a 340x330 canvas every 110ms tick
  // -- only the first time each distinct variant is encountered.
  var compositeCache = {};

  function getComposite(emotion, tick, blinking, bob, trackOffset, animOverride, smokeFrame, smokeStyle, handFrame, dollarEyes, cb) {
    getCharRaster(emotion, tick, blinking, trackOffset, animOverride, dollarEyes, function (raster) {
      if (!raster) { cb(null); return; }
      // handFrame objects come from this file's own small, precomputed
      // per-anim frame arrays (bounded set, same as animOverride --
      // charVariantKey's own comment) -- JSON.stringify is safe/cheap
      // here specifically because it's only ever computed while a hand-
      // bearing anim is actually playing (juggle/scratch/nosePick), not
      // on every ordinary idle tick.
      // smokeStyle is included in the key alongside smokeFrame -- ambient
      // and cough styles reuse overlapping numeric frame indices (both
      // start at 0) for entirely different puff configs, so the index
      // alone is not a unique cache key.
      var key = charVariantKey(emotion, tick, blinking, trackOffset, animOverride, dollarEyes) + ':' + bob +
        (smokeFrame != null ? ':smoke' + smokeStyle + smokeFrame : '') +
        (handFrame ? ':hand' + JSON.stringify(handFrame) : '');
      if (compositeCache[key]) { cb(compositeCache[key]); return; }
      var raw = buildRawStage(raster, { bob: bob, showShadow: true, smokeFrame: smokeFrame, smokeStyle: smokeStyle, handFrame: handFrame });
      var dithered = ditherCanvas(raw);
      compositeCache[key] = dithered;
      cb(dithered);
    });
  }

  // --- Animation loop (stepped, ~9fps / 110ms tick, never eased) --------

  var IDLE_BOB = [0, 0, -2, -4, -4, -2, 0, 1, 1, 0];
  var TICK_MS = 110;
  var BLINK_EVERY_MS = 6500;
  var BLINK_TICKS = 2;
  var REST_AFTER_MS = 90000;  // -> sleep emotion
  var BORED_AFTER_MS = 40000; // -> bored emotion (sleep enters FROM bored, same idle clock)
  var VIBE_BOB = [0, -3, 0, -3]; // fast two-step, ~115bpm-ish at this tick rate
  var TALK_GRACE_MS = 1500;   // grin lingers this long after the balloon closes
  var STOKED_DURATION_MS = 3000;
  var STOKED_BOB = [0, -6, -2, 0, 0, -6, -2, 0]; // two quick bobs, then flat for the rest of the 3s

  var canvasEl = null;
  var displayCtx = null;
  var animTimer = null;
  var tickCount = 0;
  var lastActivityAt = Date.now();
  var isVibing = false;
  var isResting = false; // sleep
  var isBored = false;
  var isStoked = false;
  var stokedUntil = 0;
  var stokedStartTick = -1;
  var isBalloonVisible = false;
  var talkGraceUntil = 0;
  var blinkUntilTick = -1;

  // thinking > talk > stoked > vibe > bored/sleep > rest. Blink is
  // orthogonal (see buildCharacterSVG) and never appears here. Thinking
  // ranks above talk (added 2026-08-01, ask-Bolty) because the balloon
  // IS open the entire time an ask fetch is in flight -- isBalloonVisible
  // would otherwise win and show the ordinary talk grin instead of the
  // thinking look.
  function currentEmotion() {
    var now = Date.now();
    if (isAskThinking) { return EMOTION_THINKING; }
    if (isBalloonVisible || now < talkGraceUntil) { return EMOTION_TALK; }
    if (isStoked && now < stokedUntil) { return EMOTION_STOKED; }
    if (isVibing) { return EMOTION_VIBE; }
    if (isResting) { return EMOTION_SLEEP; }
    if (isBored) { return EMOTION_BORED; }
    return EMOTION_REST;
  }

  function currentBob(emotion) {
    if (reduceMotion) { return 0; }
    if (emotion === EMOTION_VIBE) { return VIBE_BOB[tickCount % VIBE_BOB.length]; }
    if (emotion === EMOTION_STOKED) {
      var idx = tickCount - stokedStartTick;
      return (idx >= 0 && idx < STOKED_BOB.length) ? STOKED_BOB[idx] : 0;
    }
    return IDLE_BOB[tickCount % IDLE_BOB.length];
  }

  // bored/sleep never trigger while the balloon is open or the radio is
  // playing -- both idle clocks just hold at their current value in
  // that case (activity's own listener already resets them on any real
  // interaction; this only prevents them from ADVANCING while otherwise
  // idle-looking but actually busy).
  // Dialogue side-effects on these transitions (a "waking up"/"bored
  // mutter" line) were dropped this pass -- the new line pool has no
  // dedicated bucket for either, and adding one back would mean an
  // extra speaking opportunity outside the "max 1 spontaneous per
  // ~4min" cadence the spec explicitly keeps unchanged. The emotion
  // state itself (isResting/isBored, driving the visual sleep/bored
  // look) is untouched.
  function updateIdleState() {
    if (isBalloonVisible || isVibing) { return; }
    // Nodding off is already mid-sequence -- let it run (getActiveAnimFrame
    // advances it every tick on its own) rather than re-triggering it from
    // scratch on every single tick idleMs stays past REST_AFTER_MS.
    if (activeAnim && activeAnim.name === 'noddingOff') { return; }
    var idleMs = Date.now() - lastActivityAt;
    if (!isResting && idleMs > REST_AFTER_MS) {
      startNoddingOff(); // the bridge into sleep -- replaces the old instant switch entirely, see its own comment
    } else if (!isResting && !isBored && idleMs > BORED_AFTER_MS) {
      isBored = true;
      maybeYawnOnBoredEntry();
    }
  }

  // --- Hit area (real visible pixels, not the padded stage box) ----------
  // .bolty-hit-area (snippets/bolty.liquid) starts at 0x0 and gets sized/
  // positioned here, once, from the ACTUAL alpha channel of the first
  // fully-composited frame -- box+character+shadow together, exactly as
  // painted -- rather than a hand-measured guess. Sits inside the same
  // (scaled) .bolty-scene as the canvas, so it tracks --eva-bolty-scale
  // automatically with no extra math. Waits for boxReady so the very
  // first sample isn't missing the box's own footprint (buildRawStage
  // skips drawing the box entirely until its Image has loaded).
  var hitAreaComputed = false;

  function computeAlphaBBox(canvas) {
    var w = canvas.width, h = canvas.height;
    var data;
    try { data = canvas.getContext('2d').getImageData(0, 0, w, h).data; } catch (e) { return null; }
    var minX = w, minY = h, maxX = -1, maxY = -1;
    for (var y = 0; y < h; y++) {
      for (var x = 0; x < w; x++) {
        if (data[(y * w + x) * 4 + 3] > 0) {
          if (x < minX) { minX = x; }
          if (x > maxX) { maxX = x; }
          if (y < minY) { minY = y; }
          if (y > maxY) { maxY = y; }
        }
      }
    }
    if (maxX < minX || maxY < minY) { return null; } // fully transparent -- nothing to measure yet
    return { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
  }

  function maybeApplyHitAreaBBox(frameCanvas) {
    if (hitAreaComputed || !hitAreaEl || !boxReady) { return; }
    var bbox = computeAlphaBBox(frameCanvas);
    if (!bbox) { return; } // try again next tick
    hitAreaComputed = true;
    hitAreaEl.style.left = bbox.x + 'px';
    hitAreaEl.style.top = bbox.y + 'px';
    hitAreaEl.style.width = bbox.w + 'px';
    hitAreaEl.style.height = bbox.h + 'px';
    // A restored saved position (restorePositionIfAny, called from
    // init() before this measurement ever lands) was clamped against the
    // rough padded-box fallback above -- now that the real visible
    // bounds exist, re-clamp for real so a position saved near an edge
    // doesn't stay pinned to the earlier, more conservative fallback.
    if (userMoved) {
      var reclamped = clampToViewport(posX, posY);
      posX = reclamped.x;
      posY = reclamped.y;
      applyPosition(posX, posY);
    }
  }

  function drawFrame() {
    if (!canvasEl || !displayCtx) { return; }

    updateIdleState();
    if (isStoked && Date.now() >= stokedUntil) { isStoked = false; }
    advanceAskBoltyTick();

    var emotion = currentEmotion();
    // Advances/clears activeAnim as a side effect (see its own comment) --
    // must run before anything below that reads activeAnim this tick.
    var animFrame = getActiveAnimFrame();
    var isBlinking = !activeAnim && tickCount <= blinkUntilTick; // an animation already fully owns the lids for its duration -- blink is suppressed rather than fought with
    var bob = animFrame ? animFrame.bob : currentBob(emotion);
    var dollarEyesNow = isDollarEyesActive();
    // spying scripts its own pupil offset via animOverride, and dollar
    // eyes replace the pupils outright -- neither should fight live
    // cursor tracking ("while dollar eyes are active, cursor eye-tracking
    // is suppressed").
    var trackOffset = (activeAnim || dollarEyesNow) ? null : computeEyeTrackOffset(emotion);
    var animOverride = animFrame ? animFrame.override : null;
    var smokeFrame = animFrame ? animFrame.smokeFrame : null;
    var smokeStyle = animFrame ? animFrame.smokeStyle : null;
    // hands live directly on the frame object (animOverride.hands), same
    // way tonguePos/mouthOffsetX already do -- each hand-bearing anim's
    // own buildXFrames() bakes a seed onto it at build time, so no extra
    // tick bookkeeping is needed here (see drawHands' own comment on why
    // that seed must be deterministic).
    var handFrame = previewHandsOn ? HAND_PREVIEW_FRAME : (animOverride ? animOverride.hands : null);

    getComposite(emotion, tickCount, isBlinking, bob, trackOffset, animOverride, smokeFrame, smokeStyle, handFrame, dollarEyesNow, function (frameCanvas) {
      if (!frameCanvas) { return; }
      displayCtx.clearRect(0, 0, canvasEl.width, canvasEl.height);
      displayCtx.imageSmoothingEnabled = false;
      displayCtx.drawImage(frameCanvas, 0, 0);
      maybeApplyHitAreaBBox(frameCanvas);
    });

    if (!activeAnim && tickCount > blinkUntilTick && Math.random() < (TICK_MS / BLINK_EVERY_MS)) {
      blinkUntilTick = tickCount + BLINK_TICKS;
    }

    // Ambient triggers -- never fire while one's already playing (each
    // checks canStartAnim itself, but gating here too avoids rolling
    // dice every tick for nothing).
    if (!activeAnim) {
      maybeTriggerRandomCough(emotion);
      maybeTriggerRandomLick(emotion);
      maybeTriggerSmoke(emotion);
      maybeTriggerSpyingLongSession(emotion);
    }

    tickCount++;
  }

  function startAnimLoop() {
    if (animTimer || reduceMotion) {
      if (reduceMotion) { drawFrame(); } // draw one static frame, no loop
      return;
    }
    animTimer = setInterval(drawFrame, TICK_MS);
  }

  function stopAnimLoop() {
    if (animTimer) { clearInterval(animTimer); animTimer = null; }
  }

  function noteActivity() {
    // Captured before isResting is reset below -- true only on the exact
    // tick a COMPLETED sleep ends (isResting only ever becomes true once
    // startNoddingOff's onComplete has already fired, well after its own
    // activeAnim is done and cleared). Waking mid-nod-off (the cancel
    // just below) never sets this, so it can't double-fire the stretch.
    var wasResting = isResting;
    lastActivityAt = Date.now();
    isResting = false;
    isBored = false;
    // "Any visitor activity during it snaps him back to rest" -- nodding
    // off is the only one of the original five animations an interaction
    // cancels outright (the others are short enough, well under 2s, that
    // letting them finish regardless of activity is fine; nodding off is
    // the one that visually GATES a real state transition, so it has to
    // be interruptible). Clearing activeAnim here means getActiveAnimFrame
    // (below) falls through to normal rendering next tick, and since
    // isResting/isBored are already reset above, currentEmotion() lands
    // on plain REST immediately, not back on bored. Per spec: "waking
    // from nodding-off mid-sequence still snaps instantly" -- this branch
    // is exactly that snap, unchanged from before this pass.
    if (activeAnim && activeAnim.name === 'noddingOff') { activeAnim = null; }
    // Reaction pack 2: waking from a COMPLETED sleep plays the (now
    // stretch-extended) yawn as the wake-up transition instead of
    // snapping straight to rest. playYawn's own canStartAnim gate
    // (reduceMotion/activeAnim/visible/dragging) still applies -- under
    // reduced motion this simply no-ops and he lands on plain rest, per
    // spec ("everything respects prefers-reduced-motion by falling back
    // to rest").
    if (wasResting) { playYawn(false); }
  }

  // --- Animation triggers (five one-shot animations) ----------------------
  // activeAnim holds the currently-playing animation's own frame array
  // and start tick, or null when nothing's playing. Only one plays at a
  // time -- canStartAnim refuses to start a new one while activeAnim is
  // already set, same as it refuses under prefers-reduced-motion or
  // while hidden/dragging.
  var activeAnim = null; // { name, startTick, frames, smokeStyle, onComplete }

  // Dev-only static preview of the neutral hand position (EvaBoltyDebug.
  // previewHands below) -- NOT part of the anim system, doesn't consume
  // the activeAnim slot, purely a style-QA aid so the hands' own look can
  // be checked at rest before any animation using them exists. Never
  // true outside ?boltydev=1, and drawFrame checks it ahead of
  // animOverride.hands so it overrides whatever (if anything) would
  // otherwise be showing.
  var previewHandsOn = false;
  var HAND_PREVIEW_FRAME = {
    left: { x: CX - HAND_REST_X_OFFSET, y: HAND_REST_Y, rot: 0 },
    right: { x: CX + HAND_REST_X_OFFSET, y: HAND_REST_Y, rot: 0 },
    seed: 1
  };

  // allowDuringTalk: line-paired animations (playLineAnim, below) are
  // meant to play ALONGSIDE the balloon that's pairing them, so they
  // pass true here; every ambient/ombient-adjacent trigger (random
  // cough, idle smoke, spying) passes false so they never fight the
  // talk emotion's own grin while a balloon is already open.
  function canStartAnim(allowDuringTalk) {
    if (reduceMotion || activeAnim || !isVisible() || dragging) { return false; }
    if (!allowDuringTalk && isBalloonVisible) { return false; }
    return true;
  }

  function logAnim(name) {
    if (isBoltyDevMode()) { console.debug('[bolty] anim=' + name); }
  }

  // smokeStyle: null (no smoke), or a key into SMOKE_STYLES ('ambient',
  // 'cough') selecting which drawSmokePuffs config drives this anim's
  // frame index.
  function startAnim(name, frames, smokeStyle, onComplete) {
    activeAnim = { name: name, startTick: tickCount, frames: frames, smokeStyle: smokeStyle || null, onComplete: onComplete || null };
    logAnim(name);
  }

  // Called once per tick from drawFrame. Returns { bob, override,
  // smokeFrame, smokeStyle } for the current tick of whatever's playing,
  // or null if nothing is (including the tick an animation just finished
  // on -- that tick falls straight through to normal rendering, no gap).
  function getActiveAnimFrame() {
    if (!activeAnim) { return null; }
    var idx = tickCount - activeAnim.startTick;
    if (idx < 0) { return null; }
    if (idx >= activeAnim.frames.length) {
      var onComplete = activeAnim.onComplete;
      activeAnim = null;
      if (onComplete) { onComplete(); }
      return null;
    }
    var frame = activeAnim.frames[idx];
    return { bob: frame.bob || 0, override: frame, smokeFrame: activeAnim.smokeStyle ? idx : null, smokeStyle: activeAnim.smokeStyle };
  }

  function playCough(allowDuringTalk) {
    if (!canStartAnim(allowDuringTalk)) { return; }
    startAnim('cough', COUGH_PLAY_FRAMES, 'cough');
  }

  function playSmoke(allowDuringTalk) {
    if (!canStartAnim(allowDuringTalk)) { return; }
    startAnim('smoke', SMOKE_ANIM_FRAMES, 'ambient');
  }

  function playYawn(allowDuringTalk) {
    if (!canStartAnim(allowDuringTalk)) { return; }
    startAnim('yawn', YAWN_ANIM_FRAMES, false);
  }

  function playSpying(allowDuringTalk) {
    if (!canStartAnim(allowDuringTalk)) { return; }
    startAnim('spying', SPYING_ANIM_FRAMES, false);
  }

  function playSurprised(allowDuringTalk) {
    if (!canStartAnim(allowDuringTalk)) { return; }
    startAnim('surprised', SURPRISED_ANIM_FRAMES, false);
  }

  function playLick(allowDuringTalk) {
    if (!canStartAnim(allowDuringTalk)) { return; }
    startAnim('lick', LICK_ANIM_FRAMES, false);
  }

  // Reaction pack 3 -- the three hand-bearing animations. Same
  // canStartAnim/startAnim shape as every animation above (so reduced-
  // motion/single-slot/visible/dragging gating is free, no special-
  // casing needed), just pointed at the new frame arrays.
  function playJuggle(allowDuringTalk) {
    if (!canStartAnim(allowDuringTalk)) { return; }
    startAnim('juggle', JUGGLE_ANIM_FRAMES, false);
  }

  function playScratch(allowDuringTalk) {
    if (!canStartAnim(allowDuringTalk)) { return; }
    startAnim('scratch', SCRATCH_ANIM_FRAMES, false);
  }

  function playNosePick(allowDuringTalk) {
    if (!canStartAnim(allowDuringTalk)) { return; }
    startAnim('nosePick', NOSEPICK_ANIM_FRAMES, false);
  }

  // Nodding off replaces the old instant bored->sleep switch entirely
  // (called from updateIdleState, further down, at the same REST_AFTER_MS
  // threshold the instant switch used to fire at). If it can't play right
  // now for any reason -- reduced motion, or blocked the same way any
  // other animation can be blocked -- falls back to the old instant
  // switch so sleep still reliably happens, just without the bridge.
  function startNoddingOff() {
    if (canStartAnim(false)) {
      startAnim('noddingOff', NODDING_OFF_FRAMES, false, function () { isResting = true; });
    } else {
      isResting = true;
    }
  }

  // Reaction pack 2's "nod" -- the SAME frame data as the structural
  // bored->sleep bridge above, but played as a plain cosmetic one-shot
  // (no onComplete, never touches isResting) so a line can pair with it
  // ("anim":"nod") without accidentally forcing sleep as a side effect.
  // startNoddingOff (the real idle-triggered bridge) is untouched.
  function playNod(allowDuringTalk) {
    if (!canStartAnim(allowDuringTalk)) { return; }
    startAnim('nod', NODDING_OFF_FRAMES, false);
  }

  // Dollar eyes -- NOT a startAnim/activeAnim animation (see this
  // constant's own top-of-file comment): a simple wall-clock overlay
  // window, same shape as stokedUntil, checked directly in drawFrame and
  // threaded into buildCharacterSVG/buildEye as `dollarEyes`. Gated on
  // reduceMotion/isVisible here since it bypasses canStartAnim entirely
  // (there's no activeAnim slot to gate it via).
  var dollarEyesUntil = 0;

  function isDollarEyesActive() { return Date.now() < dollarEyesUntil; }

  function playDollarEyes() {
    if (reduceMotion || !isVisible()) { return; }
    dollarEyesUntil = Date.now() + BOLTY_DOLLAR_EYES_DURATION_MS;
    logAnim('dollar');
  }

  // Line-paired animations: assets/bolty-lines.json entries may carry an
  // optional "anim" key ({ "t": "...", "src": "per", "anim": "cough" }).
  // speak() (voice-lines section, further down) calls this after showing
  // the balloon; absence of the key, or an unrecognized name, is simply
  // never in this map and so never errors -- per spec, absence must
  // never error. "noddingOff" (the structural bored->sleep bridge) is
  // deliberately still not included here -- "nod" (above) is its line-
  // pairable cosmetic-only twin.
  var LINE_ANIM_PLAYERS = {
    cough: playCough, smoke: playSmoke, yawn: playYawn, spying: playSpying,
    nod: playNod, surprised: playSurprised, lick: playLick, dollar: playDollarEyes,
    juggle: playJuggle, scratch: playScratch, nosePick: playNosePick
  };

  function playLineAnim(name) {
    var player = LINE_ANIM_PLAYERS[name];
    if (player) { player(true); } // allowed to co-occur with the balloon pairing it
  }

  // --- Ambient animation triggers (called once per tick from drawFrame,
  // only while nothing's already playing) ----------------------------------

  function maybeTriggerRandomCough(emotion) {
    if (emotion !== EMOTION_REST) { return; }
    if (Math.random() < (TICK_MS / COUGH_RANDOM_REST_EVERY_MS)) { playCough(false); }
  }

  // Lick's ambient trigger -- "same rarity class as the random cough"
  // above, same REST-only gate, same shape of roll.
  function maybeTriggerRandomLick(emotion) {
    if (emotion !== EMOTION_REST) { return; }
    if (Math.random() < (TICK_MS / LICK_RANDOM_REST_EVERY_MS)) { playLick(false); }
  }

  function maybeTriggerSmoke(emotion) {
    if (emotion === EMOTION_VIBE) {
      if (Math.random() < (TICK_MS / SMOKE_VIBE_EVERY_MS)) { playSmoke(false); }
    } else if (emotion === EMOTION_REST || emotion === EMOTION_BORED) {
      if (Math.random() < (TICK_MS / SMOKE_IDLE_EVERY_MS)) { playSmoke(false); }
    }
  }

  function maybeTriggerSpyingLongSession(emotion) {
    if (emotion !== EMOTION_REST) { return; }
    if (!worldConditions().long_session) { return; }
    if (Math.random() < (TICK_MS / SPYING_LONG_SESSION_EVERY_MS)) { playSpying(false); }
  }

  // Yawn: "on entering bored" -- called from updateIdleState right where
  // isBored is newly set, not a per-tick roll like the others above.
  // Probabilistic even at baseline (playing it every single time would
  // get repetitive), much likelier late at night.
  function maybeYawnOnBoredEntry() {
    var chance = worldConditions().late_night ? YAWN_ON_BORED_LATE_NIGHT_CHANCE : YAWN_ON_BORED_CHANCE;
    if (Math.random() < chance) { playYawn(false); }
  }

  // Spying's second trigger: the visitor returning to this tab after
  // being away. visibilitychange fires on genuine tab-switch/minimize,
  // not on merely losing window focus, so this doesn't fire on every
  // click into some other on-screen element.
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) { return; }
    if (currentEmotion() !== EMOTION_REST) { return; }
    if (Math.random() < SPYING_ON_TAB_RETURN_CHANCE) { playSpying(false); }
  });

  // --- Stoked (add-to-cart) ------------------------------------------------

  // Set the instant a cart-add succeeds and never cleared for the rest of
  // the session (gift-card nudge, see isCartEmpty below) -- a real signal
  // that doesn't need a fresh /cart.js fetch to stay accurate immediately
  // after an AJAX add, unlike the server-rendered cart-has-items body
  // class, which only refreshes on the next full render.
  var cartHasItemsThisSession = false;

  function triggerStoked() {
    // cartHasItemsThisSession/noteActivity are real side effects the rest
    // of the file depends on (gift-card nudge, idle timers) regardless of
    // which page the add happened on -- always run those.
    cartHasItemsThisSession = true;
    noteActivity();
    // The STOKED emotion + dollar-eyes/surprised/context.cart-line
    // reaction is NOT one of the emotions the /cart quiet-observer spec
    // allows there ("rest, bored, sleep, vibe" only) -- an add-to-cart
    // that happens ON /cart itself (e.g. a "you may also like" widget)
    // gets the cart-observer's own "increased" reaction instead (see
    // onCartActivity above), not this one, so the two never fire
    // together for the same event.
    if (isQuietObserverPath()) { return; }
    isStoked = true;
    stokedUntil = Date.now() + STOKED_DURATION_MS;
    stokedStartTick = tickCount;
    // Reaction pack 2: sometimes dollar eyes, and when that roll fails a
    // SECOND, lower-odds roll for surprised instead -- never both. The
    // rest of the stoked reaction (bob, brows, the line below) is
    // unchanged either way; when both rolls fail, add-to-cart looks
    // exactly as it did before this pass. allowDuringTalk=true for
    // surprised so it isn't silently blocked by the reaction line's own
    // balloon (same reasoning playLineAnim already uses for line-paired
    // anims -- meant to play alongside it, not be starved by it).
    if (Math.random() < BOLTY_DOLLAR_EYES_CHANCE) {
      playDollarEyes();
    } else if (Math.random() < BOLTY_SURPRISED_CHANCE) {
      playSurprised(true);
    }
    // A direct reaction to the visitor's own action, not the timed
    // spontaneous cadence -- reuses context.cart (the new pool folds
    // cart-page and add-to-cart reactions into one bucket, no separate
    // "stoked" bucket exists anymore).
    if (isVisible()) { speak(pickUnseenFromBucket('context.cart')); }
  }

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (form && form.tagName === 'FORM' && typeof form.action === 'string' && form.action.indexOf('/cart/add') > -1) {
      triggerStoked();
    }
  });

  // --- Cart activity observer (2026-08-02 hygiene pass) --------------------
  // Replaces the previous window.fetch monkey-patch entirely. That wrapper
  // always returned the original, untouched promise (never altered what
  // callers received or swallowed their rejections) -- but merely being IN
  // the call path put bolty.js into the stack trace of every fetch
  // site-wide, cart-related or not, including completely unrelated ones
  // (an ad-blocker-blocked analytics call, Restock Rocket's own wrapper),
  // which then showed up in THEIR error logs. PerformanceObserver's
  // resource-timing stream sees every network request the page makes
  // (fetch- or XHR-driven, this theme's own vendor bundle included)
  // read-only, with nothing to wrap or intercept -- "observe, don't
  // intercept," literally: bolty.js no longer appears in anyone else's
  // call stack, ever.
  //
  // Outcome (item added vs removed vs cart emptied) isn't read off the
  // observed request/response directly -- Safari's own
  // PerformanceResourceTiming doesn't expose responseStatus, and this
  // store is ~80% mobile traffic per this file's own history, so that
  // would silently never fire for most visitors. Instead, every observed
  // /cart/add or /cart/change activity triggers ONE separate, independent
  // GET /cart.js, and every subscriber below reacts to a diff against the
  // last known item_count -- a second, wholly separate request, not a
  // continuation of the first.
  //
  // Shared by triggerStoked's own JS-cart-add detection (below) and the
  // /cart quiet-observer's reactions (further down, near contextBucket) --
  // one fetch, one diff, multiple independent listeners via onCartActivity.
  var cartActivityCallbacks = [];
  var lastKnownCartItemCount = null;

  function onCartActivity(fn) { cartActivityCallbacks.push(fn); }

  function fetchCartState() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  function notifyCartActivity() {
    fetchCartState().then(function (cart) {
      if (!cart || typeof cart.item_count !== 'number') { return; }
      var oldCount = lastKnownCartItemCount;
      var newCount = cart.item_count;
      lastKnownCartItemCount = newCount;
      cartActivityCallbacks.forEach(function (fn) { fn(newCount, oldCount, cart); });
    });
  }

  (function watchCartActivity() {
    if (window.__evaBoltyCartObserverInstalled) { return; } // guard against double-install
    window.__evaBoltyCartObserverInstalled = true;
    if (typeof PerformanceObserver === 'undefined') { return; } // unsupported -- silently no-op, the classic form-submit listener above still covers non-JS cart adds
    try {
      var po = new PerformanceObserver(function (list) {
        list.getEntries().forEach(function (entry) {
          if (entry.name.indexOf('/cart/add') > -1 || entry.name.indexOf('/cart/change') > -1) {
            notifyCartActivity();
          }
        });
      });
      po.observe({ type: 'resource', buffered: true });
    } catch (e) {}
  })();

  // JS-driven (fetch- or XHR-based) cart-add detection for the "stoked"
  // reaction -- oldCount !== null guards the very first read of a page
  // (nothing to compare against yet, so no false "stoked" on arrival);
  // a real increase anywhere on the site (not just /cart) still triggers
  // it, matching the old fetch-wrapper's own site-wide coverage.
  onCartActivity(function (newCount, oldCount) {
    if (oldCount !== null && newCount > oldCount) { triggerStoked(); }
  });

  // --- Cart-add recommendation chain (2026-08-11) --------------------------
  // Deck-triggered, forward-only nudge: add a deck -> griptape reminder;
  // add griptape (or dismiss that reminder) -> trucks reminder; add
  // trucks -> wheels reminder; add wheels -> bearings reminder. Each step
  // fires at most once per session, and the whole chain only exists if a
  // real deck-add was observed this session (STORAGE_CART_CHAIN_ARMED) --
  // explicit spec: this is a pure forward nudge off what was JUST added,
  // NEVER a scan of current cart contents for what's missing. If a
  // visitor's cart already has some parts and skipped others, Bolty does
  // not proactively point out the gap -- he only helps fill gaps if asked
  // directly (Ask Bolty or the wizard). No brand-opinion copy either:
  // searched this whole repo for a "Skate Knowledge"/brand-take canon
  // Per may have given him in a prior interview round -- none exists yet
  // (checked assets/bolty-lines.json and everywhere else), so every
  // reminder below stays neutral, pulled straight from the existing
  // product_* line pools. Per's own rule for exactly this situation:
  // stay neutral rather than invent a house favorite, flag the gap for a
  // future interview round instead.
  //
  // Forward references SETUP_PARTS/partByKey/pickPartTip (defined later,
  // in the "Buy something" wizard section) -- safe: this code only runs
  // inside event callbacks invoked long after the full script (and those
  // var/function assignments) has executed, never at parse time.
  //
  // Category detection reuses productTypeBucket() (context-line section,
  // below) against each cart line's product_title/product_type -- the
  // SAME word-boundary deck/truck/wheel/bearing/grip matching the rest
  // of the file already uses for on-page context lines, not a new
  // heuristic invented for this feature.
  // COPY WRITTEN THIS PASS (2026-08-24) -- flagged for Per to rewrite in his
  // own voice if he wants; the rest of Bolty's lines all come from
  // assets/bolty-lines.json, which has no line for either of these cases.
  var EVA_GRIP_HANDLED_LINE = "that eva deck's already gripped, man.. jessup, done at the shop. trucks next..";
  var EVA_GRIP_WIZARD_CAVEAT = " eva decks come gripped already, so this one's for everything else..";

  var CART_CHAIN_STEPS = ['griptape', 'trucks', 'wheels', 'bearings'];
  var STORAGE_CART_CHAIN_ARMED = 'evaBoltyCartChainArmed';

  function cartChainShownKey(partKey) { return 'evaBoltyCartChainShown_' + partKey; }

  // Deck detection by COLLECTION MEMBERSHIP, not the title regex
  // (2026-08-24). productTypeBucket's \bdeck\b/\bboard\b test misses 13 of
  // the 33 products in SKATEBOARDS -- every Deathwish, Sour, Creature,
  // Passport and Baker deck whose title carries neither word -- so no
  // brand's deck reliably armed the chain. product_type is empty catalog-
  // wide and vendor is the literal string "Eva Hardware" on everything, so
  // the collection is the only real category data. Verified live: 13
  // reclassify, and nothing in SKATEBOARDS is in ACCESSORIES/STUFF.
  //
  // The two griptape SKUs are themselves members of SKATEBOARDS in this
  // catalog, so they're excluded explicitly -- the same exclusion
  // snippets/griptape-addon.liquid already makes server-side for the same
  // reason. Everything that isn't a deck still falls through to
  // productTypeBucket unchanged.
  var CART_DECK_COLLECTION = 'skateboards';
  var CART_EVA_COLLECTION = 'eva';

  // Handle -> 1 map for a collection, memoised via fetchCollectionProducts
  // (defined further down with the ask-box routing; function declarations
  // hoist, and this is only ever called from a cart event long after init).
  function collectionHandleSet(handle) {
    return fetchCollectionProducts(handle).then(function (products) {
      var set = {};
      for (var i = 0; i < products.length; i++) { set[products[i].handle] = 1; }
      return set;
    });
  }

  function isDeckHandle(handle, deckSet) {
    return !!(handle && deckSet[handle] && !GRIPTAPE_HANDLES[handle]);
  }

  // Eva decks ship with Jessup grip ALREADY APPLIED (Per, 2026-08-24), so
  // they must never be offered griptape. Eva deck = in SKATEBOARDS *and* in
  // EVA -- checked live against the whole SKATEBOARDS collection with zero
  // disagreements against the title-token test snippets/griptape-addon.liquid
  // already uses server-side for this same rule. Collection membership is
  // preferred over the title test because it survives a future Eva deck
  // whose title omits the word "Eva".
  function isEvaDeckHandle(handle, deckSet, evaSet) {
    return isDeckHandle(handle, deckSet) && !!evaSet[handle];
  }

  function cartLineCategory(item, deckSet) {
    if (deckSet && isDeckHandle(item.handle, deckSet)) { return 'product_deck'; }
    return productTypeBucket({ title: item.product_title, type: item.product_type, tags: [] });
  }

  // Brand-pick overrides (2026-08-11, Per's canon FOR THIS CHAIN
  // specifically) -- griptape (Jessup) and bearings (SKF) have confirmed
  // picks; trucks/wheels don't yet. Deliberately NOT applied to the
  // wizard's own griptape step (SETUP_PARTS still routes there to
  // /collections/mob-griptape) -- scoped to what Per actually confirmed
  // for the cart-add chain, not assumed to extend to the wizard too;
  // flagged to him separately. Trucks/wheels have no confirmed pick yet
  // (trucks needs its own interview round per Per's own words -- real
  // brand differences, not a guess; wheels: no take given at all) so
  // those two fall through to the neutral SETUP_PARTS entry, same as
  // before.
  //
  // griptape -> /products/jessup-griptape confirmed live (200, real
  // "Jessup" content on the page) before wiring this in -- the
  // mob-griptape COLLECTION it replaces doesn't even contain a Jessup
  // product, so recommending Jessup by brand while linking to that
  // collection would have been a real mismatch, same class of bug as
  // yesterday's wizard-link fixes.
  //
  // bearings -> SKF, no link change needed: /collections/skf (already
  // the wizard's own bearings link, live-verified yesterday) is ALREADY
  // 100% SKF products (6 active listings, all "SKF - ... Bearings"), so
  // the pick and the existing route were already the same thing -- only
  // the link label changes, to read as an actual endorsement rather
  // than a generic "bearings" pointer.
  var CART_CHAIN_BRAND_OVERRIDE = {
    griptape: { url: '/products/jessup-griptape', noun: 'Jessup griptape' },
    bearings: { url: '/collections/skf', noun: 'SKF bearings' }
  };

  // textOverride: used only by the Eva-deck path below, which needs to say
  // "grip's already handled" rather than this part's normal tip line.
  function showCartChainReminder(partKey, onDismiss, textOverride) {
    var part = partByKey(partKey);
    if (!part || !isVisible() || isQuietObserverPath()) { return; }
    var override = CART_CHAIN_BRAND_OVERRIDE[partKey];
    var url = override ? override.url : part.url;
    var noun = override ? override.noun : part.noun;
    sSet(cartChainShownKey(partKey), '1');
    showTipWithLinkAndActions(textOverride || pickPartTip(part.key), 'take me to the ' + noun, url, [], onDismiss);
  }

  // newlyAddedItems: real /cart.js line objects that weren't present on
  // the previous read (see the diffing subscriber below) -- never the
  // full current cart, so this can only ever react to something that was
  // just, actually added.
  function advanceCartChainOnAdd(newlyAddedItems) {
    if (isQuietObserverPath()) { return; } // /cart quiet-observer: no proactive balloons here either, same guard triggerStoked itself uses
    // Both collection fetches are memoised, so this costs one request each
    // per page at most, and nothing at all on later adds.
    Promise.all([
      collectionHandleSet(CART_DECK_COLLECTION),
      collectionHandleSet(CART_EVA_COLLECTION)
    ]).then(function (sets) {
      runCartChain(newlyAddedItems, sets[0], sets[1]);
    });
  }

  function runCartChain(newlyAddedItems, deckSet, evaSet) {
    if (isQuietObserverPath()) { return; } // re-checked: the fetches above are async, the visitor may have reached /cart since
    var addedBuckets = newlyAddedItems.map(function (item) { return cartLineCategory(item, deckSet); });

    if (addedBuckets.indexOf('product_deck') > -1 && !sGet(STORAGE_CART_CHAIN_ARMED)) {
      sSet(STORAGE_CART_CHAIN_ARMED, '1');

      // Eva decks arrive with Jessup grip already on them, so the griptape
      // step is SKIPPED for them -- acknowledged, not sold -- and the chain
      // moves straight to the next thing in the build order. If the same add
      // brought a non-Eva deck too, that deck genuinely needs grip, so the
      // normal griptape step wins.
      var addedDecks = newlyAddedItems.filter(function (item) { return isDeckHandle(item.handle, deckSet); });
      var everyDeckIsEva = addedDecks.length > 0 && addedDecks.every(function (item) {
        return isEvaDeckHandle(item.handle, deckSet, evaSet);
      });

      if (everyDeckIsEva) {
        if (!sGet(cartChainShownKey('trucks'))) {
          showCartChainReminder('trucks', null, EVA_GRIP_HANDLED_LINE);
        }
        return; // one balloon per add
      }

      if (!sGet(cartChainShownKey('griptape'))) {
        showCartChainReminder('griptape', function () {
          // "(or once dismissed)" -- spec's own escape hatch, scoped to
          // ONLY this one transition; steps 2->3 and 3->4 below are
          // add-only, no dismiss shortcut.
          if (!sGet(cartChainShownKey('trucks'))) { showCartChainReminder('trucks'); }
        });
      }
      return; // one balloon per add
    }

    if (!sGet(STORAGE_CART_CHAIN_ARMED)) { return; } // no deck added this session yet -- chain stays fully silent, no gap-scanning, no unprompted trucks/wheels/bearings pitch

    for (var i = 0; i < CART_CHAIN_STEPS.length - 1; i++) {
      var justAddedBucket = 'product_' + CART_CHAIN_STEPS[i];
      var nextKey = CART_CHAIN_STEPS[i + 1];
      if (addedBuckets.indexOf(justAddedBucket) > -1 && !sGet(cartChainShownKey(nextKey))) {
        showCartChainReminder(nextKey);
        return; // one balloon per add
      }
    }
  }

  // Diffs cart.items by their stable per-line `key` (variant + properties
  // hash) against the last read to isolate genuinely NEW lines -- a
  // quantity bump on an already-owned line is deliberately NOT "newly
  // added" (already owns it, no re-nag). null-until-first-read mirrors
  // lastKnownCartItemCount's own guard just above: nothing to diff
  // against on the very first read of a fresh page load, so that read
  // only seeds the baseline and never fires the chain retroactively for
  // items already sitting in the cart from a previous page/session.
  var cartChainLastKnownKeys = null;

  onCartActivity(function (newCount, oldCount, cart) {
    var items = (cart && cart.items) || [];
    var currentKeys = {};
    items.forEach(function (item) { currentKeys[item.key] = item; });
    if (cartChainLastKnownKeys === null) {
      cartChainLastKnownKeys = currentKeys;
      return;
    }
    var previousKeys = cartChainLastKnownKeys;
    cartChainLastKnownKeys = currentKeys;
    var newlyAdded = items.filter(function (item) { return !previousKeys[item.key]; });
    if (newlyAdded.length) { advanceCartChainOnAdd(newlyAdded); }
  });

  // --- Voice lines (JSON-driven, this pass) --------------------------------
  // The entire line pool now lives in assets/bolty-lines.json -- one source
  // of truth, no hardcoded text in this file. Fetched once (loadLines,
  // module init below) via window.EvaBoltyLinesUrl (wired in
  // layout/theme.liquid, same asset_url-handoff convention as
  // EvaKallareFilmsManifestUrl etc., since a plain .js asset can't call
  // asset_url itself). If the fetch fails, LINES_DATA stays null and every
  // picker below returns null -- Bolty stays silent rather than erroring;
  // the character itself (render/animation/drag/hide) is unaffected.
  //
  // Every line gets a stable ID of "<path>.<index>" (e.g.
  // "context.product_hardware.3", "world.weather_rain.1", "drag.0") at
  // load time. A per-session seen-set (sessionStorage, key evaBoltySeen --
  // a plain JSON array of shown IDs) blocks repeats; sessionStorage
  // naturally survives PJAX (this script's own module state does too, but
  // the spec calls for sessionStorage specifically, so that's the
  // authoritative store, read/written fresh on every check). When every
  // line in the whole file has been shown this session, the set clears
  // and starts over rather than ever going silent.

  var LINES_DATA = null;
  var FLAT_LINES = {};   // id -> {t, src}
  var BUCKET_IDS = {};   // path key ("entrance", "context.product_deck", "world.weather_rain", ...) -> [id, ...]
  var TOTAL_LINE_COUNT = 0;
  var linesLoadAttempted = false;

  function flattenArrayBucket(pathKey, arr) {
    var ids = [];
    (arr || []).forEach(function (item, i) {
      var id = pathKey + '.' + i;
      FLAT_LINES[id] = item;
      ids.push(id);
    });
    BUCKET_IDS[pathKey] = ids;
    TOTAL_LINE_COUNT += ids.length;
  }

  // --- Bubble type (thought bubble pass) -----------------------------------
  // SPEECH (the Office Assistant balloon + tail, "talking TO the
  // visitor") vs THOUGHT (cloud + circle trail, "thinking out loud, the
  // visitor overhears"). Bucket-level default, overridable per line via
  // an optional "bubble":"speak"|"think" key in bolty-lines.json --
  // absent or unrecognized always falls through to the bucket default,
  // never errors. Options (radio-bullet flows) are speech by definition,
  // enforced in showBalloonLine itself, not here.
  var BUBBLE_SPEAK = 'speak', BUBBLE_THINK = 'think';

  // pathKey is everything before the trailing ".N" index lineFromId's
  // caller already builds every id from (e.g. "world.weather_rain.3" ->
  // "world.weather_rain") -- reusing that existing id shape rather than
  // threading a separate bucket argument through every picker.
  function defaultBubbleForBucket(pathKey) {
    if (pathKey === 'gossip' || pathKey === 'philosophy' || pathKey === 'scene') { return BUBBLE_THINK; }
    if (pathKey.indexOf('world.') === 0) { return BUBBLE_THINK; }
    // Everything else -- entrance, service, all context.* buckets,
    // sold_out (a context.* bucket), drag, hide, generic, and every
    // interactive flow -- speech by default, per spec.
    return BUBBLE_SPEAK;
  }

  // Single place that turns a line id into the {id,t,src,anim,bubble}
  // pick object every picker below returns -- every pick site used to
  // build this object by hand, which is exactly how a first pass at
  // "anim" as an optional per-line key could have quietly gone missing
  // from three of the four call sites; one shared builder means there's
  // only one place to get it right. anim stays undefined (never a key at
  // all, via the spread-free conditional below) when the line doesn't
  // have one, which speak()'s own `if (pick.anim)` treats identically to
  // it being absent -- never an error either way. bubble is always set
  // (never undefined) since every pick needs SOME rendering to fall back
  // to; bubbleSource is carried along purely for the ?boltydev=1 log in
  // speak(), not used for rendering.
  function lineFromId(id) {
    var line = FLAT_LINES[id];
    var pick = { id: id, t: line.t, src: line.src };
    if (line.anim) { pick.anim = line.anim; }
    var pathKey = id.substring(0, id.lastIndexOf('.'));
    var bucketDefault = defaultBubbleForBucket(pathKey);
    var override = (line.bubble === BUBBLE_SPEAK || line.bubble === BUBBLE_THINK) ? line.bubble : null;
    pick.bubble = override || bucketDefault;
    pick.bubbleSource = override ? ('line override') : ('bucket default: ' + pathKey);
    return pick;
  }

  function loadLines() {
    if (linesLoadAttempted) { return; }
    linesLoadAttempted = true;
    var url = window.EvaBoltyLinesUrl;
    if (!url) { return; } // no URL wired -- fail silent, per spec
    fetch(url).then(function (r) {
      if (!r.ok) { throw new Error('bad status'); }
      return r.json();
    }).then(function (data) {
      LINES_DATA = data;
      ['entrance', 'entrance_late_night', 'drag', 'hide', 'philosophy', 'generic', 'service', 'scene', 'gossip'].forEach(function (key) {
        flattenArrayBucket(key, data[key]);
      });
      Object.keys(data.context || {}).forEach(function (key) {
        flattenArrayBucket('context.' + key, data.context[key]);
      });
      Object.keys(data.world || {}).forEach(function (key) {
        flattenArrayBucket('world.' + key, data.world[key]);
      });
    }).catch(function () {
      LINES_DATA = null; // fetch failed (network, bad JSON, 404, ...) -- stay silent, never throw
    });
  }

  function linesReady() { return !!LINES_DATA; }

  // Same ?xxxdev=1 convention assets/eva-radio.js already uses
  // (?tapedev=1) for its own dev-only logging -- no Bolty-specific debug
  // flag existed before this pass, so this establishes one on the same
  // pattern rather than inventing a new convention.
  function isBoltyDevMode() {
    try { return new URLSearchParams(window.location.search).get('boltydev') === '1'; } catch (e) { return false; }
  }

  function logRung(rung, pathKey, id) {
    if (isBoltyDevMode()) { console.debug('[bolty] rung=' + rung + ' bucket=' + pathKey + ' id=' + id); }
  }

  // --- Seen-set (sessionStorage, survives PJAX by construction) -----------

  var STORAGE_LINE_SEEN = 'evaBoltySeen';

  function getSeenIds() {
    try {
      var raw = sessionStorage.getItem(STORAGE_LINE_SEEN);
      if (!raw) { return []; }
      var arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr : [];
    } catch (e) { return []; }
  }

  function isLineSeen(id) { return getSeenIds().indexOf(id) > -1; }

  function markLineSeen(id) {
    try {
      var arr = getSeenIds();
      if (arr.indexOf(id) === -1) {
        arr.push(id);
        sessionStorage.setItem(STORAGE_LINE_SEEN, JSON.stringify(arr));
      }
    } catch (e) {}
  }

  function resetSeenIds() {
    try { sessionStorage.removeItem(STORAGE_LINE_SEEN); } catch (e) {}
  }

  function maybeResetIfExhausted() {
    if (!linesReady()) { return; }
    if (getSeenIds().length >= TOTAL_LINE_COUNT) { resetSeenIds(); }
  }

  // Random UNSEEN line from one bucket path, or null if that bucket is
  // empty or every one of its lines has already been shown this session.
  function pickUnseenFromBucket(pathKey) {
    var ids = BUCKET_IDS[pathKey];
    if (!ids || !ids.length) { return null; }
    var seen = getSeenIds();
    var unseen = ids.filter(function (id) { return seen.indexOf(id) === -1; });
    if (!unseen.length) { return null; }
    var pick = unseen[Math.floor(Math.random() * unseen.length)];
    return lineFromId(pick);
  }

  // Same as pickUnseenFromBucket, pooled across several bucket paths at
  // once (scene+gossip's shared "loitering" rung, below) -- one random
  // pick across the combined unseen set, not one bucket tried before the
  // other, so neither bucket systematically goes first.
  function pickUnseenFromAnyBucket(pathKeys) {
    var combined = [];
    pathKeys.forEach(function (pk) {
      (BUCKET_IDS[pk] || []).forEach(function (id) { combined.push(id); });
    });
    var seen = getSeenIds();
    var unseen = combined.filter(function (id) { return seen.indexOf(id) === -1; });
    if (!unseen.length) { return null; }
    var pick = unseen[Math.floor(Math.random() * unseen.length)];
    return lineFromId(pick);
  }

  // Single funnel: every shown line (spontaneous, entrance, drag, hide,
  // add-to-cart) passes through here so the seen-set stays accurate no
  // matter which picker produced it.
  function speak(pick) {
    if (!pick) { return; }
    markLineSeen(pick.id);
    if (isBoltyDevMode()) { console.debug('[bolty] bubble=' + pick.bubble + ' (' + pick.bubbleSource + ')'); }
    showBalloonLine(pick.t, null, pick.bubble);
    // Optional per-line "anim" key (assets/bolty-lines.json) -- absence
    // is the overwhelmingly common case and must never error; playLineAnim
    // itself already no-ops on a missing/unrecognized name, so a plain
    // truthy check is enough here.
    if (pick.anim) { playLineAnim(pick.anim); }
  }

  // Wraps an UNPROMPTED line (entrance greeting, or the periodic
  // spontaneous ladder) in a randomized BOLTY_SPEAK_DELAY_MIN/MAX_MS
  // delay -- see that constant's own comment near the top of the file.
  // Reactions (drag/hide/add-to-cart/tap) call speak() directly and stay
  // immediate, per spec -- this is only for lines nothing prompted.
  //
  // pickFn is called AFTER the delay elapses, not before -- so if the
  // delay gets cancelled (page/context changed underneath it) nothing is
  // ever picked/marked-seen for a line that's no longer going to show.
  // A double-rAF defers the delay's own start to after the next paint
  // ("never speak before the page has painted"), and forces a blink right
  // as the countdown begins -- a cheap, already-existing animation (see
  // BLINK_TICKS) standing in for "he notices you before he speaks,"
  // rather than building a new animation state for it.
  var pendingDelayedSpeakTimer = null;

  function cancelPendingDelayedSpeak() {
    if (pendingDelayedSpeakTimer) {
      clearTimeout(pendingDelayedSpeakTimer);
      pendingDelayedSpeakTimer = null;
    }
  }

  function speakAfterDelay(pickFn) {
    cancelPendingDelayedSpeak();
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        blinkUntilTick = tickCount + BLINK_TICKS;
        var delay = BOLTY_SPEAK_DELAY_MIN_MS + Math.random() * (BOLTY_SPEAK_DELAY_MAX_MS - BOLTY_SPEAK_DELAY_MIN_MS);
        pendingDelayedSpeakTimer = setTimeout(function () {
          pendingDelayedSpeakTimer = null;
          speak(pickFn());
        }, delay);
      });
    });
  }

  // A PJAX navigation completing means the page/context just changed --
  // any delayed line still waiting from the PREVIOUS page is now stale
  // and must not arrive late on the new one.
  document.addEventListener('eva:pjax:loaded', cancelPendingDelayedSpeak);

  // --- Context bucket (page/product routing) ------------------------------
  // Priority, most-specific-first (own judgment call where the spec
  // doesn't rank two page-level checks against each other -- documented
  // inline at each tier): 404 > kallare > contact > faq/returns/shipping >
  // search(empty) > cart > home > newsletter-on-screen > radio-playing >
  // collection > product-type (sold_out override first) . Returns null
  // (no context match -- e.g. a plain /pages/about) rather than a
  // "fallback" bucket; the ladder's own rung (b)/(c)/(d) below handle
  // that case, per spec.

  function isNewsletterFormOnScreen() {
    var form = document.querySelector('form[id^="newsletter-"]');
    if (!form) { return false; }
    var rect = form.getBoundingClientRect();
    var viewportH = window.innerHeight || document.documentElement.clientHeight;
    return rect.bottom > 0 && rect.top < viewportH;
  }

  // Word-boundary match, not a raw substring test -- "board" as a raw
  // substring false-positives on the generic "skateboard" tag lots of
  // unrelated products in this catalog carry (commit 5e06771's fix,
  // carried over unchanged). Checks title too, not just type/tags --
  // this catalog's product.type is usually empty and tags are generic,
  // but the real category word ("Deck", "Wheel", ...) reliably shows up
  // in the title ("Eva - Glocken Deck", "Hardbody - Violate Deck 8.5").
  // product.vendor is NOT used for anything here -- also from 5e06771:
  // it's just "Eva Hardware" on both genuine Eva decks and third-party
  // ones, not a real brand signal (moot anyway -- the new line pool has
  // no Eva-brand-specific bucket to route to).
  function productTypeBucket(ctx) {
    var type = (ctx.type || '').toLowerCase();
    var tags = (ctx.tags || []).map(function (t) { return String(t).toLowerCase(); });
    var title = (ctx.title || '').toLowerCase();
    var hasAny = function (needle) {
      var re = new RegExp('\\b' + needle + '\\b', 'i');
      return re.test(type) || re.test(title) || tags.some(function (t) { return re.test(t); });
    };
    // truck/wheel/bearing all need the optional trailing 's' for the
    // exact same reason griptape did (found 2026-08-11, see below): every
    // real product on this store uses the PLURAL word ("Trucks", "Wheels",
    // "Bearings" -- collection titles, product titles, all of it) and
    // \btruck\b/\bwheel\b/\bbearing\b never match a plural, so these three
    // buckets have silently never matched a single real product either --
    // confirmed live against actual catalog titles ("Independent Trucks
    // 149", "Spitfire Wheels 53mm", "SKF - Skateboard Bearings"), not
    // guessed. 's?' only allows the singular/plural pair, not an open
    // suffix -- "Wheelbase 14" (a real spec line in deck descriptions)
    // still correctly does NOT match \bwheels?\b, verified directly.
    if (hasAny('deck') || hasAny('board')) { return 'product_deck'; }
    if (hasAny('trucks?')) { return 'product_trucks'; }
    if (hasAny('wheels?')) { return 'product_wheels'; }
    if (hasAny('bearings?')) { return 'product_bearings'; }
    // 'griptape' checked ahead of the bare 'grip' -- \bgrip\b never
    // matches "Griptape" (found 2026-08-11, building the cart-add chain:
    // every real griptape product on this store is titled "...Griptape..."
    // or "...Griptape Cleaner...", one compound word, so \b never lands
    // between "grip" and "tape" and this bucket has silently never
    // matched a single real product since productTypeBucket shipped --
    // confirmed with a plain regex test, not guessed). 'grip' kept as a
    // fallback for a hypothetical future product titled bare "Grip".
    if (hasAny('griptape') || hasAny('grip')) { return 'product_griptape'; }
    if (hasAny('shoe')) { return 'product_shoes'; }
    if (hasAny('shirt') || hasAny('hoodie') || hasAny('sweatshirt') || hasAny('jacket')) { return 'product_apparel'; }
    // Riser pads and tools (allen keys etc.) have no bucket of their own
    // in the new pool -- folded into product_hardware, same family of
    // voice content (bolts/screws/tools), since the old riser/tool
    // buckets no longer exist in bolty-lines.json.
    if (hasAny('bolt') || hasAny('hardware') || hasAny('nut') || hasAny('screw') || hasAny('riser') || hasAny('tool') || hasAny('allen') || hasAny('key')) { return 'product_hardware'; }
    return 'product_other'; // complete decks, wax/bushings, gift cards, anything else
  }

  function contextBucket() {
    var bodyClass = document.body.className;
    if (/\btemplate-404\b/.test(bodyClass)) { return 'error_404'; }

    var path = window.location.pathname.toLowerCase();
    if (path.indexOf('/pages/evas-kallare') === 0) { return 'kallare'; }
    if (path.indexOf('/pages/contact') === 0) { return 'contact'; }

    var title = document.title.toLowerCase();
    if (/return|faq|shipping/.test(path) || /return|faq|shipping/.test(title)) { return 'faq'; }

    if (/\btemplate-search\b/.test(bodyClass)) {
      var resultsWrap = document.getElementById('CollectionAjaxContent');
      if (!resultsWrap || resultsWrap.querySelectorAll('.grid__item').length === 0) {
        return 'search'; // this bucket's own lines are specifically about finding nothing
      }
      // real results showing -- no bucket claims a normal search page, falls through
    }

    if (/\btemplate-cart\b/.test(bodyClass) || path === '/cart') { return 'cart'; }
    if (path === '/') { return 'home'; }

    // Real-time UI state (what's actually on screen right now) ranks above
    // the more static collection/product routing below -- own judgment
    // call, not explicitly ordered by the spec.
    if (isNewsletterFormOnScreen()) { return 'newsletter'; }
    if (isVibing) { return 'radio'; } // no dedicated radio PAGE exists on this store -- "actively playing" is the sensible real-world reading of this bucket, reusing the existing eva:radio-play/-stop wiring

    if (path.indexOf('/collections/') === 0) { return 'collection'; }

    var ctx = window.EvaBoltyProductContext;
    if (ctx) {
      if (ctx.available === false) { return 'sold_out'; } // overrides type, per spec
      return productTypeBucket(ctx);
    }

    return null; // no match (e.g. /pages/about) -- ladder falls through to world/philosophy/generic
  }

  // --- World-state conditions ----------------------------------------------
  // Reuses data the theme already fetches -- no new API calls. Weather:
  // layout/theme.liquid + assets/header-weather.js already cache
  // {sky, temp, sunset, sunrise} in localStorage (window.EvaTheme.
  // WEATHER_STORAGE_KEY) for the header LCD strip; read straight from
  // there. night_mode reads the header's own resolved data-mode attribute
  // (layout/theme.liquid's auto/day/night resolution) rather than
  // recomputing anything.

  var WEATHER_COLD_MAX_C = 5;   // judgment call -- Stockholm-appropriate "cold" cutoff, not spec'd numerically
  var WEATHER_NICE_MIN_C = 15;  // judgment call -- "nice" = clear AND comfortably warm

  function getWeatherCache() {
    try {
      var key = (window.EvaTheme && window.EvaTheme.WEATHER_STORAGE_KEY) || 'eva-weather-cache';
      var raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }

  var STORAGE_EVER_VISITED = 'evaBoltyEverVisited';
  var isRepeatVisitorCached = null; // evaluated once at true module init (see bottom of file) -- see its own call site for why

  function initRepeatVisitorFlag() {
    try {
      var already = localStorage.getItem(STORAGE_EVER_VISITED) === '1';
      isRepeatVisitorCached = already;
      if (!already) { localStorage.setItem(STORAGE_EVER_VISITED, '1'); }
    } catch (e) { isRepeatVisitorCached = false; }
  }

  var STORAGE_SESSION_START = 'evaBoltySessionStart';
  var LONG_SESSION_MS = 10 * 60 * 1000;

  function sessionStartTime() {
    var v = sGet(STORAGE_SESSION_START);
    if (!v) { v = String(Date.now()); sSet(STORAGE_SESSION_START, v); }
    return parseInt(v, 10);
  }

  // --- Page-view count (gift-card nudge) -----------------------------------
  // Counts real page views this session, hard loads and PJAX navigations
  // alike -- notePageView() is called once from init() (a hard load, one
  // real script instance per load) and once per 'eva:pjax:loaded' (a soft
  // nav within the same instance), so neither path double- or under-counts
  // the other.
  var STORAGE_PAGE_VIEWS = 'evaBoltyPageViews';

  function notePageView() {
    var n = parseInt(sGet(STORAGE_PAGE_VIEWS) || '0', 10);
    if (isNaN(n)) { n = 0; }
    sSet(STORAGE_PAGE_VIEWS, String(n + 1));
  }

  function pageViewCount() {
    var n = parseInt(sGet(STORAGE_PAGE_VIEWS) || '0', 10);
    return isNaN(n) ? 0 : n;
  }

  // The hard-load count itself happens in init() (below) -- this only
  // covers the soft-nav side, one increment per completed PJAX swap.
  document.addEventListener('eva:pjax:loaded', notePageView);

  // Landing on /cart via a PJAX nav (the common case -- most cart/product
  // links are PJAX-eligible): re-seed the cart-observer baseline the same
  // way init() does for a hard load, and force-close any balloon that
  // happened to still be open from the previous page (an interactive
  // options-menu/tip/ask-panel balloon persisting into quiet-observer mode
  // would contradict "NO options menu" -- rootEl lives outside #MainContent
  // and is never rebuilt by PJAX, so nothing else would close it for free).
  document.addEventListener('eva:pjax:loaded', function () {
    if (!isQuietObserverPath()) { return; }
    notifyCartActivity();
    hideBalloon();
  });

  function isMay17() {
    var now = new Date();
    return now.getMonth() === 4 && now.getDate() === 17; // getMonth() is 0-indexed
  }

  // Boundaries a clean, non-overlapping reading of the spec's stated
  // ranges (morning<11, afternoon 11-17, evening 17-23, late_night
  // 23-05): morning's own lower bound (5) isn't spelled out explicitly,
  // but is the only reading that doesn't double-count hours 0-4 as both
  // "late_night" and "morning" at once.
  function worldConditions() {
    var cache = getWeatherCache();
    var sky = cache && typeof cache.sky === 'string' ? cache.sky : null;
    var temp = cache && typeof cache.temp === 'number' ? cache.temp : null;
    var hour = new Date().getHours();
    var day = new Date().getDay(); // 0 Sun .. 6 Sat, local browser time
    var shopOpenDay = day >= 3 && day <= 6; // Wed-Sat
    return {
      weather_rain: sky === 'rain',
      weather_snow: sky === 'snow',
      weather_cold: temp !== null && temp <= WEATHER_COLD_MAX_C,
      weather_nice: sky === 'clear' && temp !== null && temp >= WEATHER_NICE_MIN_C,
      morning: hour >= 5 && hour < 11,
      afternoon: hour >= 11 && hour < 17,
      evening: hour >= 17 && hour < 23,
      late_night: hour >= 23 || hour < 5,
      shop_open: shopOpenDay,
      shop_closed: !shopOpenDay,
      night_mode: document.documentElement.getAttribute('data-mode') === 'nighttime',
      repeat_visitor: !!isRepeatVisitorCached,
      long_session: (Date.now() - sessionStartTime()) >= LONG_SESSION_MS,
      eva_birthday: isMay17()
    };
  }

  // eva_birthday outranks every other true world bucket, per spec; among
  // the rest, random order, first one with an unseen line wins.
  function pickWorldLine() {
    var conds = worldConditions();
    if (conds.eva_birthday) {
      var forced = pickUnseenFromBucket('world.eva_birthday');
      if (forced) { return { pathKey: 'world.eva_birthday', line: forced }; }
    }
    var trueKeys = Object.keys(conds).filter(function (k) { return k !== 'eva_birthday' && conds[k]; });
    for (var i = trueKeys.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var tmp = trueKeys[i]; trueKeys[i] = trueKeys[j]; trueKeys[j] = tmp;
    }
    for (var k = 0; k < trueKeys.length; k++) {
      var pathKey = 'world.' + trueKeys[k];
      var pick = pickUnseenFromBucket(pathKey);
      if (pick) { return { pathKey: pathKey, line: pick }; }
    }
    return null;
  }

  // Collection pages loiter-eligible at a reduced rate vs. the always-
  // eligible contexts below (home/kallare/radio/long-session) -- no rate
  // was specified, this is a deliberately conservative starting value,
  // tune freely.
  var COLLECTION_LOITER_RATE = 0.3;

  // scene/gossip ("loitering" lines) fire only where a visitor is hanging
  // around rather than shopping. The product/cart/search/404 exclusion is
  // a hard override -- checked first, wins outright even if the radio's
  // playing or the session's long, since someone comparing deck widths on
  // a product page is shopping, not loitering, full stop. Below that, an
  // explicit allow-list (not a general "everything else" fallback):
  // home/kallare always qualify, radio-playing and long_session qualify
  // wherever they're not already excluded above, collection pages qualify
  // at COLLECTION_LOITER_RATE. Any other context (faq/contact/newsletter/
  // no match) does not loiter.
  function isLoiteringEligible() {
    var ctx = contextBucket();
    if (ctx === 'cart' || ctx === 'search' || ctx === 'error_404' || ctx === 'sold_out' ||
      (ctx && ctx.indexOf('product_') === 0)) {
      return false;
    }
    if (ctx === 'home' || ctx === 'kallare') { return true; }
    if (isVibing) { return true; }
    if ((Date.now() - sessionStartTime()) >= LONG_SESSION_MS) { return true; }
    if (ctx === 'collection') { return Math.random() < COLLECTION_LOITER_RATE; }
    return false;
  }

  // --- Fallback ladder (spontaneous lines only) ---------------------------
  // a. context bucket matching the current page
  // a2. scene/gossip ("loitering"), only when isLoiteringEligible()
  // b. a world bucket whose condition is currently true
  // c. philosophy
  // d. generic
  // If literally nothing unseen exists anywhere (global exhaustion), the
  // set clears and the whole ladder runs once more rather than going silent.

  function runLadderOnce() {
    var ctxKey = contextBucket();
    if (ctxKey) {
      var pick = pickUnseenFromBucket('context.' + ctxKey);
      if (pick) { logRung('a', 'context.' + ctxKey, pick.id); return pick; }
    }
    if (isLoiteringEligible()) {
      var loiterPick = pickUnseenFromAnyBucket(['scene', 'gossip']);
      if (loiterPick) { logRung('a2', 'loitering', loiterPick.id); return loiterPick; }
    }
    var worldPick = pickWorldLine();
    if (worldPick) { logRung('b', worldPick.pathKey, worldPick.line.id); return worldPick.line; }
    var philPick = pickUnseenFromBucket('philosophy');
    if (philPick) { logRung('c', 'philosophy', philPick.id); return philPick; }
    var genPick = pickUnseenFromBucket('generic');
    if (genPick) { logRung('d', 'generic', genPick.id); return genPick; }
    return null;
  }

  function pickSpontaneousLadder() {
    if (!linesReady()) { return null; }
    maybeResetIfExhausted();
    var result = runLadderOnce();
    if (!result) {
      resetSeenIds();
      result = runLadderOnce();
    }
    return result;
  }

  // --- Entrance (once per session) ----------------------------------------
  // Rotates through the entrance bucket ACROSS sessions via a localStorage
  // index (not the sessionStorage seen-set, which resets every session --
  // entrance only ever fires once per session anyway, so the seen-set
  // would never block a repeat within a session even if it were used
  // here). Late-night window (23:00-05:59, per this section's own spec
  // line -- deliberately not unified with world.late_night's slightly
  // different 23-05 boundary above, since the two are separately
  // specified) substitutes entrance_late_night if it has an unseen line.

  var STORAGE_ENTRANCE_IDX = 'evaBoltyEntranceIdx';
  var STORAGE_ENTRANCE_LATE_IDX = 'evaBoltyEntranceLateIdx';

  function nextRotatingIndex(storageKey, bucketLen) {
    if (!bucketLen) { return -1; }
    var raw = null;
    try { raw = localStorage.getItem(storageKey); } catch (e) {}
    var idx = ((raw !== null ? parseInt(raw, 10) : -1) + 1) % bucketLen;
    try { localStorage.setItem(storageKey, String(idx)); } catch (e) {}
    return idx;
  }

  function isEntranceLateNightWindow() {
    var h = new Date().getHours();
    return h >= 23 || h < 6;
  }

  function pickEntranceLine() {
    if (!linesReady()) { return null; }
    if (isEntranceLateNightWindow()) {
      var lateIds = BUCKET_IDS.entrance_late_night || [];
      if (lateIds.length) {
        var lateIdx = nextRotatingIndex(STORAGE_ENTRANCE_LATE_IDX, lateIds.length);
        var lateId = lateIds[lateIdx];
        if (!isLineSeen(lateId)) {
          logRung('entrance', 'entrance_late_night', lateId);
          return lineFromId(lateId);
        }
      }
    }
    // service folded into the entrance rotation (this pass) -- one
    // combined pool, one shared rotating index, so a returning visitor
    // sometimes gets one of Per's assistant greetings instead of a plain
    // "Whatsgoodie". Still exactly one line, still once per session --
    // only the pool the rotation draws from got wider.
    var ids = (BUCKET_IDS.entrance || []).concat(BUCKET_IDS.service || []);
    if (!ids.length) { return null; }
    var idx = nextRotatingIndex(STORAGE_ENTRANCE_IDX, ids.length);
    var id = ids[idx];
    logRung('entrance', id.indexOf('service.') === 0 ? 'service' : 'entrance', id);
    return lineFromId(id);
  }

  // --- Drag / hide (own buckets, no cadence interaction) ------------------

  function pickDragLine() {
    var p = pickUnseenFromBucket('drag');
    if (p) { logRung('drag', 'drag', p.id); }
    return p;
  }

  function pickHideLine() {
    var p = pickUnseenFromBucket('hide');
    if (p) { logRung('hide', 'hide', p.id); }
    return p;
  }

  // --- Gift-card nudge (spontaneous trigger, 2026-08-02) -------------------
  // Once per session: a visitor who's been browsing a while, looked at
  // several pages, and still has an empty cart gets a one-off nudge toward
  // the gift card. Checked from the SAME ~4min timer tick and
  // speakAfterDelay notice-you delay every other spontaneous line uses
  // (see pickSpontaneousOrGiftNudge/scheduleTalk below) rather than firing
  // the instant its conditions become true -- "normal cadence rules apply"
  // is the whole point, not a special-cased popup.
  //
  // NOTE: GIFT_CARD_URL's product (handle "giftcard") is currently DRAFT
  // in Shopify admin as of this writing -- the link will 404 for real
  // visitors until Per publishes it himself. Product status is out of
  // scope for this session (see CLAUDE.md's product-scope rule) and is
  // NOT touched here; flagging rather than guessing at a fix.
  var GIFT_CARD_URL = '/products/giftcard';
  var GIFT_NUDGE_MIN_SESSION_MS = 6 * 60 * 1000; // ">6 minutes"
  var GIFT_NUDGE_MIN_PAGE_VIEWS = 4;              // "4+ page views"
  var STORAGE_GIFT_NUDGE_SHOWN = 'evaBoltyGiftNudgeShown';

  // cart-has-items (layout/theme.liquid's own body class, kept fresh across
  // navigations by eva-pjax.js) covers the common case; the in-session
  // cartHasItemsThisSession flag (set by triggerStoked, above) covers the
  // gap right after a successful AJAX add-to-cart with no navigation
  // since, which the server-rendered class alone wouldn't reflect yet.
  function isCartEmpty() {
    if (cartHasItemsThisSession) { return false; }
    return !document.body.classList.contains('cart-has-items');
  }

  function isGiftCardNudgeEligible() {
    if (sGet(STORAGE_GIFT_NUDGE_SHOWN)) { return false; } // once per session
    if (isBalloonVisible) { return false; }                // "not while balloon open"
    if (isCartOrCheckoutPath()) { return false; }           // "not on cart/checkout"
    if ((Date.now() - sessionStartTime()) <= GIFT_NUDGE_MIN_SESSION_MS) { return false; }
    if (pageViewCount() < GIFT_NUDGE_MIN_PAGE_VIEWS) { return false; }
    if (!isCartEmpty()) { return false; }
    return true;
  }

  // Renders via showTipWithLink (text + a real link + Cancel), the same
  // mechanism the entrance menu's tip screens use (showSetupPartScreen,
  // showLearnAboutEvaContent below) -- an actionable link deserves the
  // persistent chrome (stays open until dismissed), not an 8s
  // auto-dismissing plain line the visitor might not have time to click.
  function showGiftCardNudge() {
    sSet(STORAGE_GIFT_NUDGE_SHOWN, '1');
    showTipWithLink("can't decide? a gift card lets the skater decide themselves..", 'grab a gift card', GIFT_CARD_URL);
  }

  // Slots ahead of the normal ladder inside the SAME speakAfterDelay call
  // scheduleTalk already makes -- when eligible this renders the nudge
  // itself (via showTipWithLink, not the plain-text pick shape) and
  // returns null, so the speak(pickFn()) at the end of speakAfterDelay is
  // a harmless no-op (speak() already treats a falsy pick as nothing to
  // say). Otherwise falls straight through to the normal ladder, unchanged.
  function pickSpontaneousOrGiftNudge() {
    if (isGiftCardNudgeEligible()) {
      showGiftCardNudge();
      return null;
    }
    return pickSpontaneousLadder();
  }

  var talkTimer = null;
  var TALK_INTERVAL_MS = 4 * 60 * 1000;

  function scheduleTalk() {
    if (talkTimer) { clearTimeout(talkTimer); }
    talkTimer = setTimeout(function () {
      // isSpontaneousSuppressed(): "Just chill" (10min).
      // isAskPanelOpen: "while the input is open: no spontaneous lines"
      // (ask-Bolty spec) -- a spontaneous line popping in over the ask
      // panel would blow away whatever the visitor was mid-typing.
      // isQuietObserverPath(): /cart gets ONLY the cart-event reactions
      // (see onCartActivity above), never the spontaneous ladder/nudge.
      // isBoltyEngaged(): once interacted with, no more proactive balloons
      // for the rest of the session (invitation-only from here on).
      if (isVisible() && !isResting && !dragging && !isSpontaneousSuppressed() && !isAskPanelOpen && !isQuietObserverPath() && !isBoltyEngaged()) {
        speakAfterDelay(pickSpontaneousOrGiftNudge);
      }
      scheduleTalk();
    }, TALK_INTERVAL_MS);
  }

  // --- Balloon -------------------------------------------------------

  var rootEl, balloonEl;

  // --- Balloon anchoring ---------------------------------------------------
  // Positions the balloon against his LIVE, current on-screen rect (drag
  // position included) every time it's called -- never a fixed CSS
  // offset. hitAreaEl is the same "real visible pixels, not the padded
  // stage box" rect drag/clamp already uses (computeAlphaBBox), so "the
  // top of the visible character (not the padded box)" falls out of
  // reusing it rather than needing new geometry.
  var BALLOON_GAP_PX = 14;         // speech tail: clear gap between balloon and his head
  var BALLOON_THINK_GAP_PX = 34;   // thought trail needs more room to step down in
  var BALLOON_VIEWPORT_MARGIN_PX = 8;  // never flush against a viewport edge
  var BALLOON_TAIL_INSET_PX = 20;      // keep the tail/trail attachment inside the rounded body, off the corners

  function positionBalloon() {
    if (!balloonEl || !hitAreaEl) { return; }
    var hitRect = hitAreaEl.getBoundingClientRect();
    if (!hitRect.width || !hitRect.height) { return; } // alpha-bbox not measured yet -- CSS fallback offset holds until the next call

    var headX = hitRect.left + hitRect.width / 2;
    var headTopY = hitRect.top;
    var headBottomY = hitRect.bottom;

    var isThink = balloonEl.classList.contains('bolty-balloon--think');
    var gap = isThink ? BALLOON_THINK_GAP_PX : BALLOON_GAP_PX;

    var balloonWidth = balloonEl.offsetWidth || 240;
    var balloonHeight = balloonEl.offsetHeight || 0;
    var vw = window.innerWidth;
    // Ask-Bolty mobile pass: window.innerHeight is the LAYOUT viewport and
    // typically does NOT shrink when a mobile on-screen keyboard opens
    // (the keyboard just overlays it), while window.visualViewport.height
    // does -- using the latter when available is what actually lets the
    // bottom clamp below react to the keyboard instead of measuring a
    // viewport that looks unchanged to JS.
    var vh = (window.visualViewport && window.visualViewport.height) || window.innerHeight;

    // Flip below: no room above him for the balloon + gap + a viewport
    // margin -- render below instead, tail/trail pointing up. Never
    // partly off-screen either direction.
    var flipBelow = (headTopY - gap - balloonHeight) < BALLOON_VIEWPORT_MARGIN_PX;
    var top = flipBelow ? (headBottomY + gap) : (headTopY - gap - balloonHeight);
    // Bottom clamp (ask-Bolty mobile pass) -- previously nothing clamped
    // this edge at all ("a balloon taller than the viewport is a content
    // problem, not a positioning one, and these lines are always short").
    // That stopped being true once the ask panel could grow tall with a
    // revealed answer while a mobile keyboard has shrunk vh out from
    // under it: pull top up just enough to keep the balloon's bottom
    // edge inside the visible (non-keyboard-covered) area, never below
    // BALLOON_VIEWPORT_MARGIN_PX itself.
    var maxTop = vh - BALLOON_VIEWPORT_MARGIN_PX - balloonHeight;
    if (top > maxTop) { top = Math.max(BALLOON_VIEWPORT_MARGIN_PX, maxTop); }

    var left = headX - balloonWidth / 2;
    var minLeft = BALLOON_VIEWPORT_MARGIN_PX;
    var maxLeft = Math.max(minLeft, vw - BALLOON_VIEWPORT_MARGIN_PX - balloonWidth);
    left = Math.max(minLeft, Math.min(maxLeft, left));

    // Attachment point in balloon-local X, clamped off the rounded
    // corners -- "tail may attach off-center" when the balloon has been
    // slid sideways to stay on screen.
    var anchorX = headX - left;
    anchorX = Math.max(BALLOON_TAIL_INSET_PX, Math.min(balloonWidth - BALLOON_TAIL_INSET_PX, anchorX));

    balloonEl.style.left = left + 'px';
    balloonEl.style.top = top + 'px';
    balloonEl.style.right = 'auto';
    balloonEl.style.bottom = 'auto';
    balloonEl.style.setProperty('--bolty-tail-x', anchorX + 'px');
    balloonEl.classList.toggle('bolty-balloon--below', flipBelow);

    if (isThink) { positionTrailDots(anchorX, balloonHeight, flipBelow); }
  }

  // Steps TRAIL_DOT_COUNT circles from just past the bubble's edge to
  // just short of his head, largest-to-smallest, all sharing the tail's
  // own anchorX -- "the trailing circles take the tail's role," same
  // single connection point, just drawn as a stepped trail instead of
  // one triangle. Coordinates are balloon-local (bottom/top negative
  // offsets, like the CSS tail's own bottom:-14px convention) so they
  // move for free with the balloon's own live left/top.
  function positionTrailDots(anchorX, balloonHeight, flipBelow) {
    var dots = balloonEl.querySelectorAll('.bolty-balloon__trail-dot');
    if (!dots.length) { return; }
    var startGap = 4, endGap = BALLOON_THINK_GAP_PX - 4;
    dots.forEach(function (dot, i) {
      var t = dots.length > 1 ? i / (dots.length - 1) : 0;
      var size = TRAIL_DOT_SIZES[i] || TRAIL_DOT_SIZES[TRAIL_DOT_SIZES.length - 1];
      var offset = lerp(startGap, endGap, t);
      dot.style.width = size + 'px';
      dot.style.height = size + 'px';
      dot.style.left = (anchorX - size / 2) + 'px';
      if (flipBelow) {
        dot.style.bottom = 'auto';
        dot.style.top = (-offset - size) + 'px';
      } else {
        dot.style.top = 'auto';
        dot.style.bottom = (-offset - size) + 'px';
      }
    });
  }

  var TRAIL_DOT_COUNT = 3;
  var TRAIL_DOT_SIZES = [12, 9, 6]; // largest nearest the bubble, smallest nearest his head

  // bubbleType: BUBBLE_SPEAK (the balloon + triangular tail, default) or
  // BUBBLE_THINK (cloud + circle trail). Thoughts never carry options --
  // "if a flow needs options it is speech by definition," enforced here
  // rather than trusted from the caller, so a future line-pairing bug
  // can't accidentally hand a think bubble a radio-bullet list it has no
  // markup for.
  function showBalloonLine(text, options, bubbleType) {
    if (!balloonEl) { return; }
    var isThink = bubbleType === BUBBLE_THINK && !(options && options.length);

    var html = '<button type="button" class="bolty-balloon__dismiss" aria-label="Dismiss">✕</button>';
    // Text paragraph is conditional (ask-Bolty pass) -- the click-to-
    // reopen menu (showHelpMenu(false)) passes an empty string for "just
    // the options, no greeting," and an empty-but-present <p> would still
    // draw a blank line of height above the list. Every other caller
    // still passes real text, so this is a no-op change for them.
    if (text) { html += '<p class="bolty-balloon__text"></p>'; }
    if (options && options.length) {
      html += '<ul class="bolty-balloon__options">';
      options.forEach(function (opt, i) {
        html += '<li class="bolty-balloon__option" data-opt="' + i + '"><span class="bolty-balloon__bullet"></span><span class="bolty-balloon__option-text"></span></li>';
      });
      html += '</ul><hr class="bolty-balloon__sep">';
    }
    if (isThink) {
      // Scallops: static decoration, not repositioned per line (see
      // bolty.css's own comment). Trail dots: real elements so
      // positionBalloon() can set their size/position live, same as the
      // tail's --bolty-tail-x custom property does for speech.
      html += '<span class="bolty-balloon__scallop bolty-balloon__scallop--tl"></span>';
      html += '<span class="bolty-balloon__scallop bolty-balloon__scallop--tr"></span>';
      html += '<span class="bolty-balloon__scallop bolty-balloon__scallop--r"></span>';
      for (var d = 0; d < TRAIL_DOT_COUNT; d++) {
        html += '<span class="bolty-balloon__trail-dot" data-trail="' + d + '"></span>';
      }
    } else {
      // Cancel revived (2026-08-01, ask-Bolty spec: "restores the
      // original locked Office-Assistant format... separator, Cancel").
      // Matches the pre-balloon-anchoring-pass markup exactly (git show
      // 2f392d4^:assets/bolty.js) -- unconditional on every speech
      // balloon, not just when options are present, same as it was
      // before that pass removed it. Thoughts never get one: they're
      // ambient/overheard, not a flow with a decision to back out of,
      // and the scalloped shape has no chrome for it.
      html += '<div class="bolty-balloon__cancel-row"><button type="button" class="bolty-balloon__cancel">Cancel</button></div>';
    }
    balloonEl.innerHTML = html;
    balloonEl.classList.toggle('bolty-balloon--think', isThink);
    var textEl = balloonEl.querySelector('.bolty-balloon__text');
    if (textEl) { textEl.textContent = text; }
    if (options && options.length) {
      var items = balloonEl.querySelectorAll('.bolty-balloon__option-text');
      options.forEach(function (opt, i) { items[i].textContent = opt.label; });
      // Each option's onSelect owns whether the balloon stays open (e.g.
      // "Get help" swaps in a tip via showTipWithLink, "Ask me
      // something.." swaps in the ask panel via showAskPanel) or closes
      // (onSelect calls hideBalloon itself) -- this handler must NOT
      // also call hideBalloon() after onSelect(), which would immediately
      // undo whatever new content onSelect just showed.
      balloonEl.querySelectorAll('.bolty-balloon__option').forEach(function (el, i) {
        el.addEventListener('click', function () {
          if (options[i].onSelect) { options[i].onSelect(); }
        });
      });
    }
    // The corner X and Cancel both just close the balloon -- Bolty stays.
    // The only way to hide Bolty himself is the minimize button or the
    // header icon (hideBoltyNow/toggleBoltyVisibility), never this chrome.
    balloonEl.querySelector('.bolty-balloon__dismiss').addEventListener('click', hideBalloon);
    var cancelBtn = balloonEl.querySelector('.bolty-balloon__cancel');
    if (cancelBtn) { cancelBtn.addEventListener('click', hideBalloon); }
    balloonEl.classList.add('is-open');
    isBalloonVisible = true; // -> talk emotion (see currentEmotion)

    // Anchor to his LIVE position now that the balloon has real content/
    // size to measure (offsetHeight is 0 before .is-open makes it
    // display:block, which the class toggle above already did
    // synchronously) -- see positionBalloon's own comment.
    positionBalloon();

    // Auto-hide a plain spontaneous line (no options) after a while so it
    // doesn't sit open forever -- entrance/explicit-option balloons stay
    // open until the visitor acts.
    if (!options) {
      setTimeout(function () {
        if (balloonEl.querySelector('.bolty-balloon__text') && balloonEl.querySelector('.bolty-balloon__text').textContent === text) {
          hideBalloon();
        }
      }, 8000);
    }
  }

  function hideBalloon() {
    if (balloonEl) { balloonEl.classList.remove('is-open'); }
    if (isBalloonVisible) {
      isBalloonVisible = false;
      talkGraceUntil = Date.now() + TALK_GRACE_MS; // grin lingers a beat after closing
    }
    // Ask-Bolty cleanup: closing the balloon (X, Cancel, or a fresh
    // showBalloonLine/showTipWithLink/showAskPanel replacing its content)
    // always tears down any in-flight ask state. Bumping askRequestToken
    // invalidates a still-pending fetch/POST so a late response can't pop
    // an answer into a balloon the visitor already dismissed or reused
    // for something else.
    isAskPanelOpen = false;
    isAskThinking = false;
    isAskAnswering = false;
    askMouthOverride = null;
    askRequestToken++;
    if (window.visualViewport) { window.visualViewport.removeEventListener('resize', repositionForKeyboard); }
  }

  // --- Entrance menu (2026-08-01, rebuilt to 4 items 2026-08-10) ----------
  // Bolty's canonical balloon, shown on entrance (rotating greeting line +
  // "Would you like help?" + the options) and again, options-only with no
  // greeting, any time the visitor clicks him while idle. Four top-level
  // choices, exact order: "Buy something" (the step-by-step setup
  // wizard -- showBuySetupMenu below), "Learn about Eva" (brand lore --
  // showLearnAboutEvaContent), "Entertainment" (Kallare/Radio --
  // showEntertainmentMenu), "Just chill" (closes the balloon, same
  // suppress-window the old "Just browse without help" option used).
  // "Ask me something.." has no top-level slot in the 4-item list per
  // spec -- it's nested as a closing option inside the "Buy something"
  // and "Learn about Eva" branches instead (gated on askBoltyEnabled()
  // same as before). See buildEntranceMenuOptions/showHelpMenu below for
  // the top-level wiring; the ask flow itself (thinking state, predictive
  // search, worker POST, type-out reveal) lives further down in its own
  // section.

  var HARDWARE_COLLECTION_URL = '/collections/hardware';
  var KALLARE_URL = '/pages/evas-kallare';

  // "Learn about Eva" destination. STOPGAP (2026-08-24, Per's call): the
  // real story page /pages/eva-4-eva still 404s -- it exists as a Page
  // object ("EVA 4 EVER", eva-story template) but isPublished:false, so
  // the link was pulled entirely in ce6f09e. This points at the EVA
  // collection instead so the branch has a live destination again; swap
  // back to the story page once Per publishes it. Label deliberately
  // follows the existing "Take me to hardware" pattern rather than
  // reusing 'Read our story', which described a story page and would be
  // wrong for a collection.
  var EVA_COLLECTION_URL = '/collections/eva';
  var EVA_COLLECTION_LABEL = 'take me to the eva collection';
  var BROWSE_SUPPRESS_MS = 10 * 60 * 1000; // "Just chill" (nee "Just browse without help")

  var STORAGE_SUPPRESS_UNTIL = 'evaBoltySuppressUntil';

  function suppressSpontaneousFor(ms) {
    try { sessionStorage.setItem(STORAGE_SUPPRESS_UNTIL, String(Date.now() + ms)); } catch (e) {}
  }

  function isSpontaneousSuppressed() {
    try {
      var until = parseInt(sessionStorage.getItem(STORAGE_SUPPRESS_UNTIL) || '0', 10);
      return Date.now() < until;
    } catch (e) { return false; }
  }

  // --- Engaged quiet period (2026-08-02, made decaying 2026-08-21) ----------
  // Once a visitor has interacted with the balloon -- asked a question, or
  // chosen ANY of the four menu options (including "Just chill," which is
  // itself a real choice, not a non-interaction) -- he stops opening a
  // balloon on his own for ENGAGED_QUIET_MS: no spontaneous ladder, no
  // gift-card nudge.
  //
  // It shipped as a permanent per-session boolean latch and that was the
  // bug: nothing ever cleared it, and since the 4-item menu (8d28fa2) the
  // ONLY route to the ask box runs through options that set it first, so
  // using the ask box silenced every ambient bubble for the rest of the
  // browser session. Now it stores a TIMESTAMP and decays.
  //
  // The clock RESTARTS on every interaction -- markBoltyEngaged() always
  // overwrites with now, so this is a quiet-period timer measured from the
  // LAST thing the visitor did, not a countdown from first contact. He must
  // never interrupt someone who is still clicking or typing.
  //
  // Explicitly does NOT cover: clicking him (still opens the menu --
  // "invitation only," per spec, is the whole point) or the /cart
  // quiet-observer's own cart-event reactions (that spec's own text keeps
  // those alive under engaged too), and isQuietObserverPath() still silences
  // him in the cart regardless of this timer. Session-scoped only --
  // sessionStorage, never localStorage.
  var STORAGE_ENGAGED = 'evaBoltyEngaged';

  function markBoltyEngaged() {
    try { sessionStorage.setItem(STORAGE_ENGAGED, String(Date.now())); } catch (e) {}
  }

  function isBoltyEngaged() {
    try {
      var raw = sessionStorage.getItem(STORAGE_ENGAGED);
      if (!raw) { return false; }
      var at = parseInt(raw, 10);
      // Migration: a session that started before this change holds the old
      // boolean '1'. parseInt('1') is a valid number, so it reads as a
      // timestamp 1ms after the epoch -- an elapsed time of decades, i.e.
      // long expired, which is exactly the safe reading. Any other
      // non-numeric junk (NaN) is treated as expired too: an unparseable
      // value must never mean "engaged forever," that was the whole bug.
      if (!isFinite(at)) { return false; }
      var elapsed = Date.now() - at;
      // A negative elapsed means the clock moved backwards (skew, DST, a
      // manually-set clock). Treat it as expired rather than engaged --
      // erring toward silence here could re-create the permanent-mute bug.
      if (elapsed < 0) { return false; }
      return elapsed < ENGAGED_QUIET_MS;
    } catch (e) { return false; }
  }

  // --- "Buy something" setup wizard (2026-08-10, routes corrected 2026-08-10) --
  // Six real parts, in the order you'd actually build a board: griptape
  // goes on the deck before anything else touches it, then trucks bolt on
  // (hardware), then wheels/bearings go on the trucks last. Every url is a
  // real, curated COLLECTION, live-checked (HTTP 200, non-empty, real
  // product content confirmed) against this draft theme -- never a
  // /search?q= fallback, which was the original ship's bug: text search
  // cross-matches unrelated product copy (e.g. wheels product descriptions
  // mentioning bearings) instead of returning a curated, on-topic result
  // set. Collection choices:
  //   deck     -> eva-brador ("Eva brador" = Swedish for Eva boards), the
  //               actual curated Eva-brand decks collection -- NOT the
  //               generic skateboards collection, which mixes completes/
  //               decks/other brands and isn't "the Eva decks collection."
  //   griptape -> griptape (2026-08-24: a real dedicated GRIPTAPE
  //               collection now exists, 2 products -- Jessup and MOB.
  //               Was mob-griptape, a 1-product brand collection chosen
  //               only because no broader griptape collection existed
  //               then; it does now, so the brand collection is no longer
  //               the closest real target).
  //   bearings -> bearings (2026-08-24: a real dedicated Bearings
  //               collection now exists, 8 storefront products. Was skf,
  //               the brand collection, chosen for the same "closest thing
  //               available" reason. The bearings collection is broader:
  //               it holds the same 6 SKF listings PLUS two Bronson
  //               bearing-CARE items, which is a superset, not a
  //               mismatch).
  //   trucks/hardware/wheels -> unchanged, already real dedicated
  //               collections (also in BOLTY_COLLECTIONS above).
  var SETUP_PARTS = [
    { key: 'deck', noun: 'decks', url: '/collections/eva-brador' },
    { key: 'griptape', noun: 'griptape', url: '/collections/griptape' },
    { key: 'trucks', noun: 'trucks', url: '/collections/trucks' },
    { key: 'hardware', noun: 'hardware', url: HARDWARE_COLLECTION_URL },
    { key: 'wheels', noun: 'wheels', url: '/collections/wheels' },
    { key: 'bearings', noun: 'bearings', url: '/collections/bearings' }
  ];

  // Per part: a random unseen line from that part's own context.product_*
  // bucket (assets/bolty-lines.json already has one per part -- product_
  // deck/trucks/wheels/bearings/hardware/griptape, 26 lines total, none of
  // it invented here). Falls back to a short generic line per part if
  // lines haven't loaded or that part's bucket is exhausted for the
  // session -- always shows SOMETHING. Hardware's fallback matches the
  // line the old "Get help picking hardware" tip used to fall back to.
  var SETUP_PART_FALLBACKS = {
    deck: "Every setup starts with the deck, man.",
    griptape: "Grip it before anything else goes on.",
    trucks: "Trucks mount to the deck -- match the width close.",
    hardware: "Eight bolts per board, man. People forget. Eight of us.",
    wheels: "Wheels next -- softer for cruising, harder for tricks.",
    bearings: "Bearings are what actually let the wheels spin."
  };

  function pickPartTip(key) {
    var pick = pickUnseenFromBucket('context.product_' + key);
    if (pick) {
      markLineSeen(pick.id);
      return pick.t;
    }
    return SETUP_PART_FALLBACKS[key];
  }

  function partByKey(key) {
    for (var i = 0; i < SETUP_PARTS.length; i++) {
      if (SETUP_PARTS[i].key === key) { return SETUP_PARTS[i]; }
    }
    return null;
  }

  // One part's screen -- reused by both the picker (jumps straight to a
  // single part, no Next) and the walkthrough (chains through all six via
  // the Next button). withNext: true wires "Next ->" to the following
  // part in SETUP_PARTS order; false/omitted means this is a standalone
  // pick (picker) or the walkthrough's last step, where "Ask me
  // something.." takes the second button slot instead (closing-line nest,
  // per spec -- only when askBoltyAvailable()).
  function showSetupPartScreen(key, withNext) {
    var part = partByKey(key);
    if (!part) { return; }
    var actions = [];
    if (withNext) {
      var idx = SETUP_PARTS.indexOf(part);
      var next = SETUP_PARTS[idx + 1];
      if (next) {
        actions.push({ label: 'Next →', onClick: function () { showSetupPartScreen(next.key, true); } });
      } else if (askBoltyAvailable()) {
        actions.push({ label: 'Ask me something..', onClick: showAskPanel });
      }
    }
    // The wizard can't know which deck the visitor picked, so the griptape
    // step keeps its link and gains the Eva caveat instead of branching.
    var tip = pickPartTip(part.key);
    if (part.key === 'griptape') { tip += EVA_GRIP_WIZARD_CAVEAT; }
    showTipWithLinkAndActions(tip, 'take me to the ' + part.noun, part.url, actions);
  }

  // "I just need one thing" -- direct picker, one radio-bullet per part,
  // straight to that part's tip+link (no walkthrough chaining).
  function showSetupPartPicker() {
    var options = SETUP_PARTS.map(function (part) {
      return { label: part.key.charAt(0).toUpperCase() + part.key.slice(1), onSelect: function () { showSetupPartScreen(part.key, false); } };
    });
    showBalloonLine('', options, BUBBLE_SPEAK);
  }

  // "Buy something" branch entry -- two ways in, plus the nested
  // "Ask me something.." closing option per spec (only branches 1 and 2
  // get it; Entertainment/Just chill don't).
  // --- "Buy something" branching tree (2026-08-25, Per's design) -----------
  // Replaces the old two-option setup menu. Shape:
  //   Buy something -> "what are you after?"
  //     Skateboard -> "complete setup, or just a deck?"
  //         Complete  -> the build flow, with THE CART AS THE STATE
  //         Just a deck -> plain deck links, no build framing at all
  //     Trucks / Wheels / Clothing -> that category's links, and NOTHING else
  //     Something else -> point at the search
  var BUY_CATEGORY_LINKS = {
    trucks: { noun: 'trucks', url: '/collections/trucks' },
    wheels: { noun: 'wheels', url: '/collections/wheels' },
    clothing: { noun: 'clothing', url: '/collections/apparel' },
    deck: { noun: 'decks', url: '/collections/eva-brador' }
  };

  function showBuySetupMenu() {
    var options = [
      { label: 'Skateboard', onSelect: showSkateboardChoice },
      { label: 'Trucks', onSelect: function () { showBuyCategory('trucks'); } },
      { label: 'Wheels', onSelect: function () { showBuyCategory('wheels'); } },
      { label: 'Clothing', onSelect: function () { showBuyCategory('clothing'); } },
      { label: 'Something else', onSelect: showSomethingElse }
    ];
    if (askBoltyAvailable()) {
      options.push({ label: 'Ask me something..', onSelect: showAskPanel });
    }
    showBalloonLine('what are you after?', options, BUBBLE_SPEAK);
  }

  function showSkateboardChoice() {
    var options = [
      { label: 'Complete setup', onSelect: startBuildFlow },
      { label: 'Just a deck', onSelect: function () { showBuyCategory('deck'); } }
    ];
    showBalloonLine('complete setup, or just a deck?', options, BUBBLE_SPEAK);
  }

  // Trucks / Wheels / Clothing / just-a-deck. Category links ONLY -- this path
  // deliberately does NOT read the cart and must never mention a build. Per
  // was explicit: if someone lands here with a deck already in their cart
  // from an earlier visit, Bolty says nothing about it. Silent by design.
  function showBuyCategory(key) {
    var cat = BUY_CATEGORY_LINKS[key];
    if (!cat) { return; }
    var tip = pickPartTip(key === 'clothing' ? 'apparel' : key) || '';
    showTipWithLinkAndActions(tip, 'take me to the ' + cat.noun, cat.url, []);
  }

  // --- THE CART IS THE STATE ------------------------------------------------
  // Before saying anything about what is next, read the live cart. Whatever is
  // already in it counts as DONE, so Bolty only ever answers "what's missing,
  // in build order" and never re-pitches a step the visitor has already
  // covered. This is a read of /cart.js at the moment the visitor asks -- not
  // the session-scoped cart-add chain, which is a different feature with its
  // own once-per-session flags. Nothing here writes any flag.
  var BUILD_ORDER = ['deck', 'griptape', 'trucks', 'wheels', 'bearings', 'hardware'];

  function startBuildFlow() {
    showBalloonLine('let me see what you got..', [], BUBBLE_THINK);
    Promise.all([
      fetchCartState(),
      collectionHandleSet(CART_DECK_COLLECTION),
      collectionHandleSet(CART_EVA_COLLECTION)
    ]).then(function (r) {
      showNextBuildStep(missingBuildSteps(r[0], r[1], r[2]));
    }).catch(function () {
      showNextBuildStep(BUILD_ORDER.slice()); // cart unreadable -- treat as empty rather than guess
    });
  }

  // Returns the build steps still outstanding, in build order.
  function missingBuildSteps(cart, deckSet, evaSet) {
    var items = (cart && cart.items) || [];
    var done = {};
    var hasEvaDeck = false;
    for (var i = 0; i < items.length; i++) {
      var bucket = cartLineCategory(items[i], deckSet);
      if (bucket && bucket.indexOf('product_') === 0) {
        done[bucket.slice('product_'.length)] = true;
      }
      if (isEvaDeckHandle(items[i].handle, deckSet, evaSet)) { hasEvaDeck = true; }
    }
    // Eva decks ship with Jessup grip already on them, so griptape is not a
    // missing step for them -- the same rule the cart-add chain already applies.
    if (hasEvaDeck) { done.griptape = true; }
    return BUILD_ORDER.filter(function (step) { return !done[step]; });
  }

  function showNextBuildStep(missing) {
    if (!missing.length) {
      showBalloonLine("that's a whole setup, man.. you're done.", [], BUBBLE_SPEAK);
      return;
    }
    var key = missing[0];
    var part = partByKey(key) || BUY_CATEGORY_LINKS[key];
    if (!part) { return; }
    var rest = missing.slice(1);
    var actions = [];
    if (rest.length) {
      actions.push({ label: 'Next \u2192', onClick: function () { showNextBuildStep(rest); } });
    } else if (askBoltyAvailable()) {
      actions.push({ label: 'Ask me something..', onClick: showAskPanel });
    }
    var tip = pickPartTip(key) || '';
    if (key === 'griptape') { tip += EVA_GRIP_WIZARD_CAVEAT; }
    showTipWithLinkAndActions(tip, 'take me to the ' + part.noun, part.url, actions);
  }

  // --- "Something else" -> the search ---------------------------------------
  // Copy is Per's own, verbatim from the spec ("use the search, man.."). His
  // line pool has no equivalent instruction line -- context.search holds only
  // reactions to an empty result ("We don't have that.. but perhaps you're a
  // bad speller.") -- so nothing existing could be reused here and nothing was
  // invented either.
  //
  // The picture is the theme's OWN search glyph, the same inline SVG
  // snippets/header-icon-row-mobile.liquid renders for the search button --
  // not a new asset, so it cannot drift from the icon it is pointing at.
  var SEARCH_ICON_SVG = '<svg aria-hidden="true" focusable="false" viewBox="0 0 64 64" class="bolty-balloon__icon-svg">' +
    '<path d="M47.16 28.58A18.58 18.58 0 1 1 28.58 10a18.58 18.58 0 0 1 18.58 18.58ZM54 54 41.94 42"/></svg>';

  function showSomethingElse() {
    // TODO (Stage 2 hands, gated): once the floating hands are approved and
    // live in production, add the POINTING GESTURE here -- Bolty points at the
    // real search icon in the header while this balloon is up. The call goes
    // exactly at this spot, right before showBalloonWithIcon below. Do NOT add
    // it until hands actually ship; there are no hands in production today,
    // which is why this ships as picture-in-balloon only.
    showBalloonWithIcon('use the search, man..', SEARCH_ICON_SVG);
  }

  // Balloon variant that shows a small picture under the line. The icon markup
  // is a trusted in-file constant so innerHTML is safe for it; the TEXT still
  // goes through textContent, same "never innerHTML untrusted text" discipline
  // the rest of this file keeps.
  function showBalloonWithIcon(text, iconMarkup) {
    if (!balloonEl) { return; }
    var html = '<button type="button" class="bolty-balloon__dismiss" aria-label="Dismiss">\u2715</button>';
    html += '<p class="bolty-balloon__text"></p>';
    html += '<div class="bolty-balloon__icon">' + iconMarkup + '</div>';
    html += '<div class="bolty-balloon__cancel-row"><button type="button" class="bolty-balloon__cancel">Cancel</button></div>';
    balloonEl.innerHTML = html;
    balloonEl.classList.remove('bolty-balloon--think');
    balloonEl.querySelector('.bolty-balloon__text').textContent = text;
    var dismiss = function () { hideBalloon(); };
    balloonEl.querySelector('.bolty-balloon__cancel').addEventListener('click', dismiss);
    balloonEl.querySelector('.bolty-balloon__dismiss').addEventListener('click', dismiss);
    balloonEl.classList.add('is-open');
    isBalloonVisible = true;
    positionBalloon();
  }

  // --- "Learn about Eva" lore branch (2026-08-10, link dropped 2026-08-10) --
  // Same scene+gossip pool the spontaneous ladder's "loitering" rung
  // already draws from (both real, Per-authored shop-vibe lines), plus
  // context.home (also real, shop-origin flavor -- "one of few real
  // skateshops left in scandinavia"). Nothing invented here: no separate
  // "brand lore" pool exists in bolty-lines.json, so this reuses the
  // closest existing real material rather than fabricating new copy.
  // Originally also linked to /pages/eva-4-eva as a "read our story" --
  // pulled after a live-route audit found that page 404s (it's a real
  // Page object, "EVA 4 EVER" / eva-story template, but isPublished:false
  // -- still mid-build, several spreads have "[CAPTION TBD]" captions).
  // Per's call: lines only until he actually publishes it, rather than
  // ship a link that 404s or wait on an unpublished page. Renders via
  // showBalloonLine's plain options list (no link markup at all) instead
  // of showTipWithLinkAndActions. "Tell me more" loops another unseen
  // line from the same pool; "Ask me something.." is the nested closing
  // option, same rule as the Buy-something branch.
  function showLearnAboutEvaContent() {
    var pick = linesReady() ? pickUnseenFromAnyBucket(['scene', 'gossip', 'context.home']) : null;
    var text = pick ? pick.t : 'One of few real skateshops left in Scandinavia, man.';
    if (pick) { markLineSeen(pick.id); }
    var actions = [{ label: 'Tell me more', onClick: showLearnAboutEvaContent }];
    if (askBoltyAvailable()) {
      actions.push({ label: 'Ask me something..', onClick: showAskPanel });
    }
    showTipWithLinkAndActions(text, EVA_COLLECTION_LABEL, EVA_COLLECTION_URL, actions);
  }

  // --- "Entertainment" branch (2026-08-10) ---------------------------------
  // Kallare is a real page (/pages/evas-kallare, same URL the header's TV
  // icon already links to -- snippets/header-icon-stack.liquid). Radio has
  // no dedicated page (confirmed elsewhere in this file, contextBucket()'s
  // own 'radio' comment) -- it's a persistent widget toggled by the same
  // .js-eva-radio-toggle button the header's boombox icon already drives,
  // so this clicks that button rather than navigating anywhere. No nested
  // "Ask me something.." here -- spec only nests it in branches 1 and 2.
  function showEntertainmentMenu() {
    var options = [
      { label: "Eva's Kallare", onSelect: function () { window.location.href = KALLARE_URL; } },
      { label: 'Eva Radio', onSelect: function () {
        hideBalloon();
        var toggle = document.querySelector('.js-eva-radio-toggle');
        if (toggle) { toggle.click(); }
      } }
    ];
    showBalloonLine('Kick back for a bit.', options, BUBBLE_SPEAK);
  }

  // Context tip + link -- swaps the balloon body in place (its own
  // dedicated render, not showBalloonLine, since the markup differs: a
  // link instead of an options list, no separator). Matches the original
  // polish-pass shape exactly (git show 8dd162e), rebuilt against the
  // current live-anchored balloon.
  //
  // actions (2026-08-10, entrance-menu rebuild): optional array of extra
  // {label, onClick} buttons rendered in the cancel-row ahead of Cancel --
  // the setup wizard's "Next ->" step-through and the lore branch's "Tell
  // me more"/"Ask me something.." both need a second live action next to
  // the tip+link shape without forking a whole new render function.
  //
  // onDismiss (2026-08-11, cart-add chain): optional callback fired in
  // addition to hideBalloon when the visitor closes this specific balloon
  // via Cancel or the corner X (clicking the link itself doesn't count --
  // that's a real navigation, not a dismissal). Only the griptape->trucks
  // step of the cart-chain uses this ("on griptape add OR once dismissed,
  // recommend trucks" -- the other chain steps are add-only, no dismiss
  // escape hatch, per spec).
  // LABEL RULE (2026-08-24, Per): every link label must NAME WHERE IT GOES.
  // "the realest skate" tells a visitor nothing; "take me to the bearings"
  // does. Labels stay in Bolty's voice -- lowercase, casual -- but the
  // destination has to be in the words. This applies to every label passed
  // in here. NOTE: the ask-box's link label is NOT covered by this -- the
  // worker generates it at runtime from the LINK RULE in its own system
  // prompt, so it can only be changed there, not in this file.
  function showTipWithLinkAndActions(text, linkLabel, href, actions, onDismiss) {
    if (!balloonEl) { return; }
    var html = '<button type="button" class="bolty-balloon__dismiss" aria-label="Dismiss">✕</button>';
    html += '<p class="bolty-balloon__text"></p>';
    html += '<a class="bolty-balloon__link"></a>';
    html += '<div class="bolty-balloon__cancel-row">';
    (actions || []).forEach(function (action, i) {
      html += '<button type="button" class="bolty-balloon__action" data-action="' + i + '"></button>';
    });
    html += '<button type="button" class="bolty-balloon__cancel">Cancel</button></div>';
    balloonEl.innerHTML = html;
    balloonEl.classList.remove('bolty-balloon--think');
    balloonEl.querySelector('.bolty-balloon__text').textContent = text;
    var link = balloonEl.querySelector('.bolty-balloon__link');
    link.textContent = linkLabel;
    link.href = href;
    balloonEl.querySelectorAll('.bolty-balloon__action').forEach(function (el, i) {
      el.textContent = actions[i].label;
      el.addEventListener('click', actions[i].onClick);
    });
    var dismiss = function () { hideBalloon(); if (onDismiss) { onDismiss(); } };
    balloonEl.querySelector('.bolty-balloon__cancel').addEventListener('click', dismiss);
    balloonEl.querySelector('.bolty-balloon__dismiss').addEventListener('click', dismiss);
    balloonEl.classList.add('is-open');
    isBalloonVisible = true;
    positionBalloon();
  }

  function showTipWithLink(text, linkLabel, href) {
    showTipWithLinkAndActions(text, linkLabel, href, []);
  }

  // Shared by Ask Bolty and the gift-card nudge (both explicitly denied on
  // /cart and /checkout) -- renamed from isAskDeniedPath when the nudge
  // started reusing it, since it's no longer ask-specific.
  function isCartOrCheckoutPath() {
    var p = window.location.pathname;
    return p.indexOf('/cart') === 0 || p.indexOf('/checkout') === 0;
  }

  // /checkout stays fully hidden (isDeniedPath, unchanged) -- /cart is the
  // one page Bolty is present on but silenced, per the 2026-08-02 "quiet
  // observer" spec: no entrance, no options menu (not even on click), no
  // spontaneous ladder, no gift-card nudge, but his idle emotions/
  // animations keep running normally. Read live every time, same as every
  // other path check in this file -- never cached, so it's automatically
  // correct across PJAX navigations in and out of /cart with no separate
  // "re-check on nav" step needed.
  function isQuietObserverPath() {
    return window.location.pathname.indexOf('/cart') === 0;
  }

  function askBoltyAvailable() {
    return askBoltyEnabled() && !isCartOrCheckoutPath() && !askCapReached();
  }

  // --- /cart quiet-observer reactions (2026-08-02) --------------------------
  // Bolty's only speech on /cart: real, observed cart-state changes, never
  // the spontaneous ladder/entrance/menu. Exempt from evaBoltyEngaged's
  // proactive-balloon suppression (that flag's own spec explicitly keeps
  // "cart reactions per item 1" alive) since these are direct reactions to
  // the visitor's own action, same category as triggerStoked's reaction
  // elsewhere on the site, not an unprompted line.
  //
  // Reasoned purely off item_count deltas from the shared cart-activity
  // observer above (fetchCartState/onCartActivity) -- no per-line-item
  // tracking needed: an increase is "quantity increased or item added"
  // (one reaction either way, per spec), a decrease to exactly 0 is "cart
  // emptied," and any other decrease is "item removed" (also covers a
  // quantity decrease that doesn't zero the cart -- the spec draws no
  // separate case for that, so it reads naturally as "removed" too).
  var CART_OBSERVER_LINES = {
    removed: ["aw.. you didn't want it?", "gone. like everything eventually"],
    increased: ["that's the spirit"],
    emptied: ["empty. we've all been there, man"],
    oneItemNudge: "just one thing? you can do better than that"
  };
  var CART_OBSERVER_COOLDOWN_MS = 20000; // "never two in a row within 20s"
  var CART_ONE_ITEM_DELAY_MS = 30000;    // "nothing changes for 30s"
  var STORAGE_ONE_ITEM_NUDGE_SHOWN = 'evaBoltyCartOneItemNudgeShown';

  var lastCartObserverLineAt = 0;
  var oneItemTimer = null;

  function canFireCartObserverLine() {
    return (Date.now() - lastCartObserverLineAt) >= CART_OBSERVER_COOLDOWN_MS;
  }

  function pickOne(arr) { return arr[Math.floor(Math.random() * arr.length)]; }

  function speakCartObserverLine(text) {
    lastCartObserverLineAt = Date.now();
    if (!isVisible()) { return; }
    showBalloonLine(text, null, BUBBLE_SPEAK);
  }

  // Re-armed on every real count change (any change at all, not just to/
  // from 1) -- "cart has 1 item and nothing changes for 30s" means a
  // continuous 30s window with the count sitting still at exactly 1, so
  // any other change during that window has to reset the clock.
  function maybeStartOneItemTimer(count) {
    if (oneItemTimer) { clearTimeout(oneItemTimer); oneItemTimer = null; }
    if (count !== 1 || sGet(STORAGE_ONE_ITEM_NUDGE_SHOWN)) { return; }
    oneItemTimer = setTimeout(function () {
      oneItemTimer = null;
      if (!isQuietObserverPath() || lastKnownCartItemCount !== 1) { return; } // re-verify nothing changed while the timer was running
      if (sGet(STORAGE_ONE_ITEM_NUDGE_SHOWN) || !canFireCartObserverLine()) { return; }
      sSet(STORAGE_ONE_ITEM_NUDGE_SHOWN, '1');
      speakCartObserverLine(CART_OBSERVER_LINES.oneItemNudge);
    }, CART_ONE_ITEM_DELAY_MS);
  }

  // oldCount === null means this is the first read since a page load/nav
  // (nothing to diff against) -- just seed the baseline and check whether
  // to arm the one-item timer for a cart that ALREADY sat at 1 item when
  // the visitor arrived, without treating arrival itself as a "change."
  onCartActivity(function (newCount, oldCount) {
    maybeStartOneItemTimer(newCount);
    if (oldCount === null || newCount === oldCount) { return; }
    if (!isQuietObserverPath() || !canFireCartObserverLine()) { return; }
    if (newCount === 0) {
      speakCartObserverLine(pickOne(CART_OBSERVER_LINES.emptied));
    } else if (newCount > oldCount) {
      speakCartObserverLine(pickOne(CART_OBSERVER_LINES.increased));
    } else {
      speakCartObserverLine(pickOne(CART_OBSERVER_LINES.removed));
    }
  });

  function buildEntranceMenuOptions() {
    // Every option marks the engaged flag -- "chosen ANY balloon option,"
    // no exceptions (including "Just chill," which is a real choice, not
    // a non-interaction). "Ask me something.." has no slot here -- it's
    // nested inside the Buy-something branch instead, see showBuySetupMenu.
    //
    // THREE items since 2026-08-25 (Per): "Entertainment" was renamed to
    // "Show me around" -- label only, it still opens the same
    // showEntertainmentMenu (Kallare + Eva Radio), destination untouched --
    // and "Learn about Eva" was dropped outright, with no replacement and no
    // redirect. showLearnAboutEvaContent() and EVA_COLLECTION_URL are left in
    // the file untouched but now have NO caller; see the report.
    return [
      { label: 'Buy something', onSelect: function () {
        markBoltyEngaged();
        showBuySetupMenu();
      } },
      { label: 'Show me around', onSelect: function () {
        markBoltyEngaged();
        showEntertainmentMenu();
      } },
      { label: 'Just chill', onSelect: function () {
        markBoltyEngaged();
        suppressSpontaneousFor(BROWSE_SUPPRESS_MS);
        hideBalloon();
      } }
    ];
  }

  // withGreeting: true for the once-per-session entrance (rotating
  // greeting line, same pool/rotation pickEntranceLine already drove
  // before this pass, + " Would you like help?" + the options); false
  // for every later click-to-reopen (options only, no text line at all --
  // showBalloonLine's own text-is-conditional change makes that a clean
  // empty <p>-less balloon rather than an odd blank line above the list).
  function showHelpMenu(withGreeting) {
    var text = '';
    var bubble = BUBBLE_SPEAK;
    var anim = null;
    if (withGreeting) {
      var pick = linesReady() ? pickEntranceLine() : null;
      if (pick) {
        markLineSeen(pick.id);
        if (isBoltyDevMode()) { console.debug('[bolty] bubble=' + pick.bubble + ' (' + pick.bubbleSource + ')'); }
        text = pick.t + ' Would you like help?';
        bubble = pick.bubble;
        anim = pick.anim;
      } else {
        text = 'Would you like help?'; // lines never loaded -- still show the menu, just without a greeting prefix
      }
    }
    showBalloonLine(text, buildEntranceMenuOptions(), bubble);
    if (anim) { playLineAnim(anim); }
  }

  // Same delay/blink pattern speakAfterDelay uses (BOLTY_SPEAK_DELAY_MIN/
  // MAX_MS, forced blink right as the countdown starts) for the one-time
  // entrance menu specifically -- kept as its own small function rather
  // than reusing speakAfterDelay itself, since that helper always routes
  // its result through speak()'s plain showBalloonLine(text, null, ...)
  // call and has no way to carry an options array through. Shares
  // pendingDelayedSpeakTimer/cancelPendingDelayedSpeak with speakAfterDelay
  // so a page change (eva:pjax:loaded) correctly cancels a still-pending
  // entrance menu too, exactly as it would a pending spontaneous line.
  function delayedEntranceMenu() {
    cancelPendingDelayedSpeak();
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        blinkUntilTick = tickCount + BLINK_TICKS;
        var delay = BOLTY_SPEAK_DELAY_MIN_MS + Math.random() * (BOLTY_SPEAK_DELAY_MAX_MS - BOLTY_SPEAK_DELAY_MIN_MS);
        pendingDelayedSpeakTimer = setTimeout(function () {
          pendingDelayedSpeakTimer = null;
          showHelpMenu(true);
        }, delay);
      });
    });
  }

  // --- Ask Bolty: real Q&A in the balloon (2026-08-01) ---------------------
  // Third help-menu option. Flow: submit -> EMOTION_THINKING (pupils up,
  // closed-mouth smile, balloon shows a stepped-pulsing "..") -> fetch
  // this shop's own predictive search for lightweight product context ->
  // POST {question, products, collections, page:{title}} to BOLTY_ASK_URL
  // (8s timeout) -> mouth-synced type-out reveal of the answer, settling
  // to a smile -> if the worker returned a link, one Office-97-style link
  // line renders right after the reveal finishes -> input stays for a
  // follow-up. Any failure (timeout / non-200 / empty answer) shows an
  // in-character fallback line with a real link to /pages/contact instead
  // of erroring. Capped at ASK_QUESTION_LIMIT questions per session (only
  // real answers count -- a failed attempt doesn't spend one), bypassed
  // under ?boltydev=1 for testing; never logs or persists question/answer
  // text, only a plain count.

  var ASK_QUESTION_LIMIT = 10;
  var ASK_TIMEOUT_MS = 8000;

  // How many products reach the worker. Unchanged from the suggest.json era
  // (resources[limit]=5) -- collection routing changes WHICH products are
  // picked, not how many are sent.
  var ASK_PRODUCT_LIMIT = 5;

  // Page size for /collections/<handle>/products.json. 250 is the endpoint's
  // maximum; the largest collection this routes to comes back around 96KB,
  // and each collection is fetched at most once per page (askCollectionCache).
  var ASK_COLLECTION_FETCH_LIMIT = 250;
  var ASK_REVEAL_CHARS_PER_TICK = 2; // "~2 chars per 9fps tick" (TICK_MS)
  var STORAGE_ASKED_COUNT = 'evaBoltyAsked';

  var isAskPanelOpen = false;   // ask panel (input/thinking/answer) is the current balloon content
  var isAskThinking = false;    // fetch/POST in flight -> EMOTION_THINKING
  var isAskAnswering = false;   // mouth-synced type-out reveal in progress
  var askAnswerFullText = '';
  var askAnswerRevealedChars = 0;
  var askMouthOverride = null;  // one-shot post-reveal settle (MOUTH_SMILE), cleared on the next fresh balloon/menu/ask
  var askPendingLink = null;    // {url,label} awaiting render once the type-out reveal finishes, or null
  var askRequestToken = 0;      // invalidates a stale in-flight response after Cancel/X/navigation
  var askQuestionCount = (function () {
    var n = parseInt(sGet(STORAGE_ASKED_COUNT) || '0', 10);
    return isNaN(n) ? 0 : n;
  })();

  // ?boltydev=1 bypasses the session cap (same dev-mode convention as
  // EvaBoltyDebug/logRung elsewhere in this file) -- lets testing ask
  // more than 10 questions in one session without burning a real session
  // or editing sessionStorage by hand.
  function askCapReached() {
    if (isBoltyDevMode()) { return false; }
    return askQuestionCount >= ASK_QUESTION_LIMIT;
  }

  function recordAskAsked() {
    askQuestionCount++;
    try { sessionStorage.setItem(STORAGE_ASKED_COUNT, String(askQuestionCount)); } catch (e) {}
  }

  // Drives the thinking-dots pulse and the answer type-out reveal off the
  // SAME tick the character's own idle animation already runs on
  // (drawFrame, TICK_MS/~9fps) -- one clock for both, so "mouth-synced"
  // is literal rather than two timers hoping to stay aligned.
  function advanceAskBoltyTick() {
    if (isAskThinking) {
      var dotsEl = balloonEl && balloonEl.querySelector('.bolty-balloon__thinking-dots');
      if (dotsEl) { dotsEl.classList.toggle('is-dim', tickCount % 2 === 0); }
    }
    if (isAskAnswering) {
      askAnswerRevealedChars = Math.min(askAnswerFullText.length, askAnswerRevealedChars + ASK_REVEAL_CHARS_PER_TICK);
      var answerEl = balloonEl && balloonEl.querySelector('[data-bolty-ask-answer]');
      if (answerEl) { answerEl.textContent = askAnswerFullText.slice(0, askAnswerRevealedChars); }
      positionBalloon(); // balloon grows as text reveals -- keep the tail/height live, same as everything else here
      if (askAnswerRevealedChars >= askAnswerFullText.length) {
        isAskAnswering = false;
        askMouthOverride = MOUTH_SMILE; // "settles to smile at the end"
        // Only after the type-out is fully done -- rendering it any
        // earlier would get wiped out by this same function's own
        // textContent overwrite on the very next tick.
        renderAskLink(askPendingLink);
        askPendingLink = null;
      }
    }
  }

  function isAllowedAskLinkUrl(url) {
    return typeof url === 'string' && url.length > 0 &&
      (url.indexOf('/') === 0 || url.indexOf(window.location.origin) === 0);
  }

  // Renders one link line after the answer's type-out completes -- the
  // worker already validates link.url server-side against the product/
  // collection URLs the theme itself sent (BOLTY_COLLECTIONS + the
  // predictive-search products), but this re-checks on the theme side
  // too and drops anything that doesn't pass, per spec. textContent +
  // setAttribute only, same "never innerHTML" discipline as the answer
  // reveal and the fallback link -- the label is untrusted worker text.
  function renderAskLink(link) {
    if (!link || !isAllowedAskLinkUrl(link.url) || typeof link.label !== 'string' || !link.label) { return; }
    var answerEl = balloonEl && balloonEl.querySelector('[data-bolty-ask-answer]');
    if (!answerEl) { return; }
    answerEl.appendChild(document.createElement('br'));
    var a = document.createElement('a');
    a.textContent = link.label;
    a.setAttribute('class', 'bolty-balloon__link');
    a.setAttribute('href', link.url);
    answerEl.appendChild(a);
    positionBalloon();
  }

  function repositionForKeyboard() { positionBalloon(); }

  function onAskInputFocus() {
    if (window.visualViewport) { window.visualViewport.addEventListener('resize', repositionForKeyboard); }
    // A short delay for the keyboard's own open animation to actually
    // finish shrinking the visual viewport before re-measuring -- an
    // immediate call would just re-read the pre-keyboard height.
    setTimeout(positionBalloon, 300);
  }

  function onAskInputBlur() {
    if (window.visualViewport) { window.visualViewport.removeEventListener('resize', repositionForKeyboard); }
  }

  // Question words/filler that don't help predictive search -- stripped
  // before building a query (see extractAskKeywords below).
  var ASK_STOPWORDS = {
    'a': 1, 'an': 1, 'the': 1, 'is': 1, 'are': 1, 'was': 1, 'were': 1,
    'do': 1, 'does': 1, 'did': 1, 'have': 1, 'has': 1, 'had': 1, 'i': 1,
    'im': 1, 'you': 1, 'your': 1, 'we': 1, 'us': 1, 'our': 1, 'my': 1,
    'me': 1, 'it': 1, 'its': 1, 'this': 1, 'that': 1, 'these': 1,
    'those': 1, 'what': 1, 'which': 1, 'who': 1, 'where': 1, 'when': 1,
    'why': 1, 'how': 1, 'can': 1, 'could': 1, 'would': 1, 'should': 1,
    'will': 1, 'shall': 1, 'may': 1, 'might': 1, 'of': 1, 'for': 1,
    'to': 1, 'in': 1, 'on': 1, 'at': 1, 'with': 1, 'about': 1, 'if': 1,
    'and': 1, 'or': 1, 'but': 1, 'not': 1, 'no': 1, 'yes': 1,
    'please': 1, 'got': 1, 'get': 1, 'getting': 1, 'sell': 1,
    'sells': 1, 'selling': 1, 'carry': 1, 'carries': 1, 'carrying': 1,
    'looking': 1, 'look': 1, 'need': 1, 'needs': 1, 'needed': 1,
    'want': 1, 'wants': 1, 'wanted': 1, 'any': 1, 'some': 1,
    'there': 1, 'here': 1, 'guys': 1, 'store': 1, 'shop': 1
  };

  // Pulls real search terms out of a free-form question -- lowercases,
  // strips punctuation, drops stopwords and 1-letter tokens. Shopify's
  // predictive search AND-matches every query token as a prefix against
  // title/tag/vendor/type, so a raw sentence like "do you have grip
  // tape" never matches anything (confirmed live 2026-08-10 against
  // /search/suggest.json: "griptape" surfaces Jessup/MOB Grip fine,
  // "grip tape" and the full sentence both return zero) -- this is what
  // lets the real keywords through instead of the noise around them.
  // Capped at 6 keywords so a long rambling question can't turn into a
  // long serial chain of fallback requests in fetchAskProducts below.
  function extractAskKeywords(question) {
    var words = String(question || '')
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .split(/\s+/);
    var keywords = [];
    for (var i = 0; i < words.length && keywords.length < 6; i++) {
      var w = words[i];
      if (w.length < 2 || ASK_STOPWORDS[w]) { continue; }
      keywords.push(w);
    }
    return keywords;
  }

  // Griptape scoping (2026-08-11): a bare "grip" search also pulls in
  // grip-CARE tools/cleaners that share the word but aren't griptape
  // itself (Gtool - Square Grip Cleaner, MOB/Ripcare griptape cleaners)
  // -- confirmed live, a raw "grip" suggest hit returns 4 products, only
  // 2 of which are actually griptape. product_type and tags are both
  // empty on every one of these (same gap flagged in the earlier
  // grounding check), so there's no structured field to filter on --
  // title match is the only reliable signal, backed by an explicit
  // handle allowlist in case title wording ever changes.
  var GRIPTAPE_HANDLES = { 'jessup-griptape': 1, 'mob-griptape': 1 };

  // True when the extracted keyword set is asking about griptape itself
  // ("grip" or "griptape" -- catches both the one-word and two-word
  // phrasing extractAskKeywords can produce for the same question).
  function isGriptapeQuery(keywords) {
    for (var i = 0; i < keywords.length; i++) {
      if (keywords[i].indexOf('grip') === 0) { return true; }
    }
    return false;
  }

  // Narrows raw suggest results down to real griptape SKUs only -- exact
  // "...Griptape" title match, or the handle allowlist above as a
  // backstop. Only ever called when isGriptapeQuery is true, so this
  // never touches results for unrelated categories (bearings, wheels,
  // etc).
  function scopeGriptapeResults(results) {
    return results.filter(function (p) {
      return !!GRIPTAPE_HANDLES[p.handle] || /griptape$/i.test(p.title || '');
    });
  }

  // One /search/suggest.json lookup for a given query string -- shared by
  // fetchAskProducts' primary (all-keywords) and fallback (one-keyword)
  // attempts below. keywords is the full extracted set for the ORIGINAL
  // question (not just this particular query string), so the griptape
  // scope still applies correctly on a single-keyword fallback attempt
  // (e.g. querying "grip" alone after "grip tape" together came back
  // empty). Non-fatal on failure, same as before: resolves to [] rather
  // than rejecting.
  function suggestAskProducts(query, keywords) {
    var url = '/search/suggest.json?q=' + encodeURIComponent(query) + '&resources[type]=product&resources[limit]=' + ASK_PRODUCT_LIMIT;
    return fetch(url, { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        var results = data && data.resources && data.resources.results && data.resources.results.products;
        if (!Array.isArray(results)) { return []; }
        if (isGriptapeQuery(keywords)) { results = scopeGriptapeResults(results); }
        return results.map(function (p) {
          return { title: p.title, url: p.url, available: !!p.available };
        });
      })
      .catch(function () { return []; });
  }

  // Shopify's own storefront predictive search -- the ORIGINAL grounding
  // path, now the fallback for questions that name neither a category nor a
  // brand (specific product names: "sour josef scott jatta"). Unchanged
  // behaviour, just renamed from fetchAskProducts and moved behind the
  // routing below. Non-fatal on failure: an empty products array still lets
  // the question through, it just reaches the worker with no product hints.
  //
  // Searches extracted keywords rather than the raw question (see
  // extractAskKeywords). Tries all keywords together first (most precise
  // when every term matters, e.g. "wheels 53mm"); if that comes back
  // empty, falls back to each keyword alone in turn and stops at the
  // first one that returns results. That fallback is what actually
  // rescues a query like "grip tape" against a single-token product
  // title like "Griptape" -- the two words together never match, but
  // "grip" alone does (it's a valid prefix of "Griptape").
  function searchAskProducts(question) {
    var keywords = extractAskKeywords(question);
    if (!keywords.length) { return Promise.resolve([]); }

    function tryKeywordsFrom(index) {
      if (index >= keywords.length) { return Promise.resolve([]); }
      return suggestAskProducts(keywords[index], keywords).then(function (products) {
        if (products.length) { return products; }
        return tryKeywordsFrom(index + 1);
      });
    }

    return suggestAskProducts(keywords.join(' '), keywords).then(function (products) {
      if (products.length) { return products; }
      if (keywords.length === 1) { return []; }
      return tryKeywordsFrom(0);
    });
  }

  // Lowercased, punctuation-flattened, space-padded -- so a phrase test can
  // be a plain indexOf(' phrase ') and still respect word boundaries without
  // building a regex per phrase. "-" becomes a space, which is why the
  // category map lists "t shirt" rather than "t-shirt".
  function normalizeAskText(question) {
    return ' ' + String(question || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim() + ' ';
  }

  function matchesAnyPhrase(norm, phrases) {
    for (var i = 0; i < phrases.length; i++) {
      if (norm.indexOf(' ' + phrases[i] + ' ') > -1) { return true; }
    }
    return false;
  }

  // First match wins -- the map is ordered specific -> generic on purpose.
  function detectAskCategory(question) {
    var norm = normalizeAskText(question);
    for (var i = 0; i < BOLTY_CATEGORY_COLLECTIONS.length; i++) {
      if (matchesAnyPhrase(norm, BOLTY_CATEGORY_COLLECTIONS[i].phrases)) {
        return BOLTY_CATEGORY_COLLECTIONS[i].handles;
      }
    }
    return null;
  }

  function detectAskBrand(question) {
    var norm = normalizeAskText(question);
    for (var i = 0; i < BOLTY_BRAND_COLLECTIONS.length; i++) {
      if (matchesAnyPhrase(norm, BOLTY_BRAND_COLLECTIONS[i].phrases)) {
        return BOLTY_BRAND_COLLECTIONS[i].handle;
      }
    }
    return null;
  }

  // One collection's products, memoised for the page's lifetime -- a brand +
  // category question fetches two collections, and follow-up questions in the
  // same conversation very often hit the same ones. products.json is a public
  // storefront endpoint (no auth, no Admin API), same origin as the theme.
  // Availability is derived from the variants, since products.json has no
  // product-level `available` the way suggest.json does.
  var askCollectionCache = {};

  function fetchCollectionProducts(handle) {
    if (askCollectionCache[handle]) { return askCollectionCache[handle]; }
    var url = '/collections/' + encodeURIComponent(handle) + '/products.json?limit=' + ASK_COLLECTION_FETCH_LIMIT;
    var pending = fetch(url, { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        var products = data && data.products;
        if (!Array.isArray(products)) { return []; }
        return products.map(function (item) {
          var variants = Array.isArray(item.variants) ? item.variants : [];
          var available = false;
          for (var i = 0; i < variants.length; i++) {
            if (variants[i] && variants[i].available) { available = true; break; }
          }
          return { title: item.title, url: '/products/' + item.handle, available: available, handle: item.handle };
        });
      })
      .catch(function () { return []; });
    askCollectionCache[handle] = pending;
    return pending;
  }

  // Union of several collections, de-duped by handle, first occurrence wins
  // (only "pads" maps to more than one collection today).
  function fetchCollectionUnion(handles) {
    return Promise.all(handles.map(fetchCollectionProducts)).then(function (lists) {
      var seen = {};
      var out = [];
      for (var i = 0; i < lists.length; i++) {
        for (var j = 0; j < lists[i].length; j++) {
          var p = lists[i][j];
          if (!seen[p.handle]) { seen[p.handle] = 1; out.push(p); }
        }
      }
      return out;
    });
  }

  // Purchasable first. Sold-out items are kept, never filtered out -- "we
  // stock it, it's just gone right now" is a real and useful answer. Stable
  // within each group, so the collection's own curated order survives.
  function prioritizeAvailable(products) {
    var available = [];
    var soldOut = [];
    for (var i = 0; i < products.length; i++) {
      (products[i].available ? available : soldOut).push(products[i]);
    }
    return available.concat(soldOut);
  }

  // Same wire shape the worker already expects ({title, url, available}),
  // same cap as the old suggest.json limit -- the internal `handle` used for
  // intersection/de-dupe is dropped here and never sent.
  function toWorkerProducts(products) {
    return prioritizeAvailable(products).slice(0, ASK_PRODUCT_LIMIT).map(function (p) {
      return { title: p.title, url: p.url, available: p.available };
    });
  }

  // Routing order, per spec:
  //   both  -> brand collection narrowed to the category collection
  //   cat   -> that category collection
  //   brand -> that brand collection
  //   none  -> predictive search, unchanged (specific product names)
  //
  // An empty intersection deliberately returns EMPTY rather than falling back
  // to the brand alone: "got any Bones wheels" when the Bones collection holds
  // no wheels should let the worker say the shop might not have it, not hand
  // it three bushings and an LP to talk about. Falling back would re-open
  // exactly the cross-category hole this change closes.
  function fetchAskProducts(question) {
    var categoryHandles = detectAskCategory(question);
    var brandHandle = detectAskBrand(question);

    if (brandHandle && categoryHandles) {
      return Promise.all([
        fetchCollectionProducts(brandHandle),
        fetchCollectionUnion(categoryHandles)
      ]).then(function (both) {
        var inCategory = {};
        for (var i = 0; i < both[1].length; i++) { inCategory[both[1][i].handle] = 1; }
        return toWorkerProducts(both[0].filter(function (p) { return !!inCategory[p.handle]; }));
      });
    }
    if (categoryHandles) {
      return fetchCollectionUnion(categoryHandles).then(toWorkerProducts);
    }
    if (brandHandle) {
      return fetchCollectionProducts(brandHandle).then(toWorkerProducts);
    }
    return searchAskProducts(question);
  }

  function fetchWithTimeout(url, opts, timeoutMs) {
    var controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    var timeoutId = null;
    if (controller) {
      opts = opts || {};
      opts.signal = controller.signal;
      timeoutId = setTimeout(function () { controller.abort(); }, timeoutMs);
    }
    return fetch(url, opts).then(function (r) {
      if (timeoutId) { clearTimeout(timeoutId); }
      return r;
    }, function (err) {
      if (timeoutId) { clearTimeout(timeoutId); }
      throw err;
    });
  }

  // Worker contract (see BOLTY_ASK_URL's own comment): POST
  // {question, products, page:{title}}, expect 200 + JSON {answer: "..."}.
  // Any other outcome -- non-200, timeout (aborted via fetchWithTimeout),
  // network error, or a response with no non-empty string answer -- is a
  // rejection, and the caller's single catch() handles all of them
  // identically with the in-character fallback (per spec, these are one
  // failure case, not several to distinguish).
  // Resolves to {answer, link}, link null unless the worker returned one
  // that also passes isAllowedAskLinkUrl -- an invalid/disallowed url is
  // dropped here rather than surfaced as an error, so a misbehaving link
  // never fails an otherwise-good answer.
  function postAskWorker(question, products) {
    return fetchWithTimeout(BOLTY_ASK_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ question: question, products: products, collections: BOLTY_COLLECTIONS, page: { title: document.title } })
    }, ASK_TIMEOUT_MS).then(function (r) {
      if (!r.ok) { throw new Error('bad status'); }
      return r.json();
    }).then(function (data) {
      var answer = data && typeof data.answer === 'string' ? data.answer.trim() : '';
      if (!answer) { throw new Error('empty answer'); }
      var link = null;
      if (data && data.link && typeof data.link.url === 'string' && typeof data.link.label === 'string' && isAllowedAskLinkUrl(data.link.url)) {
        link = { url: data.link.url, label: data.link.label };
      }
      return { answer: answer, link: link };
    });
  }

  function renderAskThinking() {
    var answerEl = balloonEl && balloonEl.querySelector('[data-bolty-ask-answer]');
    if (!answerEl) { return; }
    answerEl.textContent = '';
    var dots = document.createElement('span');
    dots.className = 'bolty-balloon__thinking-dots';
    dots.textContent = '..';
    answerEl.appendChild(dots);
  }

  function beginAskAnswerReveal(answer, link) {
    var answerEl = balloonEl && balloonEl.querySelector('[data-bolty-ask-answer]');
    if (answerEl) { answerEl.textContent = ''; } // textContent only, never innerHTML -- the answer is untrusted text from the worker
    if (reduceMotion) {
      // startAnimLoop only ever draws ONE frame under reduced motion (no
      // setInterval at all) -- advanceAskBoltyTick, which the stepped
      // reveal depends on, would never run again, leaving the answer
      // stuck showing just its first couple characters forever. Show it
      // whole immediately instead; reduced motion means no animation, not
      // no content.
      if (answerEl) { answerEl.textContent = answer; }
      askMouthOverride = MOUTH_SMILE;
      renderAskLink(link);
    } else {
      askAnswerFullText = answer;
      askAnswerRevealedChars = 0;
      askPendingLink = link || null; // rendered by advanceAskBoltyTick once the reveal finishes
      isAskAnswering = true;
    }
    reenableAskInput();
  }

  // Static, fully trusted copy (never the untrusted answer text) -- built
  // via real DOM nodes rather than string HTML anyway, same "never
  // innerHTML" discipline as the answer reveal, so the one real link here
  // can't be confused with a place raw text ever gets parsed as markup.
  function renderAskFallback() {
    var answerEl = balloonEl && balloonEl.querySelector('[data-bolty-ask-answer]');
    if (!answerEl) { return; }
    answerEl.textContent = '';
    answerEl.appendChild(document.createTextNode("my brain's not screwed on right now.. try the "));
    var link = document.createElement('a');
    link.href = '/pages/contact';
    link.className = 'bolty-balloon__link';
    link.textContent = 'contact page';
    answerEl.appendChild(link);
    askMouthOverride = MOUTH_SMILE;
    reenableAskInput();
    positionBalloon();
  }

  function reenableAskInput() {
    if (askCapReached()) { showAskCapMessage(); return; }
    var input = balloonEl && balloonEl.querySelector('[data-bolty-ask-input]');
    var submitBtn = balloonEl && balloonEl.querySelector('[data-bolty-ask-submit]');
    if (input) { input.disabled = false; }
    if (submitBtn) { submitBtn.disabled = false; }
    positionBalloon();
  }

  // Replaces the input row in place once the session cap is hit --
  // whatever answer is already showing above it stays put; only future
  // attempts are blocked. The menu's own "Ask me something.." option is
  // separately hidden for the rest of the session via askBoltyAvailable()
  // -> askCapReached(), so this is the in-panel half of the same limit.
  function showAskCapMessage() {
    var row = balloonEl && balloonEl.querySelector('.bolty-balloon__ask-row');
    if (!row) { return; }
    row.textContent = '';
    var msg = document.createElement('p');
    msg.className = 'bolty-balloon__ask-cap-message';
    msg.textContent = 'I need a break, man.. my head is spinning. come back later';
    row.appendChild(msg);
    positionBalloon();
  }

  function handleAskSubmit(inputEl) {
    if (isAskThinking) { return; } // already mid-flight -- Enter/click can't double-submit
    var question = (inputEl.value || '').trim();
    if (!question) { return; }

    var submitBtn = balloonEl && balloonEl.querySelector('[data-bolty-ask-submit]');
    inputEl.disabled = true;
    if (submitBtn) { submitBtn.disabled = true; }
    inputEl.value = '';

    var token = ++askRequestToken;
    isAskThinking = true;
    askMouthOverride = null;
    renderAskThinking();
    positionBalloon();

    fetchAskProducts(question).then(function (products) {
      return postAskWorker(question, products);
    }).then(function (result) {
      if (token !== askRequestToken) { return; } // balloon was closed/reused while this was in flight
      // Only a REAL answer counts against the cap -- a failed attempt
      // (timeout/non-200/empty answer, caught below) doesn't spend one
      // of the visitor's 10 questions.
      recordAskAsked();
      isAskThinking = false;
      beginAskAnswerReveal(result.answer, result.link);
    }).catch(function () {
      if (token !== askRequestToken) { return; }
      isAskThinking = false;
      renderAskFallback();
    });
    // Deliberately no logging of `question` (or, later, the answer)
    // anywhere on this path -- not to console.debug, not to
    // sessionStorage/localStorage. Only recordAskAsked()'s plain integer
    // counter persists.
  }

  function showAskPanel() {
    if (!balloonEl) { return; }
    isAskPanelOpen = true;
    isAskThinking = false;
    isAskAnswering = false;
    askMouthOverride = null;

    // No separator/Cancel row here (polish pass, 2026-08-02) -- the corner
    // X already closes the panel, and Per's standing rule for THIS panel
    // specifically is X-only (unlike the help menu/tip view, which keep
    // their revived Cancel -- not touched by this pass, scope was the ask
    // panel only). The separator went with it rather than being left
    // dangling above nothing.
    var html = '<button type="button" class="bolty-balloon__dismiss" aria-label="Dismiss">✕</button>';
    html += '<p class="bolty-balloon__text" data-bolty-ask-answer></p>';
    html += '<div class="bolty-balloon__ask-row">';
    html += '<input type="text" class="bolty-balloon__ask-input" maxlength="200" placeholder="ask Bolty anything.." data-bolty-ask-input>';
    html += '<button type="button" class="bolty-balloon__ask-btn" data-bolty-ask-submit>Go</button>';
    html += '</div>';
    balloonEl.innerHTML = html;
    balloonEl.classList.remove('bolty-balloon--think');
    balloonEl.classList.add('is-open');
    isBalloonVisible = true;

    var input = balloonEl.querySelector('[data-bolty-ask-input]');
    var submitBtn = balloonEl.querySelector('[data-bolty-ask-submit]');

    function submit() { handleAskSubmit(input); }
    submitBtn.addEventListener('click', submit);
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.keyCode === 13) { e.preventDefault(); submit(); }
    });
    input.addEventListener('focus', onAskInputFocus);
    input.addEventListener('blur', onAskInputBlur);
    balloonEl.querySelector('.bolty-balloon__dismiss').addEventListener('click', hideBalloon);

    positionBalloon();
    // Explicit action (the visitor just clicked "Ask me something.."), so
    // focusing straight into the input -- and, on touch, the keyboard
    // that comes with it -- is expected here, unlike an ambient autofocus
    // on page load.
    try { input.focus(); } catch (e) {}
  }

  // --- State machine: hidden -> entering -> resident -> hidden -------------
  // "Hidden" used to be a 30-day dismiss cooldown (polish pass); the
  // features pass replaces that entirely with an indefinite flag toggled
  // by the header icon (or the hover-X, which now does the same thing).

  // Renamed from evaBoltySeen (this pass) -- that key now belongs to the
  // new line-ID no-repeat set (STORAGE_LINE_SEEN above), a different
  // thing entirely; this one-shot "has the qualifying-link trigger
  // already fired" flag needed its own name to avoid the collision.
  var STORAGE_TRIGGER_SEEN = 'evaBoltyTriggerSeen';
  var STORAGE_ENTERED = 'evaBoltyEntered';
  var STORAGE_HIDDEN = 'evaBoltyHidden';

  function sGet(key) { try { return sessionStorage.getItem(key); } catch (e) { return null; } }
  function sSet(key, val) { try { sessionStorage.setItem(key, val); } catch (e) {} }

  function isVisible() {
    return !!(rootEl && rootEl.classList.contains('is-visible'));
  }

  function isHidden() {
    try { return localStorage.getItem(STORAGE_HIDDEN) === '1'; } catch (e) { return false; }
  }

  function setHidden(hidden) {
    try { localStorage.setItem(STORAGE_HIDDEN, hidden ? '1' : '0'); } catch (e) {}
    updateToggleIconState();
  }

  function updateToggleIconState() {
    var hidden = isHidden();
    document.querySelectorAll('.js-bolty-toggle').forEach(function (el) {
      el.classList.toggle('is-bolty-off', hidden);
    });
  }

  // Screw-down exit: reverse of runEntranceAnimation (sink + spin +
  // fade), driven the same way -- rebuild the raw stage each tick and
  // dither fresh, box held static so only the character animates out.
  function runExit(onDone) {
    // Deliberately doesn't hideBalloon() here (unlike the prior build) --
    // hideBoltyNow shows the "hidden via icon" line right before calling
    // this, and it should stay visible through the sink/spin animation.
    // The balloon disappears implicitly once rootEl loses .is-visible at
    // the end (CSS: .bolty-root.is-visible .bolty-balloon.is-open --
    // .is-open alone isn't enough), no explicit hide needed.
    if (reduceMotion || !canvasEl || !displayCtx) { onDone(); return; }
    getCharRaster(currentEmotion(), tickCount, false, null, null, false, function (raster) {
      if (!raster) { onDone(); return; }
      var totalTicks = 10;
      var t = 0;
      var exitTimer = setInterval(function () {
        var progress = t / totalTicks;
        var rise = lerp(0, 104, progress);
        var rotate = lerp(0, 560, progress);
        var raw = buildRawStage(raster, { showShadow: false, riseOffset: rise, rotateDeg: rotate });
        var frame = ditherCanvas(raw);
        displayCtx.clearRect(0, 0, canvasEl.width, canvasEl.height);
        displayCtx.imageSmoothingEnabled = false;
        displayCtx.globalAlpha = Math.max(0, 1 - progress * 1.4);
        displayCtx.drawImage(frame, 0, 0);
        displayCtx.globalAlpha = 1;
        t++;
        if (t > totalTicks) {
          clearInterval(exitTimer);
          onDone();
        }
      }, TICK_MS);
    });
  }

  // Shared by the hover-X and the header icon's hide action -- both are
  // just "hide Bolty now," indefinitely, no cooldown timer.
  function hideBoltyNow() {
    if (!isVisible()) { return; }
    speak(pickHideLine()); // stays up through the exit animation, see runExit's own comment
    stopAnimLoop();
    runExit(function () {
      if (rootEl) { rootEl.classList.remove('is-visible'); }
      setHidden(true);
    });
  }

  var enteringNow = false;

  // Shared entrance animation (rise + spin, box static) -- each tick
  // rebuilds the full raw stage and dithers it fresh (~10 passes total,
  // a bounded one-time cost), same reasoning as the correction pass's
  // own comment here. onDone varies by caller: the first-ever entrance
  // shows the full intro balloon (runEntrance/finishEntrance); the header
  // icon's re-show (showBoltyFromIcon) just resumes quietly.
  function runEntranceAnimation(onDone) {
    if (enteringNow) { return; }
    enteringNow = true;
    rootEl.classList.add('is-visible', 'is-entering');

    if (reduceMotion) {
      enteringNow = false;
      rootEl.classList.remove('is-entering');
      onDone();
      return;
    }

    getCharRaster(EMOTION_REST, 0, false, null, null, false, function (raster) {
      if (!raster || !canvasEl || !displayCtx) {
        enteringNow = false;
        rootEl.classList.remove('is-entering');
        onDone();
        return;
      }
      var totalTicks = 10; // ~1.1s at 110ms
      var t = 0;
      var entranceTimer = setInterval(function () {
        var progress = t / totalTicks;
        var rise = lerp(104, 0, progress); // stage-space rise, scaled up from the prior build's 60px for the bigger stage
        var rotate = lerp(0, -560, progress);
        var raw = buildRawStage(raster, { showShadow: false, riseOffset: rise, rotateDeg: rotate });
        var frame = ditherCanvas(raw);
        displayCtx.clearRect(0, 0, canvasEl.width, canvasEl.height);
        displayCtx.imageSmoothingEnabled = false;
        displayCtx.globalAlpha = Math.min(1, progress * 1.4);
        displayCtx.drawImage(frame, 0, 0);
        displayCtx.globalAlpha = 1;
        t++;
        if (t > totalTicks) {
          clearInterval(entranceTimer);
          enteringNow = false;
          rootEl.classList.remove('is-entering');
          onDone();
        }
      }, TICK_MS);
    });
  }

  function runEntrance() {
    runEntranceAnimation(finishEntrance);
  }

  function finishEntrance() {
    sSet(STORAGE_ENTERED, '1');
    startAnimLoop();
    scheduleTalk();
    // Revived 2026-08-01 (ask-Bolty spec), rebuilt to 4 items 2026-08-10:
    // entrance is the options menu ("Buy something" / "Learn about Eva" /
    // "Entertainment" / "Just chill"), not a plain auto-dismissing line --
    // see delayedEntranceMenu/showHelpMenu.
    delayedEntranceMenu();
  }

  // The old one-shot checkPageEvents (404 / empty-search / cart-one-item
  // / newsletter-success, fired ~500ms after every resident page load,
  // independent of the timed cadence) is also gone -- error_404, search,
  // cart, and newsletter are now just ordinary context buckets
  // (contextBucket() above), delivered through the normal entrance-then-
  // ~4min-timer cadence like any other context, rather than as an extra
  // out-of-band trigger layered on top of it.
  function showResidentImmediately() {
    rootEl.classList.add('is-visible');
    startAnimLoop();
    scheduleTalk();
  }

  // Header-icon "show" path: same screw-up animation as the first-ever
  // entrance, but no line -- he's already been introduced, and the old
  // "Sure.. ignore me.." re-show line has no equivalent in the new pool.
  function showBoltyFromIcon() {
    if (isDeniedPath()) { return; }
    setHidden(false);
    restorePositionIfAny();
    runEntranceAnimation(function () {
      startAnimLoop();
      scheduleTalk();
    });
  }

  function toggleBoltyVisibility() {
    if (isVisible()) { hideBoltyNow(); } else { showBoltyFromIcon(); }
  }

  // --- Trigger: first click on a collection/product link ------------------

  function isQualifyingLink(link) {
    if (!link) { return false; }
    var href = link.getAttribute('href') || '';
    return href.indexOf('/collections/') === 0 || href.indexOf('/products/') === 0 ||
      /^https?:\/\/[^/]+\/(collections|products)\//.test(href);
  }

  function armTrigger() {
    document.addEventListener('click', function (e) {
      if (sGet(STORAGE_TRIGGER_SEEN)) { return; }
      var link = e.target.closest && e.target.closest('a[href]');
      if (!isQualifyingLink(link)) { return; }
      sSet(STORAGE_TRIGGER_SEEN, '1');
      waitForSettleThenEnter();
    });
  }

  function waitForSettleThenEnter() {
    function afterSettle() {
      setTimeout(function () {
        // isQuietObserverPath(): "NO entrance animation" on /cart (spec).
        // If the visitor happens to land here mid-/cart, entrance just
        // doesn't happen this session -- this is a one-shot timer chain,
        // not retried once its window has passed, matching how a denied
        // path already behaved before this pass.
        if (!isHidden() && !isDeniedPath() && !isQuietObserverPath()) {
          runEntrance();
        }
      }, 2500);
    }
    // Either the PJAX swap completes (most collection/product links are
    // PJAX-eligible), or -- if PJAX is disabled/denies this path for some
    // reason -- this script's own next fresh load already has
    // evaBoltyTriggerSeen set and no evaBoltyEntered yet, handled by
    // init() below on the resulting page's own load. One-time listener
    // either way.
    document.addEventListener('eva:pjax:loaded', afterSettle, { once: true });
    // Also cover the plain-hard-navigation case where THIS SAME document
    // never unloads (shouldn't normally happen for a real link click, but
    // cheap to also just settle from here if no pjax event ever fires).
    setTimeout(function () {
      if (!sGet(STORAGE_ENTERED)) { afterSettle(); }
    }, 3500);
  }

  // --- Positioning / drag ---------------------------------------------
  // Character + box + shadow move as one unit -- there's only one canvas
  // for all three, so dragging the .bolty-hit-area (sized/positioned to
  // the actual visible pixels, see the alpha-bbox section below) covers
  // them by construction. Same pattern as eva-radio.js's own drag handle
  // (pointer capture, a few px of slop before it counts as a drag), but
  // clamped to the VIEWPORT, not the document -- Bolty is position:
  // fixed, always relative to the viewport, unlike the radio's position:
  // absolute document-space widget. Pointer capture/is-dragging both
  // live on hitAreaEl now, not sceneEl -- they must be the SAME element
  // the pointer listeners are bound to (init(), above), since
  // setPointerCapture retargets the captured pointer's subsequent events
  // to whichever element captured it; capturing on one element while
  // listening on a different, non-ancestor one would silently stop
  // delivering move/up events mid-drag.

  var STORAGE_POS = 'evaBoltyPos';
  var DRAG_THRESHOLD = 5;
  var userMoved = false;
  var posX = null, posY = null; // fixed-position left/top, meaningful once userMoved
  var sceneEl = null;
  var hitAreaEl = null;
  var minimizeEl = null;
  var positionRestored = false;

  // --- Minimize button hover reveal (desktop) -------------------------
  // "A short show delay so it doesn't flicker during eye-tracking mouse
  // passes" -- a bare CSS opacity transition can't cleanly express a
  // slow fade-IN with a fast, undelayed fade-OUT (the asymmetric-
  // transition-delay trick is fragile across browsers), so this times
  // it in JS instead and just toggles a class. Checking e.relatedTarget
  // against both hover zones (the hit area and the button itself) avoids
  // a cancel-then-immediately-reschedule flicker when the pointer moves
  // directly from one to the other, mirroring what the old CSS
  // :has(a:hover, b:hover) selector did declaratively.
  var MINIMIZE_HOVER_DELAY_MS = 450;
  var minimizeShowTimer = null;

  function scheduleMinimizeShow() {
    if (minimizeShowTimer) { return; }
    minimizeShowTimer = setTimeout(function () {
      minimizeShowTimer = null;
      if (minimizeEl) { minimizeEl.classList.add('is-shown'); }
    }, MINIMIZE_HOVER_DELAY_MS);
  }

  function cancelMinimizeShow(relatedTarget) {
    if (relatedTarget === hitAreaEl || relatedTarget === minimizeEl) { return; }
    if (minimizeShowTimer) { clearTimeout(minimizeShowTimer); minimizeShowTimer = null; }
    if (minimizeEl) { minimizeEl.classList.remove('is-shown'); }
    cancelMinimizeTooltip(); // leaving the whole hover zone -- the tooltip can't outlive the button it's anchored to
  }

  // --- Minimize button tooltip ("Hide Bolty") --------------------------
  // Separate, own-timed reveal from the button's own is-shown above --
  // "on hover over the BUTTON" specifically (not over Bolty generally),
  // so this is scoped to minimizeEl alone, wired from init() alongside
  // the button's own hover listeners. No touch handling needed: this
  // class is only ever added by a real mouseenter, which touch never
  // fires ("no hover exists" -- spec).
  var MINIMIZE_TOOLTIP_DELAY_MS = 400;
  var minimizeTooltipTimer = null;

  function scheduleMinimizeTooltip() {
    if (minimizeTooltipTimer) { return; }
    minimizeTooltipTimer = setTimeout(function () {
      minimizeTooltipTimer = null;
      if (minimizeEl) { minimizeEl.classList.add('is-tooltip-shown'); }
    }, MINIMIZE_TOOLTIP_DELAY_MS);
  }

  function cancelMinimizeTooltip() {
    if (minimizeTooltipTimer) { clearTimeout(minimizeTooltipTimer); minimizeTooltipTimer = null; }
    if (minimizeEl) { minimizeEl.classList.remove('is-tooltip-shown'); }
  }

  function loadSavedPosition() {
    try {
      var raw = localStorage.getItem(STORAGE_POS);
      if (!raw) { return null; }
      var pos = JSON.parse(raw);
      if (typeof pos.x !== 'number' || typeof pos.y !== 'number') { return null; }
      return pos;
    } catch (e) { return null; }
  }

  function savePosition(x, y) {
    try { localStorage.setItem(STORAGE_POS, JSON.stringify({ x: x, y: y })); } catch (e) {}
  }

  // Drag used to clamp .bolty-root's own OWN box (340x330, unscaled --
  // CSS transforms never change an element's layout size, only its
  // painted one) rather than the visible artwork. Since .bolty-scene's
  // transform-origin is "right bottom" (see bolty.css), the shrunk
  // artwork sits flush in that box's bottom-right corner at any scale,
  // and ALL the slack -- (340 - 340*scale) px horizontally, (330 -
  // 330*scale) px vertically -- piles up on the top/left side alone.
  // Clamping the box's own left/top to >=0 left a matching amount of
  // dead space between the true viewport edge and where the visible
  // pixels actually started, i.e. exactly the "stops short, invisible
  // wall, top/left only" bug reported live.
  //
  // getVisibleBoundsOffset reads .bolty-hit-area's live
  // getBoundingClientRect() -- already sized/positioned to the real
  // composited alpha-bbox (see maybeApplyHitAreaBBox) and already
  // reflecting the current --eva-bolty-scale via the CSS transform, so
  // no hand-rolled transform-origin math is needed and nothing here goes
  // stale if the scale or transform-origin ever changes. The offset
  // between the hit-area and rootEl's own box is a fixed translation
  // (moving root by any delta moves the hit-area by the same delta), so
  // it's safe to compute once per clamp call rather than mid-drag.
  var CLAMP_KEEP_VISIBLE_PX = 40; // never let him be dragged fully off-screen and lost

  function getVisibleBoundsOffset() {
    if (!rootEl || !hitAreaEl) { return null; }
    var rootRect = rootEl.getBoundingClientRect();
    var hitRect = hitAreaEl.getBoundingClientRect();
    if (!hitRect.width || !hitRect.height) { return null; } // alpha-bbox not measured yet
    return {
      dx: hitRect.left - rootRect.left,
      dy: hitRect.top - rootRect.top,
      w: hitRect.width,
      h: hitRect.height
    };
  }

  // x/y are candidate values for rootEl's own left/top (position:fixed,
  // so always viewport-relative -- there is no parent container in play,
  // .bolty-root is a direct child of <body>, confirmed no ancestor
  // between it and <body> carries a transform/filter/perspective that
  // would otherwise trap a fixed-position descendant). Clamps the
  // VISIBLE artwork's screen rect into the viewport, with a
  // CLAMP_KEEP_VISIBLE_PX peek margin on every edge, then translates
  // back to the equivalent root left/top.
  function clampToViewport(x, y) {
    var offset = getVisibleBoundsOffset();
    if (!offset) {
      // Hit-area alpha-bbox not measured yet (e.g. very first frame) --
      // fall back to the old padded-box clamp as a rough stand-in until
      // it is; maybeApplyHitAreaBBox re-clamps for real once it lands.
      var w = (rootEl && rootEl.offsetWidth) || STAGE_W;
      var h = (rootEl && rootEl.offsetHeight) || STAGE_H;
      var maxX = Math.max(0, window.innerWidth - w);
      var maxY = Math.max(0, window.innerHeight - h);
      return { x: Math.max(0, Math.min(maxX, x)), y: Math.max(0, Math.min(maxY, y)) };
    }
    var vw = window.innerWidth, vh = window.innerHeight;
    var minVisLeft = -(offset.w - CLAMP_KEEP_VISIBLE_PX);
    var maxVisLeft = vw - CLAMP_KEEP_VISIBLE_PX;
    var minVisTop = -(offset.h - CLAMP_KEEP_VISIBLE_PX);
    var maxVisTop = vh - CLAMP_KEEP_VISIBLE_PX;
    var visLeft = Math.max(minVisLeft, Math.min(maxVisLeft, x + offset.dx));
    var visTop = Math.max(minVisTop, Math.min(maxVisTop, y + offset.dy));
    return { x: visLeft - offset.dx, y: visTop - offset.dy };
  }

  function applyPosition(x, y) {
    rootEl.style.left = x + 'px';
    rootEl.style.top = y + 'px';
    rootEl.style.right = 'auto';
    rootEl.style.bottom = 'auto';
  }

  // Runs once ever (module-level flag) -- called from init() on first
  // load and from showBoltyFromIcon() defensively (it's a no-op the
  // second time either way).
  function restorePositionIfAny() {
    if (positionRestored) { return; }
    positionRestored = true;
    var saved = loadSavedPosition();
    if (!saved) { return; } // leave the CSS default (right:20;bottom:20) in place
    userMoved = true;
    var clamped = clampToViewport(saved.x, saved.y);
    posX = clamped.x;
    posY = clamped.y;
    applyPosition(posX, posY);
  }

  function reclampAfterViewportChange() {
    // Balloon reposition is unconditional (unlike the drag-clamp below,
    // which only matters once he's actually been moved off his CSS
    // default) -- even at the default corner, a viewport resize can
    // change whether there's room above him, per spec ("recompute...
    // on window resize").
    if (isBalloonVisible) { positionBalloon(); }
    if (!userMoved || !rootEl) { return; }
    var clamped = clampToViewport(posX, posY);
    posX = clamped.x;
    posY = clamped.y;
    applyPosition(posX, posY);
  }

  window.addEventListener('resize', reclampAfterViewportChange);
  // 'orientationchange' on top of 'resize' -- some mobile browsers
  // (notably older iOS Safari) report innerWidth/innerHeight a beat
  // behind the actual rotation, so this reclamp is deferred slightly to
  // land after the viewport has actually settled into its new size,
  // rather than reading stale dimensions synchronously.
  window.addEventListener('orientationchange', function () {
    setTimeout(reclampAfterViewportChange, 50);
  });

  var dragging = false;
  var dragMoved = false;
  var dragStartX = 0, dragStartY = 0;
  var dragOriginX = 0, dragOriginY = 0;
  var dragPointerId = null;
  var dragCaptureEl = null;
  // Which element the current gesture actually started on -- 'hit' for the
  // character/box/shadow art, 'minimize' for the button (including its own
  // oversized invisible touch-target pad, see .bolty-minimize::before in
  // bolty.css). Both elements share this exact same pointerdown/move/up
  // trio (bound to each below in init()) so a press-drag started on EITHER
  // one moves Bolty via the identical threshold logic -- previously only
  // hitAreaEl had these listeners, so a pointerdown landing on the
  // minimize button's invisible pad (which visually overlaps the top of
  // the hit-area) hit an element with no drag handling at all and just
  // died there, silently swallowing any drag attempt that started near
  // his head. minimizeEl's own 'click' listener (below) still does the
  // actual hiding, gated on dragMoved being false so a real drag that
  // happens to start on the button moves him instead of hiding him.
  var dragStartedOnMinimize = false;

  function onScenePointerDown(e) {
    noteActivity();
    dragging = true;
    dragMoved = false;
    dragStartedOnMinimize = (e.currentTarget === minimizeEl);
    dragPointerId = e.pointerId;
    dragStartX = e.clientX;
    dragStartY = e.clientY;
    var rect = rootEl.getBoundingClientRect();
    dragOriginX = rect.left;
    dragOriginY = rect.top;
    dragCaptureEl = e.currentTarget;
    try { dragCaptureEl.setPointerCapture(e.pointerId); } catch (err) {}
  }

  function onScenePointerMove(e) {
    if (!dragging || e.pointerId !== dragPointerId) { return; }
    var dx = e.clientX - dragStartX;
    var dy = e.clientY - dragStartY;
    if (!dragMoved && (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD)) {
      dragMoved = true;
      hitAreaEl.classList.add('is-dragging');
    }
    if (!dragMoved) { return; }
    var clamped = clampToViewport(dragOriginX + dx, dragOriginY + dy);
    posX = clamped.x;
    posY = clamped.y;
    applyPosition(posX, posY);
  }

  function onScenePointerUp(e) {
    if (!dragging || e.pointerId !== dragPointerId) { return; }
    dragging = false;
    dragPointerId = null;
    hitAreaEl.classList.remove('is-dragging');
    try { if (dragCaptureEl) { dragCaptureEl.releasePointerCapture(e.pointerId); } } catch (err) {}
    dragCaptureEl = null;
    if (dragMoved) {
      userMoved = true;
      savePosition(posX, posY);
      // Explicit, unconditional reposition on drag end, per spec -- the
      // drag line below (when one's picked) already reopens/repositions
      // the balloon fresh as a side effect of speak(), but this covers
      // the case where a DIFFERENT balloon was already open before the
      // drag started (or the drag bucket happens to be momentarily
      // exhausted) and would otherwise sit at its pre-drag position.
      if (isBalloonVisible) { positionBalloon(); }
      // Quiet-observer spec: "he reacts ONLY to real cart events" -- a
      // drag quip isn't one, so it's suppressed on /cart same as every
      // other proactive line, even though dragging itself still works.
      if (isVisible() && !isQuietObserverPath()) { speak(pickDragLine()); }
    } else if (dragStartedOnMinimize) {
      // No drag happened -- this was a plain press-release on the
      // minimize button (or its pad). The native 'click' event minimizeEl
      // already listens for (below) fires right after this and does the
      // actual hiding, gated on dragMoved -- nothing to do here, this
      // branch just exists so the touch-tap branch below doesn't also
      // fire a spontaneous line on top of that.
    } else if (isVisible() && !isAskPanelOpen && !isQuietObserverPath()) {
      // Revived 2026-08-01 (ask-Bolty spec): "any time the visitor clicks
      // Bolty while idle, the menu reopens with just the options, no
      // greeting." Used to be touch-only (a plain spontaneous line, the
      // touch stand-in for "a desktop mouse just finding him via hover")
      // -- now fires for mouse and touch alike, since a real click/tap is
      // its own deliberate action either way, and desktop clicking him
      // previously did nothing at all. Skipped while the ask panel itself
      // is showing so a stray click on the character doesn't blow away
      // whatever the visitor is mid-typing there. Also skipped entirely
      // on /cart (quiet-observer spec: "NO options menu") -- clicking him
      // there is a no-op; dragging still works, just nothing opens.
      showHelpMenu(false);
    }
    // dragMoved is deliberately NOT reset here -- the native 'click' event
    // minimizeEl listens for (below) fires right after this handler
    // returns, for this same gesture, and reads dragMoved to decide
    // whether to hide him; it's reset at the top of the next
    // onScenePointerDown instead, same as before this pass.
    dragStartedOnMinimize = false;
  }

  // --- Eye tracking (desktop pointer only) --------------------------------
  // Pupils nudge toward the cursor, but only when nothing else already
  // claims the eyes: bored has its own drift, sleep/talk/vibe/stoked all
  // have their own fixed look, and the emotion system always wins over
  // tracking per spec -- so this only ever applies in EMOTION_REST
  // (computeEyeTrackOffset enforces that itself, called from drawFrame).
  //
  // Deliberately reuses the existing ~110ms tick loop as the "animation
  // frame" throttle rather than adding a new rAF loop or a mousemove-
  // driven redraw -- this file's whole animation philosophy is stepped/
  // never-eased (see TICK_MS etc. above), and a raw per-mousemove handler
  // would also be wrong for a different reason: buildCharacterSVG's own
  // output has to stay one of a small, bounded set of variants (see
  // charVariantKey) so the expensive rasterize+dither pass stays cached
  // -- a continuous cursor-driven offset would blow that cache open to
  // effectively unbounded keys. The mousemove listener below only ever
  // stores two numbers; computeEyeTrackOffset (called once per tick from
  // drawFrame) is where the actual discrete snap happens.
  var EYE_TRACK_STEPS = [-3, -1.5, 0, 1.5, 3]; // px, in the 160x240 SVG viewBox -- pre-scale, so --eva-bolty-scale applies to it automatically along with everything else baked into the same raster
  var EYE_TRACK_MAX = 3;

  var mouseClientX = null, mouseClientY = null;
  var hasFinePointer = false;
  try { hasFinePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches; } catch (e) {}
  var eyeTrackEnabled = hasFinePointer && !reduceMotion;

  if (eyeTrackEnabled) {
    document.addEventListener('mousemove', function (e) {
      mouseClientX = e.clientX;
      mouseClientY = e.clientY;
    }, { passive: true });
  }

  function snapToTrackStep(v) {
    var closest = EYE_TRACK_STEPS[0];
    var bestDiff = Math.abs(v - closest);
    for (var i = 1; i < EYE_TRACK_STEPS.length; i++) {
      var diff = Math.abs(v - EYE_TRACK_STEPS[i]);
      if (diff < bestDiff) { bestDiff = diff; closest = EYE_TRACK_STEPS[i]; }
    }
    return closest;
  }

  // Returns {x,y} (discrete grid values) or null (no tracking this frame
  // -- caller/cache treat null exactly like "not tracking" ever did).
  // Anchor point is an approximation (root's own box, not the precise
  // live eye coordinates) -- plenty accurate for a few-px directional
  // nudge, not meant to be exact gaze math.
  function computeEyeTrackOffset(emotion) {
    if (emotion !== EMOTION_REST) { return null; }
    if (!eyeTrackEnabled || dragging || mouseClientX == null || !rootEl) { return null; }
    var rect = rootEl.getBoundingClientRect();
    if (!rect.width || !rect.height) { return null; }
    var anchorX = rect.left + rect.width * 0.5;
    var anchorY = rect.top + rect.height * 0.35;
    var dx = mouseClientX - anchorX;
    var dy = mouseClientY - anchorY;
    var dist = Math.sqrt(dx * dx + dy * dy) || 1;
    return {
      x: snapToTrackStep((dx / dist) * EYE_TRACK_MAX),
      y: snapToTrackStep((dy / dist) * EYE_TRACK_MAX)
    };
  }

  // --- Vibe (radio play/stop) ---------------------------------------------
  // isVibing drives both the VIBE emotion and the 'radio' context bucket
  // (contextBucket() above -- no dedicated radio page exists on this
  // store, "actively playing" is the sensible reading). The old
  // immediate "tape started" line on the rising edge into playing is
  // gone this pass -- another out-of-band trigger outside the stated
  // ~4min cadence, same reasoning as the dropped rest-wake/bored-mutter/
  // reshow lines above.

  document.addEventListener('eva:radio-play', function () { isVibing = true; });
  document.addEventListener('eva:radio-stop', function () { isVibing = false; });

  // --- Activity tracking for rest state -----------------------------------

  ['scroll', 'click', 'mousemove', 'keydown', 'touchstart'].forEach(function (evt) {
    document.addEventListener(evt, noteActivity, { passive: true });
  });

  // --- Init / PJAX --------------------------------------------------------
  // Root cause of the "re-animates and loses his position on every page
  // change" bug (this pass): rootEl itself was never the problem -- it
  // lives outside #MainContent (confirmed structurally: layout/theme.
  // liquid closes </main> right after {{ content_for_layout }}, and
  // renders 'bolty' as a sibling further down, exactly like eva-radio),
  // so PJAX's own #MainContent.innerHTML swap (assets/eva-pjax.js) never
  // touches it. The actual bug was this file's OWN 'eva:pjax:loaded'
  // listener unconditionally calling teardown()+init() on every single
  // navigation: init() re-running would call showResidentImmediately(),
  // which calls scheduleTalk() -- clearing and restarting the ~4min
  // spontaneous-line timer from zero every time (idle timers must NOT
  // reset per spec), and briefly stop/restart the anim loop for no
  // reason. In a narrow window right after the very first qualifying
  // click (before finishEntrance's sSet(STORAGE_ENTERED,'1') has run),
  // it could also register a second waitForSettleThenEnter() listener,
  // risking a duplicate entrance animation. eva-radio's own equivalent
  // listener (assets/eva-radio.js) never re-runs its setup either --
  // its full init happens exactly once, and its 'eva:pjax:loaded'
  // handler only re-clamps position (needed there because that widget
  // is position:absolute in document space, which a new page's
  // different height can affect). Bolty is position:fixed (viewport-
  // relative, unaffected by document height), so mirroring that pattern
  // here means the listener needs to do nothing at all -- removed
  // rather than kept as a no-op, see below.

  var everInitialized = false; // full setup below runs exactly once per real page load, never again on PJAX nav

  function init() {
    if (everInitialized) { return; }
    everInitialized = true;
    notePageView(); // this hard load's own view -- PJAX soft navs are counted separately, see notePageView's own comment
    // Seed the cart-observer baseline on EVERY page, not just /cart
    // (2026-08-24 bugfix). This used to be guarded by isQuietObserverPath(),
    // so on a product page nothing ever seeded lastKnownCartItemCount or
    // cartChainLastKnownKeys -- and both of their subscribers bail on the
    // first read by design (oldCount === null / keys === null, so a page
    // load can't retroactively fire for items already in the cart). The
    // visitor's FIRST add-to-cart was therefore swallowed as the seeding
    // event: no stoked reaction, and the cart-add chain never armed, which
    // is exactly the "adding a deck does nothing" bug. Seeding here makes
    // that first add a real comparison instead. Runs before the rootEl
    // early-return below on purpose -- the cart observer is independent of
    // whether Bolty's own DOM is present.
    notifyCartActivity();

    rootEl = document.querySelector('[data-bolty-root]');
    if (!rootEl) { return; }
    canvasEl = rootEl.querySelector('.bolty-canvas');
    balloonEl = rootEl.querySelector('.bolty-balloon');
    sceneEl = rootEl.querySelector('.bolty-scene');
    hitAreaEl = rootEl.querySelector('[data-bolty-hit-area]');
    if (canvasEl) {
      displayCtx = canvasEl.getContext('2d');
      canvasEl.width = STAGE_W;
      canvasEl.height = STAGE_H;
    }
    minimizeEl = rootEl.querySelector('[data-bolty-minimize]');
    if (minimizeEl) {
      minimizeEl.style.backgroundImage = 'url(' + buildMinimizeButtonDataUrl() + ')';
      // Pressing it hides Bolty via the EXACT SAME function the header
      // bolt toggle calls to hide him (toggleBoltyVisibility -> this,
      // when currently visible) -- hidden state stays consistent and
      // persists identically either way, and his "hide" farewell line
      // still fires first (hideBoltyNow's own first line). Guarded on
      // dragMoved (drag-regression fix, this pass): minimizeEl now shares
      // the same pointerdown/move/up drag handlers as hitAreaEl (below,
      // and see their own comment) so a press-drag that happens to start
      // on the button -- including its own oversized invisible touch-
      // target pad, which visually overlaps the top of the hit-area --
      // moves him instead of this native click firing on top of that.
      minimizeEl.addEventListener('click', function (e) {
        if (dragMoved) { return; }
        hideBoltyNow();
      });
      minimizeEl.addEventListener('mouseenter', scheduleMinimizeShow);
      minimizeEl.addEventListener('mouseleave', function (e) { cancelMinimizeShow(e.relatedTarget); });
      // Tooltip: scoped to the button itself only, not the wider hit-area
      // hover zone above -- "on hover over the button."
      minimizeEl.addEventListener('mouseenter', scheduleMinimizeTooltip);
      minimizeEl.addEventListener('mouseleave', cancelMinimizeTooltip);
      // Same drag trio hitAreaEl gets below -- see the dragStartedOnMinimize
      // comment above onScenePointerDown for why this element needs it too.
      minimizeEl.addEventListener('pointerdown', onScenePointerDown);
      minimizeEl.addEventListener('pointermove', onScenePointerMove);
      minimizeEl.addEventListener('pointerup', onScenePointerUp);
      minimizeEl.addEventListener('pointercancel', onScenePointerUp);
    }
    if (hitAreaEl) {
      hitAreaEl.addEventListener('pointerdown', onScenePointerDown);
      hitAreaEl.addEventListener('pointermove', onScenePointerMove);
      hitAreaEl.addEventListener('pointerup', onScenePointerUp);
      hitAreaEl.addEventListener('pointercancel', onScenePointerUp);
      hitAreaEl.addEventListener('mouseenter', scheduleMinimizeShow);
      hitAreaEl.addEventListener('mouseleave', function (e) { cancelMinimizeShow(e.relatedTarget); });
    }
    // Header icon(s) -- header-icon-stack.liquid renders this markup up
    // to three times (mobile + two desktop layout branches), all
    // present in the DOM at once (CSS picks which is visible), so this
    // binds every instance. Header lives outside #MainContent too (same
    // reasoning as rootEl above), so -- like everything else in this
    // function now -- binding once here is enough, no PJAX re-bind guard
    // needed any more.
    document.querySelectorAll('.js-bolty-toggle').forEach(function (el) {
      el.addEventListener('click', toggleBoltyVisibility);
    });
    updateToggleIconState();
    restorePositionIfAny();
    if (!boxImg) { initBoxImage(); }

    if (isHidden() || isDeniedPath()) {
      return; // container stays in the DOM but never shown
    }

    // If the synchronous placement script (snippets/bolty.liquid, runs
    // before this deferred script and before first paint) already put
    // him up -- a hard load (e.g. /cart) with the session-entrance guard
    // already set -- just resume ticking. Don't re-add is-visible or
    // re-touch position; both are already correct and doing so again
    // here would be redundant, not incorrect, but the intent is "hook up
    // the still image the sync script painted," not "place him again."
    if (rootEl.classList.contains('is-visible')) {
      startAnimLoop();
      scheduleTalk();
      return;
    }

    if (sGet(STORAGE_ENTERED)) {
      showResidentImmediately();
    } else if (sGet(STORAGE_TRIGGER_SEEN)) {
      // Arrived here via a hard navigation right after the qualifying
      // click (PJAX didn't intercept it) -- settle from this fresh load.
      waitForSettleThenEnter();
    }
    // Else: nothing yet, wait for a qualifying click (armed once, below).
  }

  armTrigger(); // armed once, module-level -- survives PJAX since this script itself never reloads mid-session
  loadLines(); // fetch assets/bolty-lines.json once, ASAP -- see its own comment
  initRepeatVisitorFlag(); // must run exactly once per real page load (not per PJAX init()), see its own comment

  // Manual animation triggers for testing, ?boltydev=1 only -- same dev-
  // mode convention as logRung/logAnim's own console output. Each calls
  // the real play function with allowDuringTalk=false, i.e. exactly what
  // an ambient trigger would do; canStartAnim's usual rules (visible,
  // not hidden/dragging/mid-animation, not reduced-motion) still apply,
  // so this won't force an animation somewhere it genuinely couldn't
  // play live either. playDollarEyes bypasses canStartAnim by design
  // (see its own comment) but still respects reduceMotion/isVisible.
  if (isBoltyDevMode()) {
    window.EvaBoltyDebug = {
      playCough: function () { playCough(false); },
      playSmoke: function () { playSmoke(false); },
      playYawn: function () { playYawn(false); },
      playSpying: function () { playSpying(false); },
      playNoddingOff: startNoddingOff,
      playDollarEyes: function () { playDollarEyes(); },
      playSurprised: function () { playSurprised(false); },
      playLick: function () { playLick(false); },
      playJuggle: function () { playJuggle(false); },
      playScratch: function () { playScratch(false); },
      playNosePick: function () { playNosePick(false); },
      // Forces a thought bubble with arbitrary text for visual checking
      // -- bypasses the picker/seen-set entirely (not a real line), same
      // as the other manual triggers above bypass their own ambient
      // gating. Logs the same [bolty] bubble=... line speak() does, with
      // an explicit "debug: thinkTest" source rather than a fabricated
      // bucket name.
      thinkTest: function (text) {
        if (isBoltyDevMode()) { console.debug('[bolty] bubble=think (debug: thinkTest)'); }
        showBalloonLine(String(text || 'Just thinking out loud here.'), null, BUBBLE_THINK);
      },
      // Static style QA for the floating-hands render layer -- shows both
      // gloves at their neutral rest position with no animation, so the
      // dither/palette match can be checked before any anim using them
      // exists. previewHands() (or previewHands(true)) turns it on,
      // previewHands(false) turns it off; forces one immediate repaint
      // either way rather than waiting for the next tick, so it also
      // works under prefers-reduced-motion (where the loop only ever
      // draws once on its own).
      previewHands: function (on) {
        previewHandsOn = (on !== false);
        drawFrame();
      },
      // Steps the render loop n ticks synchronously, bypassing the real
      // setInterval timer entirely -- lets an animation's own frames be
      // inspected deterministically (e.g. after playJuggle(), step(8)
      // lands exactly on tick 8) regardless of real elapsed time, which
      // setInterval-driven ticks can't guarantee in a backgrounded/
      // throttled tab. Dev-only QA aid, same category as previewHands.
      step: function (n) {
        for (var i = 0; i < (n || 1); i++) { drawFrame(); }
      }
    };
  }

  ready(init);
  // No 'eva:pjax:loaded' listener here any more -- see this section's
  // own comment above for why. Page CONTEXT (which voice-line bucket
  // applies) needs no explicit refresh either: contextBucket() reads
  // document.body.className/location.pathname live, at the moment each
  // line is picked, never cached -- so the very next spontaneous line
  // after a navigation automatically reflects the new page with zero
  // extra code.
})();
